import { OverflowButton, useListOverflow, withListOverflow } from "../../v2/components/ListOverflow.jsx";
import { createPortal } from "react-dom";
export const OfficesTable = withListOverflow(MarketOfficesListOwner);
// src/features/plan/MarketPlanPage.jsx
//
// PLAN-MATRIX-S1: Market footprint matrix — default Plan view when ui_v2 is on.
//
// Layout: toolbar (Matrix/Board toggle + filters) → matrix table with one row
// per market. Clicking a row expands an inline accordion with two panels:
//   Left  — Offices in this market (table of locations with next lease event,
//            seats, Space Fit, disposition)
//   Right — The plan (editable: strategy, target date, plan note)
//
// Data source: GET /api/plan (planData prop pre-fetched by App shell, or fetched
// on mount as fallback). Save calls PUT /api/plan/markets/:id.
//
// Role model: editor/admin = editable right panel; viewer = read-only.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { buildMarketPlanUrl, buildMarketUrl, buildLocationUrl, buildLeaseUrl } from "../../services/buildUrl.js";
import { InternalLink } from "../../components/InternalLink.jsx";
import { ToolbarViewToggle } from "../../v2/components/ToolbarViewToggle.jsx";
import { MarketTilesView } from "../places/MarketTilesView.jsx";
import { MarketsMapView } from "../places/MarketsMapView.jsx";
import { useOfficeInventory } from "../places/marketInventory.js";
import { marketAssignment, knownTotal, officeAssignedToday, officeSeatsToday, marketAssignedToday, marketInstalledToday, CAPACITY_STATUS_DEFINITION } from "../../services/seatAccounting.js";
import { PartialMarker } from "../../components/PartialTotal.jsx";
import { marketListReturnPath } from "../places/marketRecord.js";
import { AnnualRentBreakdown, hasRecoveryBasis } from "../leases/components/AnnualRentBreakdown.jsx";
import { subscribePlanInvalidation, unsubscribePlanInvalidation, emitCommandIndexStale } from "../../services/cacheBus.js";
import { PlanCompletenessCell, countCompletePlans } from "./PlanCompletenessCell.jsx";
import { MarketRecordDrawer, parseMarketPeek, withMarketPeek, capacityHrefForMarket, marketCapacitySummary } from "./MarketRecordDrawer.jsx";
import { useCapacitySnapshot } from "../capacity/useCapacitySnapshot.js";
import { DEFAULT_TARGET_BUFFER_PCT } from "../capacity/capacityModel.js";
import { PlanExpandCaret } from "./PlanExpandCaret.jsx";
import { StageBadge } from "../../components/StageBadge.jsx";
import { T, DISPOSITION_META, STAGES } from "../../app/tokens.js";
import { LEASE_STATUS_META } from "../leases/leaseStatusLabels.js";
import { getColHeaderHighlight } from "../../v2/utils/sortColumnHighlight.js";
import { CollectionSortHeader, CollectionSortCaret } from "../../v2/components/CollectionSortHeader.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiPopover, HOME_KPI_TOOLTIP_STYLE } from "../../components/KpiPopover.jsx";
import { KPI_TILE_ROW_HEIGHT } from "./KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { log } from "../../utils/logger.js";
import { useUiPrefs } from "../../v2/hooks/useUiPrefs.js";
import { UI_PREFS_PLAN_V1 } from "../../services/portfolioStatePersistence.js";
import { loadLastTab, saveLastTab } from "../../services/tabStatePersistence.js";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { createMarket, deleteMarket, getCandidates, createCandidate, updateCandidate, deleteCandidate, getPlan, getPlanKpiHistory, getPlanUsers, reassignLocationMarket, updatePlanLocation, updatePlanMarket, setMarketActive } from "../../data/marketsClient.js";
import { useRecordPhotoRefresh } from "../../hooks/useRecordPhotoRefresh.js";
import { getCountdownStyle } from "../leases/leaseRisk.js";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { CHECKED_FILTER_VERSION, normalizeCheckedSelections, checkedPredicateSelections, selectionSetsEqual, withFacetCounts } from "../../utils/checkedSelections.js";
import { activeFilterGroups } from "../../utils/filterHiddenSearch.js";
import { showToast } from "../../components/toastService.jsx";
import { AlertChip } from "../../v2/components/AlertChip.jsx";
import { LinkedWorkflowRunsIndicator } from "../workflows/LinkedWorkflowRunsIndicator.jsx";
import { PageHeaderStack } from "../../components/PageHeaderStack.jsx";
import { PlanTabBar } from "./PlanTabBar.jsx";
import { PhotoPortraitCard } from "../../v2/pulse/PulsePage.jsx";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { useWhatIfScenario, WhatIfKpiStrip, WhatIfBody } from "./WhatIfPane.jsx";
import { MarketPressurePage } from "./MarketPressurePage.jsx";
import { ScenarioViewKey } from "./ScenarioViewKey.jsx";
import { SeatFitViewKey } from "../../components/SeatFitKey.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";
import { COLLECTION_TOOLBAR_STYLES, CollectionCount } from "../../v2/components/InlineFilterToolbar.jsx";
import { ViewControls, CompactSwitch, ExpandAllSwitch } from "../../v2/components/ViewControls.jsx";
import { ToolbarAddButton } from "../../v2/components/ToolbarAddButton.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { DrawerCloseButton } from "../../v2/components/DrawerCloseButton.jsx";
import { computePortfolioKpis, computeAnnualCostBreakdown, selectActiveMarketsForKpis, getOperatingMarketKpiCount, getEstablishingMarketKpiCount, KPI_TILE_DEFS, deriveMarketNextDecision, selectDecisionsAhead, formatKpiQoQDelta, isMarketMember, isCountedMarketOffice, isOperationallyActive, classifyMarketPresence } from "./marketsService.js";
import { DIRECTIVE_TYPE_LABELS, DIRECTIVE_STATUS_LABELS } from "./StrategyBand.jsx";
import { UnifiedContextStrip } from "./UnifiedContextStrip.jsx";
import { StrategyTabPane } from "./StrategyTabPane.jsx";
import { DirectiveDrawer } from "./DirectiveDrawer.jsx";
import { getDirectives } from "./directivesClient.js";
import { getPolicyBaseline, getEffectiveSpacePolicy, SPACE_POLICY_FALLBACK_NOTICE, normaliseForScenario } from "./policyClient.js";
import { RecommendationsPane } from "./RecommendationsPane.jsx";
import {
  computeSpaceFit,
  SPACE_FIT_COLOR,
  SPACE_FIT_BG,
  SPACE_FIT_BORDER,
  SPACE_FIT_LABEL,
} from "../../utils/spaceFitVerdict.js";
import { getAnalyticalSqft as getDisplayedSqft } from "../../utils/locationSqftDerived.js";
import {
  useSensitivityPolicy,
  canAccessMarketsStrategy,
  canonicalizeMarketsTab,
} from "../../contexts/SensitivityPolicyContext.jsx";
import { useFeatureFlagContext, FEATURE_MARKETS_STRATEGY_TABS } from "../../contexts/FeatureFlagContext.jsx";
import { COLLECTION_GEOMETRY, collectionRowHeight, COLLECTION_ONE_LINE, LIST_PRESENTATION, listHeaderEdges } from "../../v2/components/listPresentation.js";
import { useListHeaderStuck } from "../../v2/hooks/useListHeaderStuck.js";
import { useCollectionCellTitles } from "../../v2/hooks/useCollectionCellTitles.js";
import { currentMarketLadders, ladderForMarket, selectMarketNextDate, summarizeMarketsList, marketsListSortValue, marketDateDisclosure, marketsListSortPending } from "./marketsListModel.js";
import { MARKETS_COLLECTION_COL, MARKETS_COLLECTION_COL_NO_COST, marketsCollectionWidth, MarketCountry, MarketsCapacityPill, MarketsAnnualCost, MarketsNextDate } from "./MarketsListCells.jsx";
import { useMarketsListGeometry } from "./useMarketsListGeometry.js";

// ─── Strategy vocab ────────────────────────────────────────────────────────────

// One shared RAG vocabulary for all strategy/disposition pills across this
// file — sourced from DISPOSITION_META so the same colors (green/blue/amber/
// red) are used everywhere: green=Active/Maintain, blue=In build/Expand,
// amber=Right-size, red=Exit. Do NOT reintroduce T.teal or T.dispRightsizeFg
// here — those cyan/teal shades were the pre-unification inconsistency.
// PILL-STYLING-NORMALIZE: canonical RAG token pairs for all five strategies.
//   Maintain → ragGreen, Expand → buildBlue, Right-size → ragAmber,
//   Consolidate → ragAmber (same as Right-size), Exit → vacated (slate/tan).
const STRATEGY_META = {
  maintain:    { label: "Maintain",    color: T.ragGreenText,           bg: T.ragGreenBg,           border: `1px solid ${T.border}` },
  expand:      { label: "Expand",      color: DISPOSITION_META.expand.color, bg: DISPOSITION_META.expand.bg, border: `1px solid ${T.border}` },
  consolidate: { label: "Consolidate", color: T.ragAmberText,           bg: T.ragAmberBg,           border: `1px solid ${T.border}` },
  rightsize:   { label: "Right-size",  color: T.ragAmberText,           bg: T.ragAmberBg,           border: `1px solid ${T.border}` },
  exit:        { label: "Exit",        color: T.vacatedFg,              bg: T.vacatedBg,            border: `1px solid ${T.border}` },
  relocate:    { label: "Relocate",    color: T.ragAmberText,           bg: T.ragAmberBg,           border: `1px solid ${T.border}` },
  enter:       { label: "Enter",       color: DISPOSITION_META.expand.color, bg: DISPOSITION_META.expand.bg, border: `1px solid ${T.border}` },
};

// PLACES-MARKETS-DRAWER: the market-plan WRITE vocabulary is the server's
// VALID_STRATEGIES (server/routes/plan.js PUT /api/plan/markets/:id) —
// expand, maintain, consolidate, relocate, exit, enter. "rightsize" is a
// filter/sort value for legacy rows only and is rejected on write, so every
// market-plan editor offers exactly this list.
export const MARKET_PLAN_STRATEGY_OPTIONS = [
  { value: "maintain",    label: "Maintain"    },
  { value: "expand",      label: "Expand"      },
  { value: "consolidate", label: "Consolidate" },
  { value: "relocate",    label: "Relocate"    },
  { value: "enter",       label: "Enter"       },
  { value: "exit",        label: "Exit"        },
];

// Canonical strategy progression (MARKET-PLAN-SORT-FILTER-S1) — Maintain,
// Expand, Consolidate, Right-size, Exit. Drives both the Strategy column
// sort order (STRATEGY_ORDER below) and the Filter panel's option order.
// "Right-size" keeps its locked lowercase-"s" casing — do not title-case it.
export const STRATEGY_OPTIONS = [
  { value: "maintain",    label: "Maintain"    },
  { value: "expand",      label: "Expand"      },
  { value: "consolidate", label: "Consolidate" },
  { value: "rightsize",   label: "Right-size"  },
  { value: "exit",        label: "Exit"        },
];

// ─── Status vocab ──────────────────────────────────────────────────────────────

const STATUS_META = {
  Active:      { label: "Active",      color: T.utilGreenText, bg: T.utilGreen   },
  Prospecting: { label: "Prospecting", color: T.blue,          bg: T.blueSoft    },
  Integrating: { label: "Integrating", color: T.ragAmberText,  bg: T.ragAmberBg  },
  Exited:      { label: "Exited",      color: "var(--v2-light)", bg: T.lightGray   },
};

// Presence is the realized market footprint, separate from lifecycle status
// and the user's strategy intent. Keep its palette distinct so a row can
// communicate both axes at once (for example, an Exited status and Exited
// presence badge).
const PRESENCE_META = {
  Establishing: {
    label: "Establishing",
    color: T.ragAmber,
    tooltip: "Building an office here; none open yet.",
  },
  Prospecting: {
    label: "Prospecting",
    color: T.marketProspectFg,
    tooltip: "Evaluating this market; no office yet.",
  },
  Exited: {
    label: "Exited",
    color: T.light,
    tooltip: "No active office remains in this market.",
  },
};

// ─── Candidate status vocab (MARKETS-S5) ──────────────────────────────────────

const CAND_STATUS_META = {
  under_review: { label: "Under Review", color: "var(--v2-mid)", bg: T.lightGray  },
  shortlisted:  { label: "Shortlisted",  color: T.blue,          bg: T.blueSoft   },
  active:       { label: "Active",       color: T.utilGreenText, bg: T.utilGreen  },
  paused:       { label: "Paused",       color: T.ragAmber,      bg: T.ragAmberBg },
  rejected:     { label: "Rejected",     color: T.ragRed,        bg: T.ragRedBg   },
};
const CAND_STATUS_ORDER = ["under_review", "shortlisted", "active", "paused", "rejected"];
const CAND_EMPTY_FORM   = {
  name: "", address: "", floor: "", visitedDate: "",
  status: "under_review", leaseType: "", broker: "", prosRaw: "", consRaw: "",
};

const STATUS_OPTIONS = [
  { value: "Active",      label: "Active"      },
  { value: "Prospecting", label: "Prospecting" },
  { value: "Integrating", label: "Integrating" },
  { value: "Exited",      label: "Exited"      },
];

// PLAN-MARKET-EXITED-STATE-S1: a market is "Exited" — the realized, locked
// state — when either its Status field says so, or the legacy admin-only
// `active` lifecycle flag has been switched off. These two signals used to
// drive two independent, redundant indicators ("Exited" status badge vs. an
// "INACTIVE" pill); this helper is the single source of truth both the
// display and the edit-lock read from. Distinct from the Strategy "exit"
// intent (`market.strategy === "exit"`), which is left untouched.
function isMarketExited(market) {
  return market?.status === "Exited" || market?.active === false;
}

// ─── Sort constants ────────────────────────────────────────────────────────────

// Canonical strategy progression — kept in sync with STRATEGY_OPTIONS above.
const STRATEGY_ORDER = ["maintain", "expand", "consolidate", "rightsize", "exit"];
// Numeric sort keys default their first-click direction to descending
// (biggest/soonest → smallest); non-numeric keys default to ascending. Used
// by both the column-header click handler (handleSort) and PLAN_SORT_FIELDS
// below, so header clicks and the toolbar Sort tool agree.
// "nextDecision" is intentionally excluded: it stores days-until-deadline,
// where smaller = more urgent, so its default direction must stay ascending
// (soonest first) even though the underlying value is numeric.
const NUMERIC_SORT_KEYS = ["people", "seatsCapacity", "seats", "headroom", "cost", "completeness", "capacity"];
const DEFAULT_SORT_KEY = "people";
const DEFAULT_SORT_DIR = "desc";

// Suggested fields for the shared Sort tool — generic {value,label} pairs.
// The Sort component itself takes this list as a prop rather than hardcoding
// Market Plan-only field names, so it can be reused on Offices/Leases later.
// `numeric: true` drives the shared SortControl's first-selection default
// direction (descending); fields without it default to ascending.
// "nextDecision" is deliberately left off `numeric` even though its value is
// a number — days-until-deadline is more-urgent-when-smaller, so its default
// must be ascending (soonest first), matching NUMERIC_SORT_KEYS above.
const PLAN_SORT_FIELDS = [
  { value: "market",        label: "Market name" },
  { value: "strategy",      label: "Strategy" },
  { value: "completeness",  label: "Plan completeness",  numeric: true },
  { value: "people",        label: "People (headcount)", numeric: true },
  { value: "seatsCapacity", label: "Seats (capacity)",   numeric: true },
  { value: "seats",         label: "Assigned %",         numeric: true },
  { value: "headroom",      label: "Expansion",           numeric: true },
  { value: "cost",          label: "$/yr",               numeric: true },
  { value: "nextDecision",  label: "Next decision" },
];
const MARKETS_SORT_FIELDS = [
  ...PLAN_SORT_FIELDS.filter(field => field.value !== "nextDecision").map(field => ({
    ...field, label: field.value === "people" ? "Assigned" : field.value === "seatsCapacity" ? "Seats (installed)" : field.label,
  })),
  { value: "capacity", label: "Capacity", numeric: true },
  { value: "nextDate", label: "Next date" },
];

const PLAN_TABS = [
  { key: "strategy",        label: "Strategy" },
  { key: "plan",            label: "Plan"     },
  { key: "market_pressure", label: "Pressure" },
  { key: "whatif",          label: "Scenario" },
  { key: "recommendations", label: "Guidance" },
];

// Map URL path segment → internal planView key.
// Unknown segments fall back to "plan" in the component.
const TAB_SEGMENT_TO_VIEW = {
  strategy: "strategy",
  plan:     "plan",
  pressure: "market_pressure",
  scenario: "whatif",
  guidance: "recommendations",
};

// Default prefs for the Plan page (used by useUiPrefs initializer).
// viewMode: retained for backwards-compat with stored prefs but no longer
// written when switching tabs — the URL path is now the source of truth.
// density: "comfortable" | "compact" — CSS-only row-height density for Plan view.
const PLAN_PREFS_DEFAULT = {
  viewMode:     "plan",
  density:      "comfortable",
  cfSelections: {},
  search:       "",
  sortKey:      DEFAULT_SORT_KEY,
  sortDir:      DEFAULT_SORT_DIR,
  sortKey2:     null,
  sortDir2:     "asc",
};

// ─── Utility helpers ───────────────────────────────────────────────────────────

function isSentinelDate(raw) {
  if (!raw) return false;
  const s = typeof raw === "string" ? raw : String(raw);
  if (s.startsWith("9999-")) return true;
  const yr = parseInt(s.slice(0, 4), 10);
  return !isNaN(yr) && yr > 2100;
}

export function fmtDateShort(raw) {
  if (!raw) return "—";
  if (isSentinelDate(raw)) return "—";
  const s = typeof raw === "string" ? raw : String(raw);
  const d = new Date(s.includes("T") ? s : s + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

function fmtDateInput(raw) {
  if (!raw) return "";
  const s = typeof raw === "string" ? raw : String(raw);
  return s.slice(0, 10);
}

// "Jan 2027" — month + full year, used in the drawer's planned-seats row.
function fmtMonthYear(raw) {
  if (!raw || isSentinelDate(raw)) return null;
  const s = typeof raw === "string" ? raw : String(raw);
  const d = new Date(s.includes("T") ? s : s + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// "Jan '27" — compact month + 2-digit year, used in the expand table cell.
function fmtMonthYearCompact(raw) {
  if (!raw || isSentinelDate(raw)) return null;
  const s = typeof raw === "string" ? raw : String(raw);
  const d = new Date(s.includes("T") ? s : s + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const mon = d.toLocaleDateString("en-US", { month: "short" });
  const yr  = String(d.getFullYear()).slice(2);
  return `${mon} '${yr}`;
}

/** Returns months between now and a date string; negative if in the past. */
function monthsUntil(raw) {
  if (!raw) return null;
  const s = typeof raw === "string" ? raw : String(raw);
  const d = new Date(s.includes("T") ? s : s + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  return (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth());
}

/**
 * Maps the plan route's raw leaseStatus DB value + location stage to the
 * canonical LEASE_STATUS_META bucket key. Mirrors statusFor() logic using
 * the fields available in the plan payload (no leaseType available here).
 * Returns null when there is no lease to display.
 */
function planLeaseStatusBucket(rawStatus, locationStage) {
  if (!rawStatus) return null;
  if (rawStatus === "draft")        return "draft";
  if (rawStatus === "expired")      return "expired";
  if (rawStatus === "sublease_out") return "sublease_out";
  if (rawStatus === "active") {
    return locationStage === "vacated" ? "vacant_held" : "in_use";
  }
  return null;
}

/** Maps a raw eventLabel string to a canonical short type word. */
function cleanEventType(raw) {
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower.includes("expir"))   return "expiry";
  if (lower.includes("break"))   return "break";
  if (lower.includes("renewal")) return "renewal notice";
  if (lower.includes("option"))  return "option deadline";
  const firstWord = lower.split(/\s+/)[0] ?? "";
  return firstWord.slice(0, 20);
}

/** Countdown label like "in 8 mo" or "6 mo ago". */
function countdownLabel(mos) {
  if (mos === null) return null;
  if (mos > 0)  return `in ${mos} mo`;
  if (mos === 0) return "this month";
  return `${Math.abs(mos)} mo ago`;
}

// ─── PlanPill — shared visual shape for status + disposition pills ───────────
// One pill silhouette (padding/radius/weight/border treatment) reused across
// StatusBadge and DispositionBadge; only color/background vary per semantic
// value. Keeps the "status" and "disposition" vocabularies visually aligned.

function PlanPill({ label, color, bg, testId, onClick, disabled, selected = true, width, title, ariaLabel }) {
  const interactive = typeof onClick === "function";
  const baseStyle = {
    display:        "inline-flex",
    alignItems:     "center",
    justifyContent: "center",
    height:         22,
    padding:        "0 10px",
    borderRadius:   6,
    fontSize:       T.fsXs,
    fontWeight:     T.fwSemibold,
    lineHeight:     1,
    flexShrink:     0,
    whiteSpace:     "nowrap",
    ...(width != null ? { width, boxSizing: "border-box" } : null),
  };

  // Interactive variant (e.g. the office drawer's disposition selector) keeps
  // the same pill silhouette but swaps the border treatment to show a clear
  // selected/unselected state instead of the static badge's tinted border.
  if (interactive) {
    return (
      <button
        type="button"
        data-testid={testId}
        title={title}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        style={{
          ...baseStyle,
          fontFamily: "inherit",
          cursor:     disabled ? "default" : "pointer",
          color:      selected ? color : T.mid,
          background: selected ? bg : T.white,
          border:     selected ? `2px solid ${color}` : `1px solid ${T.border}`,
          transition: "all 0.1s",
        }}
      >
        {label}
      </button>
    );
  }

  // Static display variant — squarish pill, no border, tinted fill.
  return (
    <span
      data-testid={testId}
      title={title}
      aria-label={ariaLabel}
      style={{
        ...baseStyle,
        color,
        background: bg,
      }}
    >
      {label}
    </span>
  );
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

function StatusBadge({ status, testId }) {
  if (!status) return null;
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <PlanPill
      testId={testId ?? `status-badge-${status.toLowerCase()}`}
      label={meta.label}
      color={meta.color}
      bg={meta.bg}
    />
  );
}

function MarketPresenceBadge({ presence, marketId }) {
  if (presence === "Operating") return null;
  const meta = PRESENCE_META[presence];
  if (!meta) return null;
  return (
    <span
      data-testid={`presence-badge-${marketId}`}
      title={meta.tooltip}
      aria-label={`Market presence: ${meta.label}`}
      style={{
        display:       "inline-flex",
        alignItems:    "center",
        gap:           6,
        color:         meta.color,
        fontSize:      T.fsXxs5,
        fontWeight:    T.fwSemibold,
        letterSpacing: ".04em",
        lineHeight:    1,
        textTransform: "uppercase",
        whiteSpace:    "nowrap",
        flexShrink:    0,
      }}
    >
      <span
        data-testid={`presence-dot-${marketId}`}
        aria-hidden="true"
        style={{
          display:      "inline-block",
          width:        7,
          height:       7,
          borderRadius: "50%",
          background:    meta.color,
          flexShrink:    0,
        }}
      />
      {meta.label}
    </span>
  );
}

// ─── StrategyPill ─────────────────────────────────────────────────────────────

// Fixed width sized to fit the longest Strategy label ("Consolidate") so every
// Strategy pill renders at a uniform width with its label centered, regardless
// of the actual label length ("Expand", "Exit", etc.).
const STRATEGY_PILL_WIDTH = 92;

export function StrategyPill({ strategy, size = "sm" }) {
  const meta = strategy ? STRATEGY_META[strategy] : null;
  if (!meta) {
    return (
      <PlanPill
        testId="strategy-pill-unset"
        label="Not set"
        color="var(--v2-light)"
        bg={T.lightGray}
        width={STRATEGY_PILL_WIDTH}
      />
    );
  }
  return (
    <PlanPill
      testId={`strategy-pill-${strategy}`}
      label={meta.label}
      color={meta.color}
      bg={meta.bg}
      width={STRATEGY_PILL_WIDTH}
    />
  );
}

// ─── RunwayTimeline ────────────────────────────────────────────────────────────
// Dots placed along a 0–36 month track. Each dot = one office's next lease event.
// Color: ≤12mo red, 13-24mo amber, >24mo green, build stage purple.

const TRACK_MONTHS = 36;
const TRACK_W = 120;

function dotColor(stage, mos) {
  if (stage === "build") return T.purple; // purple = in build
  if (mos === null) return "var(--v2-light)";
  if (mos <= 12)  return T.ragRed;
  if (mos <= 24)  return T.ragAmber;
  return "var(--v2-green)";
}

function dotUrgencyLabel(stage, mos) {
  if (stage === "build") return "In build";
  if (mos === null) return "No lease event";
  if (mos <= 0)  return "Overdue";
  if (mos <= 12)  return "≤12 mo";
  if (mos <= 24)  return "13-24 mo";
  return ">24 mo";
}

function RunwayTimeline({ locations }) {
  const dots = locations
    .filter((l) => l.stage !== "draft" && l.stage !== "closed" && l.stage !== "vacated")
    .map((l) => {
      const mos = l.nextDeadline?.dueDate ? monthsUntil(l.nextDeadline.dueDate) : null;
      const isInBuild = l.stage === "build";
      const clampedMos = isInBuild ? TRACK_MONTHS : (mos !== null ? Math.max(0, Math.min(TRACK_MONTHS, mos)) : TRACK_MONTHS - 1);
      return {
        id:         l.locationId,
        name:       l.locationAlias || l.locationId,
        mos,
        isInBuild,
        stage:      l.stage,
        color:      dotColor(l.stage, mos),
        pct:        Math.round((clampedMos / TRACK_MONTHS) * 100),
        label:      dotUrgencyLabel(l.stage, mos),
        eventLabel: l.nextDeadline?.label ?? null,
      };
    });

  if (dots.length === 0) {
    return <span style={{ color: "var(--v2-light)", fontSize: T.fsXs }}>—</span>;
  }

  // Soonest upcoming (future) non-build event for the countdown label
  const soonest = dots
    .filter((d) => !d.isInBuild && d.mos !== null && d.mos > 0)
    .sort((a, b) => a.mos - b.mos)[0] ?? null;

  const countdownStyle = soonest ? getCountdownStyle(soonest.mos) : null;

  return (
    <div
      data-testid="runway-timeline"
      style={{
        display:       "flex",
        flexDirection: "column",
        gap:           3,
        flexShrink:    0,
      }}
    >
      {/* Dot track */}
      <div style={{
        position:   "relative",
        width:      TRACK_W,
        height:     14,
        display:    "flex",
        alignItems: "center",
        flexShrink: 0,
      }}>
        {/* Track line */}
        <div style={{
          position:     "absolute",
          left:         0,
          right:        0,
          height:       2,
          borderRadius: 1,
          background:   T.border,
        }} />
        {/* Faint year ticks at 1y / 2y / 3y */}
        {[12, 24, 36].map((mo) => (
          <div
            key={mo}
            style={{
              position:   "absolute",
              left:       mo < TRACK_MONTHS ? `${Math.round(mo / TRACK_MONTHS * 100)}%` : "100%",
              top:        2,
              width:      1,
              height:     6,
              background: "var(--v2-light)",
              opacity:    0.4,
            }}
          />
        ))}
        {/* Dots */}
        {dots.map((d) => (
          <div
            key={d.id}
            title={`${d.name}: ${d.label}${d.mos !== null ? ` (${countdownLabel(d.mos)})` : ""}`}
            data-testid={`runway-dot-${d.id}`}
            style={{
              position:     "absolute",
              left:         `${d.pct}%`,
              width:        8,
              height:       8,
              borderRadius: "50%",
              background:   d.color,
              transform:    "translateX(-50%)",
              flexShrink:   0,
              zIndex:       1,
              boxShadow:    `0 0 0 1.5px ${T.white}`,
            }}
          />
        ))}
      </div>

      {/* Countdown label for soonest upcoming event */}
      {soonest && countdownStyle && (
        <span
          data-testid="runway-countdown-label"
          style={{
            fontSize:      T.fsXxxs,
            fontWeight:    countdownStyle.fontWeight,
            color:         countdownStyle.color,
            letterSpacing: 0.2,
            lineHeight:    1,
            whiteSpace:    "nowrap",
            overflow:      "hidden",
            textOverflow:  "ellipsis",
            maxWidth:      130,
            display:       "block",
          }}
        >
          {soonest.mos}mo{soonest.eventLabel ? ` · ${cleanEventType(soonest.eventLabel)}` : ""}
        </span>
      )}
    </div>
  );
}


// ─── PlanSpaceFitPill — canonical Space Fit pill for the offices table ───────
//
// Uses the same computeSpaceFit() + SPACE_FIT_* tokens as OfficesPage so that
// the same office produces the same verdict in both views.
//
// Three sample profiles and their expected verdicts (cross-check reference):
//   Profile A — sqft=10 000, seats=100, headcount=80  → fill=0.80, cramped → Dense
//   Profile B — sqft=10 000, seats=100, headcount=105 → fill=1.05 (>1.00) → Over-Capacity
//   Profile C — sqft=5 000,  seats=100, headcount=90  → fill=0.90 (balanced), SF/seat=50 (<150) → Dense
//
// The old SpaceFitDot rendered loc.spaceFitScore.bandLabel from the server-side
// classifySpaceFit() classifier (vocabulary: "Over-seated", "Tight", "Expandable",
// "Right-sized"). That server-side path (server/lib/spaceFit.js) is archived — do
// not touch it. spaceFitScore is still returned by the plan API for other callers;
// we simply no longer render it here as the primary Space Fit label.
//
// TODO(corey): the old bandLabel vocabulary ("Over-seated", "Tight", "Expandable")
// represented a comfort-density lens distinct from the desk-fill verdict above.
// If that "Comfort Density" view should be surfaced as a separate column, wire
// computeComfort() from src/utils/comfortDensity.js into a dedicated "Comfort" column
// rather than labeling it "Space Fit".

function PlanSpaceFitPill({ loc }) {
  if (loc.stage === "draft") return <span style={{ color: T.light }}>—</span>;
  // Preserve Plan's canonical/stored area contract independently of the
  // fallback-inclusive Offices presentation projection.
  const sf      = getDisplayedSqft(loc, null);
  const seats   = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  const people  = loc.headcount != null ? Number(loc.headcount) : null;
  const { verdict } = computeSpaceFit(sf, seats, people);

  if (!verdict) {
    return <span style={{ color: "var(--v2-light)", fontSize: T.fsXs }}>—</span>;
  }

  const color  = SPACE_FIT_COLOR[verdict];
  const bg     = SPACE_FIT_BG[verdict];
  const border = SPACE_FIT_BORDER[verdict];
  const label  = SPACE_FIT_LABEL[verdict].full;

  return (
    <span
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        justifyContent: "center",
        fontSize:     T.fsXs,
        fontWeight:   T.fwSemibold,
        color,
        background:   bg,
        border:       `1px solid ${border}`,
        borderRadius: 6,
        padding:      "1px 8px",
        whiteSpace:   "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── DispositionBadge — read-only pill for location disposition in offices table

export const DISP_META = {
  maintain:  { label: "Maintain",   color: DISPOSITION_META.maintain.color,  bg: DISPOSITION_META.maintain.bg  },
  expand:    { label: "Expand",     color: DISPOSITION_META.expand.color,    bg: DISPOSITION_META.expand.bg    },
  rightsize: { label: "Right-size", color: DISPOSITION_META.rightsize.color, bg: DISPOSITION_META.rightsize.bg },
  exit:      { label: "Exit",       color: DISPOSITION_META.exit.color,      bg: DISPOSITION_META.exit.bg      },
};

export function DispositionBadge({ disposition }) {
  const meta = DISP_META[disposition];
  if (!meta) return null;
  return <PlanPill label={meta.label} color={meta.color} bg={meta.bg} />;
}

// ─── Shared read-only office field renderers ──────────────────────────────────
// PLAN-OFFICE-DRAWER-COLUMNS: single source of truth for "Next lease event",
// "People / Seats", "Planned", and "Headroom" so the offices-in-market table
// and the office popout drawer can never disagree on the computed value.

function NextLeaseEventValue({ loc }) {
  // FORWARD-CAPACITY-CONSUMERS-READ-SCHEDULE: fall back to the office's real
  // lease expiry date when there's no tracked critical-date deadline, so an
  // office with a lease but no upcoming deadline (e.g. Buffalo Grove) shows
  // a date instead of a dash. A tracked deadline always wins when present.
  const hasDeadline = loc.nextDeadline?.dueDate && !isSentinelDate(loc.nextDeadline.dueDate);
  const hasLeaseExpiryFallback = !hasDeadline && loc.leaseExpiry && !isSentinelDate(loc.leaseExpiry);
  const effectiveDate = hasDeadline ? loc.nextDeadline.dueDate : hasLeaseExpiryFallback ? loc.leaseExpiry : null;
  const mos = effectiveDate ? monthsUntil(effectiveDate) : null;
  if (!effectiveDate) {
    return <span style={{ color: "var(--v2-light)" }}>—</span>;
  }
  const dateLabel = fmtMonthYearCompact(effectiveDate) ?? "—";
  // Anchor pattern: date right-aligned, countdown left-aligned — mirrors
  // NextDecisionCell's gridTemplateColumns: "1fr auto" so the two levels
  // share the same visual anchor point within this column.
  return (
    <div style={{ display: "inline-grid", gridTemplateColumns: "1fr auto", width: "100%", alignItems: "baseline", gap: "0 4px" }}>
      <span style={{ color: T.mid, fontWeight: T.fwSemibold, fontSize: T.fsBase, textAlign: "right", whiteSpace: "nowrap" }}>
        {dateLabel}
      </span>
      {mos !== null ? (
        <span
          data-testid="office-countdown"
          style={{
            fontSize:   T.fsXs,
            fontWeight: mos <= 18 ? T.fwSemibold : T.fwRegular,
            color:      mos <= 6 ? T.red : mos <= 18 ? T.pendingAmber : undefined,
            whiteSpace: "nowrap",
            textAlign:  "left",
          }}
        >
          {countdownLabel(mos)}
        </span>
      ) : (
        <span />
      )}
    </div>
  );
}

function PeopleSeatsValue({ loc }) {
  if (loc.stage === "draft") return <span style={{ color: T.light }}>—</span>;
  if (loc.installedCapacity == null) {
    return <span style={{ color: "var(--v2-light)" }}>—</span>;
  }
  // Anchor pattern: count right-aligned — mirrors PeopleSeatsCell's "1fr auto 1fr"
  // grid so the number column shares the same anchor point as the market-level row.
  return (
    <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: "var(--v2-mid)", display: "block", textAlign: "right", width: "100%" }}>
      {loc.headcount != null && <>{loc.headcount} / </>}
      {loc.installedCapacity}
    </span>
  );
}

function PlannedValue({ loc }) {
  if (loc.stage === "draft") return <span style={{ color: T.light }}>—</span>;
  const futureRows = loc.futureScheduleRows;
  // Backward-compat fallback: schedule not loaded yet
  if (!Array.isArray(futureRows)) {
    return loc.plannedSeats > 0 ? (
      <span style={{ fontSize: T.fsBase, fontStyle: "italic", color: T.light }}>
        {loc.plannedSeats.toLocaleString()}
        {fmtMonthYearCompact(loc.onlineDate) && (
          <> · {fmtMonthYearCompact(loc.onlineDate)}</>
        )}
      </span>
    ) : (
      <span style={{ color: T.light }}>—</span>
    );
  }
  if (futureRows.length === 0) {
    return <span style={{ color: T.light }}>—</span>;
  }

  // Running-tally walk: compute signed delta for each future row.
  // Exits (installedSeats===0) always render; non-exit rows are capped at 3.
  let running = loc.installedCapacity ?? 0;
  const rows = futureRows.map((r) => {
    const delta = r.installedSeats - running;
    running = r.installedSeats;
    return { ...r, delta };
  });

  const MAX_NON_EXIT = 3;
  const exitRows     = rows.filter((r) => r.installedSeats === 0);
  const nonExitRows  = rows.filter((r) => r.installedSeats !== 0);
  const shownNonExit = nonExitRows.slice(0, MAX_NON_EXIT);
  const hiddenCount  = nonExitRows.length - shownNonExit.length;

  const shownIds = new Set([
    ...shownNonExit.map((r) => r.id),
    ...exitRows.map((r) => r.id),
  ]);
  const displayed = rows.filter((r) => shownIds.has(r.id));

  return (
    <div
      data-testid={`planned-badge-${loc.locationId}`}
      style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}
    >
      {/* Single shared grid so all rows' number / dot / date tracks align vertically. */}
      <div
        style={{
          display:             "inline-grid",
          gridTemplateColumns: "auto 10px auto",
          rowGap:              2,
          columnGap:           0,
          alignItems:          "baseline",
        }}
      >
        {displayed.map((r) => {
          const isPositive = r.delta > 0;
          const color      = isPositive ? T.greenDeep : T.ragRed;
          const sign       = r.delta >= 0 ? "+" : "−";
          const absVal     = Math.abs(r.delta);
          const dateStr    = fmtMonthYearCompact(r.effectiveDate) ?? "—";
          return [
            <span key={`n-${r.id}`} style={{ color, fontWeight: T.fwSemibold, fontSize: T.fsBase, textAlign: "right" }}>
              {sign}{absVal.toLocaleString()}
            </span>,
            <span key={`dot-${r.id}`} style={{ color: T.light, textAlign: "center", fontSize: T.fsBase }}>·</span>,
            <span key={`date-${r.id}`} style={{ color: T.mid, fontWeight: T.fwSemibold, fontSize: T.fsBase, textAlign: "left" }}>
              {dateStr}
            </span>,
          ];
        })}
      </div>
      {hiddenCount > 0 && (
        <span
          data-testid={`planned-more-${loc.locationId}`}
          style={{ fontSize: T.fsXxxs, color: T.light }}
        >
          +{hiddenCount} more
        </span>
      )}
    </div>
  );
}

function HeadroomValue({ loc, center = false }) {
  if (loc.headroom == null) {
    const dash = <span style={{ color: "var(--v2-light)" }}>—</span>;
    if (!center) return dash;
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <span style={{ color: "var(--v2-light)", display: "inline-block", minWidth: "3.5em", textAlign: "right" }}>—</span>
      </div>
    );
  }
  const color = loc.headroom > 0 ? T.greenDeep : loc.headroom < 0 ? T.ragRed : "var(--v2-mid)";
  if (!center) {
    return (
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color, display: "block", textAlign: "right", width: "100%" }}>
        {loc.headroom > 0 ? `+${loc.headroom}` : loc.headroom}
      </span>
    );
  }
  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color, display: "inline-block", minWidth: "3.5em", textAlign: "right" }}>
        {loc.headroom > 0 ? `+${loc.headroom}` : loc.headroom}
      </span>
    </div>
  );
}

// ─── MarketPlanScopeDrawer — right-side drawer for market-scope editing ──────

const DRAWER_DISP_ORDER = ["expand", "maintain", "rightsize", "exit"];

const DRAWER_DISP_META = {
  expand:    { label: "Expand",     color: DISPOSITION_META.expand.color,    bg: DISPOSITION_META.expand.bg    },
  maintain:  { label: "Maintain",   color: DISPOSITION_META.maintain.color,  bg: DISPOSITION_META.maintain.bg  },
  rightsize: { label: "Right-size", color: DISPOSITION_META.rightsize.color, bg: DISPOSITION_META.rightsize.bg },
  exit:      { label: "Exit",       color: DISPOSITION_META.exit.color,      bg: DISPOSITION_META.exit.bg      },
};

const EXIT_REASON_OPTIONS = [
  { value: "consolidate", label: "Consolidate" },
  { value: "relocate",    label: "Relocate"    },
  { value: "close",       label: "Close"       },
];

const HEALTH_DISPLAY = {
  set:          { label: "Set",          color: T.green,    bg: T.greenSoft   },
  needs_review: { label: "Needs Review", color: T.ragAmber, bg: T.ragAmberBg  },
  unset:        { label: "Not set",      color: T.light,    bg: T.lightGray   },
};

function clientDeriveHealth(draft, loc) {
  if (!draft.disposition) return "unset";
  if (!draft.planNote || !draft.targetDate) return "needs_review";
  const sixMonthsMs = 6 * 30 * 24 * 60 * 60 * 1000;
  if (loc.planSetAt && new Date(loc.planSetAt) < new Date(Date.now() - sixMonthsMs)) {
    return "needs_review";
  }
  return "set";
}

function MarketPlanScopeDrawer({ market, canEdit, onClose, onSaved, initialPlanNote, onPrev, onNext, hasPrev, hasNext }) {
  // Drawer keyboard navigation — ArrowLeft/ArrowRight mirror the ‹ › caret
  // buttons while the market editor is open, but are ignored while the user
  // is typing in the Plan note textarea or interacting with the date field.
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === "TEXTAREA" || (tag === "INPUT" && active.type !== "checkbox" && active.type !== "radio")) {
        return;
      }
      if (e.key === "ArrowLeft" && hasPrev && onPrev) onPrev();
      if (e.key === "ArrowRight" && hasNext && onNext) onNext();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasPrev, hasNext, onPrev, onNext]);

  return (
    <MarketPlanDrawer
      market={market}
      canEdit={canEdit}
      onClose={onClose}
      onSaved={onSaved}
      initialPlanNote={initialPlanNote}
      onPrev={onPrev}
      onNext={onNext}
      hasPrev={hasPrev}
      hasNext={hasNext}
    />
  );
}

// DRAWER-MARKETPLAN-REWORK: Bordered KPI tile used in the v3 drawer grid.
// Renders a label / value / optional-descriptor stack inside a rounded border.
function KpiTileV3({ label, testId, descriptor, children }) {
  return (
    <div
      data-testid={testId}
      style={{
        border:        `1px solid ${T.border}`,
        borderRadius:  9,
        padding:       "8px 10px",
        display:       "flex",
        flexDirection: "column",
        gap:           2,
        minWidth:      0,
      }}
    >
      <div style={{ fontSize: T.fsXxxs, textTransform: "uppercase", letterSpacing: 0.4, color: T.light, fontWeight: T.fwBold, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </div>
      <div style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, lineHeight: 1.3, minHeight: 18 }}>
        {children}
      </div>
      {descriptor && (
        <div style={{ fontSize: T.fsXxxs, color: T.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {descriptor}
        </div>
      )}
    </div>
  );
}

function OfficeScopeDrawer({ loc, canEdit: canEditRole, onClose, onSaved, onBusyChange, onNavigateRelated }) {
  const { canSee } = useSensitivityPolicy();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  const [draft, setDraft] = useState(() => ({
    disposition:      loc.disposition       ?? null,
    exitReason:       loc.exitReason        ?? null,
    targetDate:       fmtDateInput(loc.targetActionDate),
    planNote:         loc.planNote          ?? "",
  }));
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);
  useEffect(() => {
    onBusyChange?.(saving);
    return () => onBusyChange?.(false);
  }, [saving, onBusyChange]);

  const isDirty =
    (draft.disposition      ?? null) !== (loc.disposition      ?? null) ||
    (draft.exitReason       ?? null) !== (loc.exitReason       ?? null) ||
    (draft.targetDate       ?? "")   !== fmtDateInput(loc.targetActionDate) ||
    (draft.planNote         ?? "")   !== (loc.planNote         ?? "");

  const followRelated = (event) => {
    event.stopPropagation();
    if (onNavigateRelated && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      onNavigateRelated(event.currentTarget.getAttribute("href"));
    }
  };

  function setField(key, value) {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "disposition" && value !== "exit") next.exitReason = null;
      return next;
    });
  }

  const healthKey = clientDeriveHealth(draft, loc);
  const health = HEALTH_DISPLAY[healthKey] ?? HEALTH_DISPLAY.unset;

  // v3 header + signal derived values
  const stageMeta  = STAGES[loc.stage] ?? null;
  const lsKey      = planLeaseStatusBucket(loc.leaseStatus, loc.stage);
  const leaseMeta  = lsKey ? LEASE_STATUS_META[lsKey] : null;
  // Vacated, exited, and vacant-held offices are intentionally read-only in
  // the inline plan card. The office detail page is the place to reopen or
  // otherwise change their lifecycle state.
  const isReadOnly = loc.stage === "vacated" || loc.disposition === "exit" || lsKey === "vacant_held";
  const canEdit = canEditRole && !isReadOnly;
  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const before = {
        disposition:      loc.disposition      ?? null,
        exitReason:       loc.exitReason       ?? null,
        targetActionDate: loc.targetActionDate  ?? null,
        planNote:         loc.planNote         ?? null,
      };
      const patch = {
        disposition:      draft.disposition,
        exitReason:       draft.disposition === "exit" ? (draft.exitReason || null) : null,
        targetActionDate: draft.targetDate || null,
        planNote:         draft.planNote || null,
      };
      const updated = await updatePlanLocation(loc.locationId, patch, before);
      onSaved(loc.locationId, updated, patch);
      onClose();
    } catch (err) {
      setSaveError(err?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid={`office-plan-card-${loc.locationId}`}
      className="inline-office-plan-card"
      onClick={(e) => e.stopPropagation()}
      style={{
        border: `1.5px solid ${isReadOnly ? T.ragAmberBg : T.blueSoft}`,
        background: isReadOnly ? T.ragAmberBg : T.blueSoft,
        borderRadius: 12,
        overflow: "hidden",
        margin: "2px 16px 14px",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>

        {/* Header — v3 skeleton: ID eyebrow → "Market · Alias" title + Hub chip → address → stage + lease chips */}
        <div style={{ padding: "16px 44px 14px 20px", borderBottom: `1px solid ${T.border}`, position: "relative" }}>
          {/* ID eyebrow */}
          <div style={{ fontSize: T.fsXxxs, color: T.light, fontFamily: "monospace", letterSpacing: 0.3, marginBottom: 5 }}>
            {loc.locationId}
          </div>
          {/* "Market · Alias" title row + Hub chip */}
          <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 6, marginBottom: (loc.address ? 5 : 8) }}>
            <span style={{ fontWeight: T.fwBold, fontSize: T.fsMd, color: T.dark }}>
              {loc.name || loc.locationId}
            </span>
            {loc.locationAlias && (
              <>
                <span style={{ color: T.border }}>·</span>
                <span style={{ fontSize: T.fsMd, color: T.mid }}>{loc.locationAlias}</span>
              </>
            )}
            {loc.isHub && (
              <span style={{
                background:   T.hubChipBg,
                border:       `1px solid ${T.hubChipBorder}`,
                color:        T.hubChipText,
                borderRadius: 99,
                padding:      "1px 7px",
                fontSize:     T.fsXxs,
                fontWeight:   T.fwSemibold,
              }}>
                ◆ Hub
              </span>
            )}
          </div>
          {/* Address line — only when present */}
          {loc.address && (
            <div style={{ fontSize: T.fsXs, color: T.light, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {loc.address}
            </div>
          )}
          {/* Chips row: Office stage + Lease status */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {stageMeta && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: stageMeta.bg, borderRadius: 6, padding: "2px 10px" }}>
                <span style={{ fontSize: T.fsXxxs, textTransform: "uppercase", letterSpacing: 0.4, color: stageMeta.color, fontWeight: T.fwBold }}>Stage</span>
                <span style={{ fontSize: T.fsXs, color: stageMeta.color, fontWeight: T.fwSemibold }}>{stageMeta.label}</span>
              </span>
            )}
            {leaseMeta && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: leaseMeta.bg, borderRadius: 6, padding: "2px 10px" }}>
                <span style={{ fontSize: T.fsXxxs, textTransform: "uppercase", letterSpacing: 0.4, color: leaseMeta.color, fontWeight: T.fwBold }}>Lease</span>
                <span style={{ fontSize: T.fsXs, color: leaseMeta.color, fontWeight: T.fwSemibold }}>{leaseMeta.label}</span>
              </span>
            )}
          </div>
          <button
            type="button"
            data-testid="office-plan-collapse"
            aria-label="Collapse office plan"
            onClick={onClose}
            style={{
              position: "absolute", top: 14, right: 18, border: "none",
              background: "transparent", color: T.mid, cursor: "pointer",
              fontSize: T.fsXs, fontWeight: T.fwBold, padding: "4px 0",
            }}
          >
            Collapse ▲
          </button>
        </div>

        {isReadOnly && (
          <div
            data-testid="office-plan-readonly-banner"
            style={{ padding: "9px 20px", background: T.ragAmberBg, color: T.ragAmber, fontSize: T.fsXs, borderBottom: `1px solid ${T.ragAmberBg}` }}
          >
            This office is read-only because it is {loc.stage === "vacated" ? "vacated" : lsKey === "vacant_held" ? "Vacant-Held" : "planned to exit"}.
          </div>
        )}

        {/* 3. KPI tiles — six cards in the full-width card */}
        <div style={{ padding: "14px 20px", background: T.white }}>
          {/* Row 1: People / Seats · Headroom · Peak */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${canSeeRentCost ? 6 : 5}, minmax(0, 1fr))`, gap: 8 }}>
            <KpiTileV3 label="People / Seats" testId="office-plan-kpi-people" descriptor="assigned vs installed">
              <PeopleSeatsValue loc={loc} />
            </KpiTileV3>
            <KpiTileV3 label="Expansion capacity" testId="office-plan-kpi-headroom" descriptor="room to grow">
              <HeadroomValue loc={loc} />
            </KpiTileV3>
            <KpiTileV3 label="Peak" testId="office-plan-kpi-peak" descriptor="busiest day (3 mo)">
              <span style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
                {loc.peakPct != null ? `${loc.peakPct}%` : "—"}
              </span>
            </KpiTileV3>
            {canSeeRentCost && <KpiTileV3 label="Annual rent" testId="office-plan-kpi-rent" descriptor={hasRecoveryBasis(loc) ? "net recorded" : "rent only"}>
              <span style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
                {loc.annualRentUsd != null ? fmtKpiUsd(loc.annualRentUsd) : "—"}
              </span>
              <AnnualRentBreakdown lease={loc} />
            </KpiTileV3>}
            <KpiTileV3 label="Next lease event" testId="office-plan-kpi-lease-event" descriptor={loc.nextDeadline?.label ?? "—"}>
              <NextLeaseEventValue loc={loc} />
            </KpiTileV3>
            <KpiTileV3 label="Planned seats" testId="office-plan-kpi-planned" descriptor={loc.plannedSeats > 0 ? "planned" : "no change planned"}>
              {loc.plannedSeats > 0 ? (
                <span
                  data-testid="office-plan-planned-seats"
                  style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, fontStyle: "italic", color: T.dark }}
                >
                  {loc.plannedSeats.toLocaleString()} planned
                  {fmtMonthYear(loc.onlineDate) && (
                    <> · live {fmtMonthYear(loc.onlineDate)}</>
                  )}
                </span>
              ) : (
                <span style={{ fontSize: T.fsSm5, color: T.light }}>—</span>
              )}
            </KpiTileV3>
          </div>
        </div>

        {/* 4. Three-column plan form */}
        <div style={{ padding: "16px 20px", background: T.white }}>
          <div style={{ fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: T.fwBold, color: T.light, marginBottom: 10 }}>
            {isReadOnly ? "Plan (read-only)" : "Set the plan"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1.2fr", gap: 14, alignItems: "start" }}>
            <div>
              <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>Disposition</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {DRAWER_DISP_ORDER.map((d) => {
                  const meta = DRAWER_DISP_META[d];
                  return (
                    <PlanPill
                      key={d}
                      label={meta.label}
                      color={meta.color}
                      bg={meta.bg}
                      testId={`disp-pill-${d}`}
                      selected={draft.disposition === d}
                      disabled={!canEdit}
                      onClick={() => canEdit && setField("disposition", d)}
                    />
                  );
                })}
              </div>
              {draft.disposition === "exit" && (
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: T.fsXs, color: T.mid, display: "block", marginBottom: 4 }}>Exit reason</label>
                  <select
                    data-testid="exit-reason-select"
                    value={draft.exitReason ?? ""}
                    disabled={!canEdit}
                    onChange={(e) => canEdit && setField("exitReason", e.target.value || null)}
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: T.fsBase, color: T.dark, background: T.white }}
                  >
                    <option value="">Select reason…</option>
                    {EXIT_REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, marginBottom: 6 }}>Plan confidence</div>
                <span data-testid="plan-health-badge" style={{ display: "inline-flex", alignItems: "center", padding: "2px 10px", borderRadius: 6, fontSize: T.fsXs, fontWeight: T.fwSemibold, color: health.color, background: health.bg }}>
                  {health.label}
                </span>
                <div data-testid="plan-health-caption" style={{ fontSize: T.fsXxs, color: T.light, marginTop: 6 }}>
                  Your confidence in this plan (separate from Space Fit / Lease Health).
                </div>
              </div>
            </div>
            <div>
              <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, display: "block", marginBottom: 6 }}>Target action date</label>
              <DateInput
                type="date"
                data-testid="target-date-input"
                value={draft.targetDate}
                disabled={!canEdit}
                onChange={(e) => canEdit && setField("targetDate", e.target.value)}
                style={{ padding: "7px 30px 7px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: T.fsBase, fontFamily: "inherit", background: T.white, cursor: canEdit ? "pointer" : "default" }}
              />
            </div>
            <div>
              <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, display: "block", marginBottom: 6 }}>Plan note</label>
              <textarea
                data-testid="plan-note-input"
                value={draft.planNote}
                disabled={!canEdit}
                placeholder={canEdit ? "What's the plan for this office?" : "No plan note."}
                onChange={(e) => canEdit && setField("planNote", e.target.value)}
                rows={4}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: T.fsBase, color: T.dark, background: T.white, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", cursor: canEdit ? "text" : "default" }}
              />
            </div>
          </div>

          {/* Save error */}
          {saveError && (
            <div data-testid="office-plan-save-error" style={{ color: T.ragRed, fontSize: T.fsXs, marginTop: 10 }}>
              {saveError}
            </div>
          )}
        </div>

        {/* 5. Related links */}
        <div style={{ padding: "12px 20px", background: T.white, borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontSize: T.fsXxxs, textTransform: "uppercase", letterSpacing: 0.5, color: T.light, fontWeight: T.fwBold, marginBottom: 8 }}>
            Related
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {loc.leaseId && (
              <InternalLink
                data-testid="office-plan-related-lease"
                href={buildLeaseUrl(loc.leaseId)}
                onClick={followRelated}
                style={{
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "space-between",
                  padding:        "8px 10px",
                  borderRadius:   7,
                  border:         `1px solid ${T.border}`,
                  background:     T.white,
                  textDecoration: "none",
                  color:          T.dark,
                }}
              >
                <span style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold }}>Lease</span>
                <span style={{ fontSize: T.fsXxs, color: T.light }}>→</span>
              </InternalLink>
            )}
            <InternalLink
              data-testid="office-plan-related-office"
              href={buildLocationUrl(loc.locationId)}
              onClick={followRelated}
              style={{
                display:        "flex",
                alignItems:     "center",
                justifyContent: "space-between",
                padding:        "8px 10px",
                borderRadius:   7,
                border:         `1px solid ${T.border}`,
                background:     T.white,
                textDecoration: "none",
                color:          T.dark,
              }}
            >
                <span style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold }}>Office detail</span>
              <span style={{ fontSize: T.fsXxs, color: T.light }}>→</span>
            </InternalLink>
          </div>
        </div>

        {/* 6. Footer */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.border}`, background: T.white, display: "flex", alignItems: "center", gap: 8 }}>
          {canEdit && (
            <>
              <button
                type="button"
                data-testid="office-plan-cancel"
                onClick={onClose}
                style={{
                  padding:      "7px 14px",
                  borderRadius: 6,
                  border:       `1px solid ${T.border}`,
                  background:   T.white,
                  color:        T.mid,
                  fontWeight:   T.fwSemibold,
                  fontSize:     T.fsBase,
                  cursor:       "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="office-plan-save"
                disabled={saving || !isDirty}
                onClick={handleSave}
                style={{
                  padding:      "7px 14px",
                  borderRadius: 6,
                  border:       `1px solid ${T.border}`,
                  background:   T.white,
                  color:        T.mid,
                  fontWeight:   T.fwSemibold,
                  fontSize:     T.fsBase,
                  cursor:       saving || !isDirty ? "default" : "pointer",
                  opacity:      saving || !isDirty ? 0.65 : 1,
                }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {/* PLAN-OFFICE-DRAWER-SAVE-INDICATOR */}
              {!saving && (
                <span
                  data-testid="office-plan-dirty-indicator"
                  style={{
                    fontSize:   T.fsXxs,
                    fontWeight: T.fwSemibold,
                    color:      isDirty ? T.ragAmber : T.green,
                    display:    "inline-flex",
                    alignItems: "center",
                    gap:        4,
                  }}
                >
                  {isDirty ? "● Unsaved changes" : "✓ Saved"}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── computeMarketComposition — "<n> offices · <n> in build" summary ─────────
// Shared by the matrix row's collapsed composition string and the drawer's
// rich header subtitle, so the two never drift apart.

function computeMarketComposition(market) {
  const locs = market.locations ?? [];
  // STAGE-VS-LEASE-STATUS-DECOUPLE: membership counts every non-closed office
  // (including "vacated" — still an existing, plannable office, just unoccupied)
  // as part of the market. Only "closed" removes an office from the count.
  // Capacity/utilization math (computeMarketMetrics) deliberately still
  // excludes "vacated" separately — that's an occupancy concern, not membership.
  const activeLocs = locs.filter((l) => l.stage !== "draft" && l.stage !== "closed");
  const inBuildCount = activeLocs.filter(
    (l) => l.stage === "build"
  ).length;
  return { activeCount: activeLocs.length, inBuildCount };
}

const PHOTO_GRAD_BY_REGION = {
  AMER: T.photoGradAmer,
  EMEA: T.photoGradEmea,
  APAC: T.photoGradApac,
};

function MarketContextBand({ market }) {
  const { canSee } = useSensitivityPolicy();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  const [imageFailed, setImageFailed] = useState(false);
  const composition = computeMarketComposition(market);
  const totalSeats = marketInstalledToday(market);
  const totalHeadcount = marketAssignedToday(market);
  const utilization = totalHeadcount != null && totalSeats > 0 ? Math.round(totalHeadcount / totalSeats * 100) : null;
  const heroImage = market.photoUrl ?? market.imageUrl;
  const heroGradient = PHOTO_GRAD_BY_REGION[market.region] ?? T.photoGradAmer;
  const stats = [
    canSeeRentCost && ["Annual rent", market.totalAnnualCost != null ? fmtKpiUsd(market.totalAnnualCost) : "—", "all-in, fully-loaded"],
    ["Assigned / seats", <span key="assignment">{totalHeadcount ?? "—"}<PartialMarker coverage={market.assignmentToday?.assigned} measure="assignment" /> / {totalSeats ?? "—"}<PartialMarker coverage={market.assignmentToday?.seats} measure="installed seats" /></span>, utilization != null ? `${utilization}% spoken for` : "assignment or seats unavailable"],
    ["Expansion", market.totalHeadroom != null ? `${market.totalHeadroom > 0 ? "+" : ""}${market.totalHeadroom}` : "—", "room to grow"],
    ["Next lease wall", market.nextLeaseActionQuarter ?? "—", "nearest expiry / break"],
    ["Offices", composition.activeCount, composition.inBuildCount ? `${composition.inBuildCount} in build` : "in market"],
  ].filter(Boolean);

  return (
    <div data-testid={`market-context-band-${market.id}`} style={{ background: T.white }}>
      <div
        data-testid={`market-hero-${market.id}`}
        style={{
          position: "relative",
          height: 118,
          overflow: "hidden",
          borderBottom: `1px solid ${T.border}`,
          background: heroGradient,
        }}
      >
        {heroImage && !imageFailed && (
          <img
            data-testid={`market-hero-image-${market.id}`}
            src={heroImage}
            alt=""
            onError={() => setImageFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(12,24,44,.72), rgba(12,24,44,.15))", display: "flex", alignItems: "flex-end", padding: "12px 20px" }}>
          <div style={{ color: T.white }}>
            <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold }}>
              {market.name}{market.hasHub || (market.locations ?? []).some((l) => l.isHub) ? " ◆ Hub" : ""}
            </div>
            <div data-testid={`market-hero-subtitle-${market.id}`} style={{ fontSize: T.fsXs, color: "rgba(255,255,255,.78)", marginTop: 2 }}>
              {market.region} · {composition.activeCount} {composition.activeCount === 1 ? "office" : "offices"}
              {composition.inBuildCount ? ` · ${composition.inBuildCount} in build` : ""}
            </div>
          </div>
        </div>
      </div>
      <div
        data-testid={`market-summary-strip-${market.id}`}
        style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`, gap: 10, padding: "13px 20px", background: T.planExpandBg, borderBottom: `1px solid ${T.border}` }}
      >
        {stats.map(([label, value, descriptor]) => (
          <div key={label} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 9, padding: "8px 11px", minWidth: 0 }}>
            <div style={{ fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: ".03em", textTransform: "uppercase", color: T.light }}>{label}</div>
            <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: label === "Expansion" && market.totalHeadroom > 0 ? T.green : T.dark, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
            <div style={{ fontSize: T.fsXxxs, color: T.light, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{descriptor}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MarketPlanDrawer — rich market-scope slide-over ─────────────────────────
// Photo/eyebrow/identity header, pills, full-width completeness bar, callout
// tiles (seats/spoken-for/people), next-lease note, then the editable
// Strategy / Target date / Owner / Plan note fields + Save/Cancel footer.

// PLACES-MARKETS-DRAWER: single source for the four persisted market fields'
// editor contract, shared by the legacy MarketPlanDrawer and the Markets
// record drawer's Plan tab so draft seeding, dirty checks and the
// updatePlanMarket(id, patch, before) payload cannot drift.
export function marketPlanEditorValues(market) {
  return {
    strategy:           market?.strategy           ?? "",
    strategyTargetDate: fmtDateInput(market?.strategyTargetDate),
    owner:              market?.owner              ?? "",
    planNote:           market?.planNote           ?? "",
  };
}
export function marketPlanSavePayload(market, draft) {
  return {
    before: {
      strategy:           market.strategy           ?? null,
      strategyTargetDate: market.strategyTargetDate  ?? null,
      owner:              market.owner               ?? null,
      planNote:           market.planNote            ?? null,
    },
    patch: {
      strategy:           draft.strategy           || null,
      strategyTargetDate: draft.strategyTargetDate || null,
      owner:              draft.owner              || null,
      planNote:           draft.planNote           || null,
    },
  };
}

function MarketPlanDrawer({ market, canEdit: canEditRole, onClose, onSaved, initialPlanNote, onPrev, onNext, hasPrev, hasNext }) {
  // PLAN-MARKET-EXITED-STATE-S1: Exited markets are locked for editing even
  // for editor/admin roles — this effective flag is what every field below
  // reads instead of the raw role-based `canEdit` prop. The "Reactivate
  // market" action (in MarketHeader's ⋯ menu) is admin-gated separately and
  // stays available regardless of this lock.
  const canEdit = canEditRole && !isMarketExited(market);
  const [draft, setDraft] = useState(() => ({
    strategy:           market.strategy           ?? "",
    strategyTargetDate: fmtDateInput(market.strategyTargetDate),
    owner:              market.owner              ?? "",
    planNote:           initialPlanNote           ?? (market.planNote ?? ""),
  }));
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);
  const [users,      setUsers]      = useState([]);

  const composition  = computeMarketComposition(market);
  const completeness = computeMarketCompleteness(market);
  const metrics      = computeMarketMetrics(market);
  const leaseNote    = nextLeaseNote(metrics);

  useEffect(() => {
    let cancelled = false;
    getPlanUsers()
      .then((list) => { if (!cancelled) setUsers(Array.isArray(list) ? list : []); })
      .catch((err) => log.error("MarketPlanDrawer: getPlanUsers failed", { err: String(err) }));
    return () => { cancelled = true; };
  }, []);

  const savedValues = marketPlanEditorValues(market);
  const isDirty =
    draft.strategy           !== savedValues.strategy ||
    draft.strategyTargetDate !== savedValues.strategyTargetDate ||
    draft.owner              !== savedValues.owner ||
    draft.planNote           !== savedValues.planNote;

  function setField(key, value) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const { patch, before } = marketPlanSavePayload(market, draft);
      const updated = await updatePlanMarket(market.id, patch, before);
      if (onSaved) onSaved(market.id, updated, patch);
      onClose();
    } catch (err) {
      setSaveError(err?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // Ensure the currently-saved owner is always selectable, even if the
  // /api/plan/users list doesn't (yet) contain it.
  const ownerOptions = React.useMemo(() => {
    const names = users.map((u) => u.displayName).filter(Boolean);
    if (draft.owner && !names.includes(draft.owner)) names.unshift(draft.owner);
    return names;
  }, [users, draft.owner]);

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="market-drawer-backdrop"
        onClick={onClose}
        style={{
          position:   "fixed",
          inset:      0,
          zIndex:     1000,
          background: "rgba(0,0,0,0.18)",
        }}
      />
      {/* Drawer panel */}
      <div
        data-testid={`market-drawer-${market.id}`}
        style={{
          position:        "fixed",
          top:             0,
          right:           0,
          bottom:          0,
          width:           400,
          background:      T.white,
          boxShadow:       "-4px 0 20px rgba(0,0,0,0.12)",
          zIndex:          1001,
          display:         "flex",
          flexDirection:   "column",
          overflowY:       "auto",
        }}
      >
        {/* Photo */}
        <div
          data-testid={`market-drawer-photo-${market.id}`}
          style={{
            height:          104,
            flexShrink:      0,
            background:      market.photoUrl ? undefined : (PHOTO_GRAD_BY_REGION[market.region] ?? T.photoGradAmer),
            backgroundImage: market.photoUrl ? `url(${market.photoUrl})` : undefined,
            backgroundSize:  market.photoUrl ? "cover" : undefined,
            backgroundPosition: market.photoUrl ? "center" : undefined,
            position:        "relative",
          }}
        >
          {(onPrev || onNext) && (
            <div style={{ position: "absolute", top: 12, right: 48, display: "flex", gap: 2 }}>
              <button
                type="button"
                data-testid="market-drawer-nav-prev"
                aria-label="Previous market"
                disabled={!hasPrev}
                onClick={onPrev}
                style={{
                  background: "rgba(255,255,255,0.16)", border: "none", fontSize: T.fsMd,
                  color: hasPrev ? T.white : "rgba(255,255,255,0.4)", cursor: hasPrev ? "pointer" : "default",
                  padding: "2px 8px", borderRadius: 5, lineHeight: 1,
                }}
              >
                ‹
              </button>
              <button
                type="button"
                data-testid="market-drawer-nav-next"
                aria-label="Next market"
                disabled={!hasNext}
                onClick={onNext}
                style={{
                  background: "rgba(255,255,255,0.16)", border: "none", fontSize: T.fsMd,
                  color: hasNext ? T.white : "rgba(255,255,255,0.4)", cursor: hasNext ? "pointer" : "default",
                  padding: "2px 8px", borderRadius: 5, lineHeight: 1,
                }}
              >
                ›
              </button>
            </div>
          )}
          <DrawerCloseButton onClick={onClose} testId="market-drawer-close" />
        </div>

        {/* Header — eyebrow, name, composition, pills */}
        <div style={{ padding: "16px 20px 14px 20px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: T.fwBold, color: T.light, marginBottom: 4 }}>
            {market.region ? `${market.region} · Market` : "Market"}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <span data-testid="drawer-market-title" style={{ fontWeight: T.fwBold, fontSize: T.fsMd, color: T.dark }}>
              {market.name}
            </span>
          </div>
          <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2, marginBottom: 10 }}>
            {composition.activeCount} {composition.activeCount === 1 ? "office" : "offices"}
            {composition.inBuildCount > 0 && ` · ${composition.inBuildCount} in build`}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 12 }}>
            {market.strategy && STRATEGY_META[market.strategy] && (
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: 0.4, color: T.light }}>Strategy</span>
                <PlanPill
                  testId={`drawer-strategy-pill-${market.strategy}`}
                  label={STRATEGY_META[market.strategy].label}
                  color={STRATEGY_META[market.strategy].color}
                  bg={STRATEGY_META[market.strategy].bg}
                />
              </div>
            )}
            {market.status && (
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: 0.4, color: T.light }}>Status</span>
                <StatusBadge status={market.status} />
              </div>
            )}
          </div>

          {/* Full-width completeness bar */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: T.fsXxs, color: T.light, marginBottom: 4 }}>
              <span>Plan completeness</span>
              <span data-testid="plan-completeness-pct">{completeness.fieldsSet}/4</span>
            </div>
            <div
              data-testid="plan-completeness-meter"
              style={{ width: "100%", height: 6, borderRadius: 3, background: T.border, overflow: "hidden" }}
            >
              <div style={{
                width:        `${completeness.progressPct}%`,
                height:       "100%",
                borderRadius: 3,
                background:   T.green,
              }} />
            </div>
          </div>
        </div>

        {/* Callout tiles — seats, spoken-for, people */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, padding: "14px 20px 0 20px" }}>
          <div data-testid="market-drawer-tile-seats" style={{ background: T.lightGray, borderRadius: 8, padding: "10px 10px" }}>
            <div style={{ fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: 0.4, color: T.light, marginBottom: 3 }}>Seats</div>
            <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
              {marketInstalledToday(market) != null ? marketInstalledToday(market) : "—"}<PartialMarker coverage={market.assignmentToday?.seats} measure="installed seats" />
            </div>
          </div>
          <div data-testid="market-drawer-tile-spoken-for" style={{ background: T.lightGray, borderRadius: 8, padding: "10px 10px" }}>
            <div style={{ fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: 0.4, color: T.light, marginBottom: 3 }}>Spoken for</div>
            <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
              {metrics.assignedPct != null ? `${metrics.assignedPct}%` : "—"}
            </div>
          </div>
          <div data-testid="market-drawer-tile-people" style={{ background: T.lightGray, borderRadius: 8, padding: "10px 10px" }}>
            <div style={{ fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: 0.4, color: T.light, marginBottom: 3 }}>People</div>
            <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
              {marketAssignedToday(market) != null ? marketAssignedToday(market) : "—"}<PartialMarker coverage={market.assignmentToday?.assigned} measure="assignment" />
            </div>
          </div>
        </div>

        {/* Next-lease-event / capacity-pressure note */}
        {leaseNote && (
          <div
            data-testid={`market-drawer-lease-note-${market.id}`}
            style={{
              margin:       "14px 20px 0 20px",
              display:      "flex",
              gap:          10,
              background:   leaseNote.bg,
              borderRadius: 8,
              padding:      "10px 12px",
              alignItems:   "flex-start",
            }}
          >
            <div style={{
              width: 22, height: 22, borderRadius: 5, background: leaseNote.color, color: T.white,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: T.fwBold, fontSize: T.fsXs, flexShrink: 0,
            }}>
              !
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: T.fwSemibold, fontSize: T.fsXs, color: T.dark, marginBottom: 2 }}>
                {leaseNote.title}
              </div>
              <div style={{ fontSize: T.fsXxs, color: T.mid }}>
                {leaseNote.body}
              </div>
            </div>
          </div>
        )}

        {/* Summary line */}
        <div style={{ padding: "12px 20px 0 20px", fontSize: T.fsXs, color: T.mid }}>
          {market.totalSeats > 0 ? (
            <>
              <strong style={{ color: T.dark }}>{market.totalSeats} seats</strong> across {composition.activeCount} {composition.activeCount === 1 ? "office" : "offices"}
              {marketAssignedToday(market) != null && (
                <> · <strong style={{ color: T.dark }}>{marketAssignedToday(market)}</strong> assigned<PartialMarker coverage={market.assignmentToday?.assigned} measure="assignment" /></>
              )}
            </>
          ) : (
            "No seat data yet for this market."
          )}
        </div>

        <div style={{ borderBottom: `1px solid ${T.border}`, margin: "16px 20px 0 20px" }} />

        {/* Body */}
        <div style={{ flex: 1, padding: "20px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Strategy pill selector */}
          <div>
            <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, marginBottom: 8 }}>
              Strategy
            </div>
            <div style={{ display: "flex", flexWrap: "nowrap", gap: 6 }}>
              {MARKET_PLAN_STRATEGY_OPTIONS.map((o) => {
                const meta = STRATEGY_META[o.value];
                const isSelected = draft.strategy === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`market-strategy-pill-${o.value}`}
                    disabled={!canEdit}
                    onClick={() => canEdit && setField("strategy", o.value)}
                    style={{
                      flex:         "1 1 0",
                      minWidth:     0,
                      padding:      "4px 8px",
                      borderRadius: 99,
                      fontSize:     T.fsXs,
                      fontWeight:   T.fwSemibold,
                      cursor:       canEdit ? "pointer" : "default",
                      border:       isSelected ? `2px solid ${meta.color}` : `1px solid ${T.border}`,
                      background:   isSelected ? meta.bg : T.white,
                      color:        isSelected ? meta.color : T.mid,
                      transition:   "all 0.1s",
                      whiteSpace:   "nowrap",
                      overflow:     "hidden",
                      textOverflow: "ellipsis",
                      textAlign:    "center",
                    }}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Target date */}
          <div>
            <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, display: "block", marginBottom: 6 }}>
              Target date
            </label>
            <DateInput
              data-testid="market-drawer-target-date"
              value={draft.strategyTargetDate}
              disabled={!canEdit}
              onChange={(e) => canEdit && setField("strategyTargetDate", e.target.value)}
              style={{
                padding:      "7px 10px",
                borderRadius: 6,
                border:       `1px solid ${T.border}`,
                fontSize:     T.fsBase,
                fontFamily:   "inherit",
                background:   T.white,
                cursor:       canEdit ? "pointer" : "default",
              }}
            />
          </div>

          {/* Owner picker */}
          <div>
            <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, display: "block", marginBottom: 6 }}>
              Owner
            </label>
            <select
              data-testid="market-drawer-owner-select"
              value={draft.owner}
              disabled={!canEdit}
              onChange={(e) => canEdit && setField("owner", e.target.value)}
              style={{
                width:        "100%",
                padding:      "7px 10px",
                borderRadius: 6,
                border:       `1px solid ${T.border}`,
                fontSize:     T.fsBase,
                color:        T.dark,
                background:   T.white,
                cursor:       canEdit ? "pointer" : "default",
                boxSizing:    "border-box",
              }}
            >
              <option value="">Unassigned</option>
              {ownerOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {/* Plan note */}
          <div>
            <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, display: "block", marginBottom: 6 }}>
              Plan note
            </label>
            <textarea
              data-testid="market-drawer-note"
              value={draft.planNote}
              disabled={!canEdit}
              placeholder={canEdit ? "Summarise the plan in one line…" : "No plan note."}
              onChange={(e) => canEdit && setField("planNote", e.target.value)}
              rows={4}
              style={{
                width:      "100%",
                padding:    "8px 10px",
                borderRadius: 6,
                border:     `1px solid ${T.border}`,
                fontSize:   T.fsBase,
                color:      T.dark,
                background: T.white,
                resize:     "vertical",
                fontFamily: "inherit",
                boxSizing:  "border-box",
                cursor:     canEdit ? "text" : "default",
              }}
            />
          </div>

          {/* Plan Coverage hint */}
          <div
            data-testid="market-drawer-coverage-hint"
            style={{
              fontSize:     T.fsXxs,
              color:        T.light,
              background:   T.lightGray,
              borderRadius: 6,
              padding:      "8px 10px",
              lineHeight:   1.45,
            }}
          >
            <strong style={{ color: T.mid }}>Plan Coverage tip:</strong> Portfolio Command counts this market as covered when any office below has both disposition and target date set — not from these market-level fields.
          </div>

          {/* Save error */}
          {saveError && (
            <div data-testid="market-drawer-save-error" style={{ color: T.ragRed, fontSize: T.fsXs }}>
              {saveError}
            </div>
          )}
        </div>

        {/* Footer */}
        {canEdit && (
          <div style={{
            padding:    "14px 20px",
            borderTop:  `1px solid ${T.border}`,
            display:    "flex",
            alignItems: "center",
            gap:        8,
          }}>
            <button
              type="button"
              data-testid="market-drawer-save"
              disabled={saving || !isDirty}
              onClick={handleSave}
              style={{
                padding:      "7px 18px",
                borderRadius: 6,
                border:       "none",
                background:   T.blue,
                color:        T.white,
                fontWeight:   T.fwSemibold,
                fontSize:     T.fsBase,
                cursor:       saving || !isDirty ? "default" : "pointer",
                opacity:      saving || !isDirty ? 0.65 : 1,
              }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              data-testid="market-drawer-cancel"
              onClick={onClose}
              style={{
                padding:      "7px 14px",
                borderRadius: 6,
                border:       `1px solid ${T.border}`,
                background:   T.white,
                color:        T.mid,
                fontWeight:   T.fwSemibold,
                fontSize:     T.fsBase,
                cursor:       "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── computeMarketCompleteness — 5-field completeness gauge (saved values) ────

function isFieldSet(val) {
  return val !== null && val !== undefined && val !== "";
}

/**
 * Completeness: strategy/date/owner/note + an "offices all planned" gate,
 * scored against the market's saved (server) values — the compact header is
 * read-only, so this no longer needs to track a live draft.
 */
function computeMarketCompleteness(market) {
  const completenessFields = [
    market.strategy ?? "",
    fmtDateInput(market.strategyTargetDate),
    market.owner ?? "",
    market.planNote ?? "",
  ];
  // PLACES-MARKETS-DRAWER amendment #2: four persisted market fields only;
  // the former "all offices planned" gate is dropped (Portfolio Command's
  // separate office coverage metric is untouched).
  const fieldsSet = completenessFields.filter(isFieldSet).length;
  return { fieldsSet, progressPct: Math.round((fieldsSet / 4) * 100) };
}

// ─── MARKETS-S3: Market CRUD helpers ──────────────────────────────────────────

function generateMarketSlug(name, region) {
  const prefix = (region ?? "").toLowerCase();
  const body   = (name ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 22);
  return body ? `${prefix}-${body}` : "";
}

// ─── CreateMarketModal ─────────────────────────────────────────────────────────

function CreateMarketModal({ onClose, onCreated }) {
  const [name,       setName]       = React.useState("");
  const [region,     setRegion]     = React.useState("AMER");
  const [status,     setStatus]     = React.useState("Active");
  const [submitting, setSubmitting] = React.useState(false);
  const [error,      setError]      = React.useState(null);

  const slug = React.useMemo(() => generateMarketSlug(name, region), [name, region]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required."); return; }
    if (!slug)        { setError("Name must contain at least one letter or digit."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const created = await createMarket({ id: slug, name: name.trim(), region, status });
      onCreated(created);
    } catch (err) {
      if (err?.status === 409 || err?.name === "ConflictError") {
        setError(`Market id "${slug}" already exists. Use a different name.`);
      } else {
        setError(err?.message ?? "Failed to create market.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="create-market-modal"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(16,36,58,.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add market"
        style={{
          background: T.white, borderRadius: 12, padding: "28px 32px",
          width: 420, maxWidth: "90vw", boxShadow: T.shadowFloat,
        }}
      >
        <div style={{ fontWeight: T.fwBold, fontSize: T.fsMd, color: T.dark, marginBottom: 20 }}>
          Add market
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="cm-name" style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid, marginBottom: 6 }}>
              Market name
            </label>
            <input
              id="cm-name"
              data-testid="create-market-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Bay Area"
              style={{
                width: "100%", boxSizing: "border-box", fontSize: T.fsBase,
                border: `1px solid ${T.border}`, borderRadius: 7,
                padding: "8px 10px", fontFamily: "inherit", color: T.dark,
              }}
            />
            {slug && (
              <div style={{ marginTop: 4, fontSize: T.fsXxxs, color: T.light }}>
                ID: <code>{slug}</code>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="cm-region" style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid, marginBottom: 6 }}>
                Region
              </label>
              <select
                id="cm-region"
                data-testid="create-market-region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                style={{
                  width: "100%", fontSize: T.fsBase,
                  border: `1px solid ${T.border}`, borderRadius: 7,
                  padding: "8px 10px", fontFamily: "inherit",
                  color: T.dark, background: T.white,
                }}
              >
                <option>AMER</option>
                <option>EMEA</option>
                <option>APAC</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="cm-status" style={{ display: "block", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid, marginBottom: 6 }}>
                Status
              </label>
              <select
                id="cm-status"
                data-testid="create-market-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{
                  width: "100%", fontSize: T.fsBase,
                  border: `1px solid ${T.border}`, borderRadius: 7,
                  padding: "8px 10px", fontFamily: "inherit",
                  color: T.dark, background: T.white,
                }}
              >
                <option value="Active">Active</option>
                <option value="Prospecting">Prospecting</option>
                <option value="Integrating">Integrating</option>
              </select>
            </div>
          </div>
          {error && (
            <div data-testid="create-market-error" style={{ color: T.ragRed, fontSize: T.fsXs, marginBottom: 12 }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              data-testid="create-market-cancel"
              onClick={onClose}
              disabled={submitting}
              style={{
                background: "transparent", color: T.mid,
                border: `1px solid ${T.border}`, borderRadius: 7,
                padding: "8px 16px", fontSize: T.fsBase, fontWeight: T.fwSemibold,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="create-market-submit"
              disabled={submitting}
              style={{
                background: submitting ? T.lightGray : T.blue,
                color:      submitting ? T.mid       : T.white,
                border: "none", borderRadius: 7, padding: "8px 16px",
                fontSize: T.fsBase, fontWeight: T.fwBold,
                cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit",
              }}
            >
              {submitting ? "Creating…" : "Create market"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── MoveToMarketPicker ────────────────────────────────────────────────────────

function MoveToMarketPicker({ location, allMarkets, onClose, onMoved, onShowCreate }) {
  const [selectedId, setSelectedId] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error,      setError]      = React.useState(null);

  const available = (allMarkets ?? [])
    .filter((m) => m.id !== location.marketId && m.status !== "Exited")
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

  async function handleMove() {
    if (!selectedId) { setError("Select a market."); return; }
    setSubmitting(true);
    setError(null);
    try {
      await onMoved(location.locationId, selectedId, location);
      onClose();
    } catch (err) {
      setError(err?.message ?? "Failed to move office.");
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="move-to-market-modal"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(16,36,58,.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Move office"
        style={{
          background: T.white, borderRadius: 12, padding: "28px 32px",
          width: 400, maxWidth: "90vw", maxHeight: "80vh",
          overflowY: "auto", boxShadow: T.shadowFloat,
        }}
      >
        <div style={{ fontWeight: T.fwBold, fontSize: T.fsMd, color: T.dark, marginBottom: 6 }}>
          Move to market
        </div>
        <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 18 }}>
          Moving:{" "}
          <strong style={{ color: T.dark }}>
            {location.locationAlias || location.name || location.locationId}
          </strong>
        </div>

        {available.length === 0 ? (
          <div style={{ color: T.light, fontSize: T.fsXs, marginBottom: 16, fontStyle: "italic" }}>
            No other markets available.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 16 }}>
            {available.map((m) => (
              <label
                key={m.id}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", borderRadius: 7, cursor: "pointer",
                  background: selectedId === m.id ? T.bg : "transparent",
                  border: `1px solid ${selectedId === m.id ? T.blue : "transparent"}`,
                }}
              >
                <input
                  type="radio"
                  name="target-market"
                  value={m.id}
                  checked={selectedId === m.id}
                  onChange={() => setSelectedId(m.id)}
                  data-testid={`market-option-${m.id}`}
                />
                <span>
                  <span style={{ fontWeight: T.fwSemibold, color: T.dark, fontSize: T.fsBase }}>
                    {m.name}
                  </span>
                  <span style={{ color: T.light, fontSize: T.fsXxxs, marginLeft: 6 }}>
                    {m.region}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        <button
          type="button"
          data-testid="move-create-new-market"
          onClick={onShowCreate}
          style={{
            background: "transparent", border: "none", padding: "0 0 16px",
            color: T.blue, fontSize: T.fsXs, fontWeight: T.fwSemibold,
            cursor: "pointer", fontFamily: "inherit", display: "block",
          }}
        >
          + Create new market…
        </button>

        {error && (
          <div style={{ color: T.ragRed, fontSize: T.fsXs, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            data-testid="move-to-market-cancel"
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "transparent", color: T.mid,
              border: `1px solid ${T.border}`, borderRadius: 7,
              padding: "8px 16px", fontSize: T.fsBase, fontWeight: T.fwSemibold,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="move-to-market-confirm"
            disabled={submitting || !selectedId}
            onClick={handleMove}
            style={{
              background: submitting || !selectedId ? T.lightGray : T.blue,
              color:      submitting || !selectedId ? T.mid       : T.white,
              border: "none", borderRadius: 7, padding: "8px 16px",
              fontSize: T.fsBase, fontWeight: T.fwBold,
              cursor: submitting || !selectedId ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {submitting ? "Moving…" : "Move"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DeleteMarketConfirm ───────────────────────────────────────────────────────

function DeleteMarketConfirm({ market, onClose, onConfirm }) {
  const [submitting, setSubmitting] = React.useState(false);
  const [error,      setError]      = React.useState(null);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(market);
      onClose();
    } catch (err) {
      setError(err?.message ?? "Failed to delete market.");
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="delete-market-confirm"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(16,36,58,.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Delete market"
        style={{
          background: T.white, borderRadius: 12, padding: "28px 32px",
          width: 400, maxWidth: "90vw", boxShadow: T.shadowFloat,
        }}
      >
        <div style={{ fontWeight: T.fwBold, fontSize: T.fsMd, color: T.dark, marginBottom: 12 }}>
          Delete market?
        </div>
        <div style={{ fontSize: T.fsBase, color: T.mid, marginBottom: 20 }}>
          <strong style={{ color: T.dark }}>{market.name}</strong> will be permanently removed.
          This cannot be undone.
        </div>
        {error && (
          <div style={{ color: T.ragRed, fontSize: T.fsXs, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            data-testid="delete-market-cancel"
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "transparent", color: T.mid,
              border: `1px solid ${T.border}`, borderRadius: 7,
              padding: "8px 16px", fontSize: T.fsBase, fontWeight: T.fwSemibold,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="delete-market-confirm-btn"
            disabled={submitting}
            onClick={handleConfirm}
            style={{
              background: submitting ? T.lightGray : T.ragRed,
              color:      submitting ? T.mid       : T.white,
              border: "none", borderRadius: 7, padding: "8px 16px",
              fontSize: T.fsBase, fontWeight: T.fwBold,
              cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit",
            }}
          >
            {submitting ? "Deleting…" : "Delete market"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CandidateForm — inline add / edit form ───────────────────────────────────

function CandidateForm({ draft, setField, onSave, onCancel, saving, error, isEdit = false }) {
  return (
    <div
      data-testid="candidate-form"
      style={{
        marginTop:    8,
        padding:      "12px 14px",
        background:   T.lightGray,
        borderRadius: 6,
        border:       `1px solid ${T.border}`,
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Name *</label>
          <input
            data-testid="candidate-form-name"
            type="text"
            value={draft.name}
            onChange={(e) => setField("name", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Status *</label>
          <select
            data-testid="candidate-form-status"
            value={draft.status}
            onChange={(e) => setField("status", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          >
            {CAND_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{CAND_STATUS_META[s].label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Address</label>
          <input
            data-testid="candidate-form-address"
            type="text"
            value={draft.address}
            onChange={(e) => setField("address", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Floor</label>
          <input
            data-testid="candidate-form-floor"
            type="text"
            value={draft.floor}
            onChange={(e) => setField("floor", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Visited date</label>
          <DateInput
            data-testid="candidate-form-visited-date"
            value={draft.visitedDate}
            onChange={(e) => setField("visitedDate", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Lease type</label>
          <input
            data-testid="candidate-form-lease-type"
            type="text"
            value={draft.leaseType}
            onChange={(e) => setField("leaseType", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Broker</label>
          <input
            data-testid="candidate-form-broker"
            type="text"
            value={draft.broker}
            onChange={(e) => setField("broker", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Pros (comma-separated)</label>
          <input
            data-testid="candidate-form-pros"
            type="text"
            value={draft.prosRaw}
            placeholder="Good location, Flexible terms"
            onChange={(e) => setField("prosRaw", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ fontSize: T.fsXxxs, color: T.mid, display: "block", marginBottom: 2 }}>Cons (comma-separated)</label>
          <input
            data-testid="candidate-form-cons"
            type="text"
            value={draft.consRaw}
            placeholder="Small footprint, High cost"
            onChange={(e) => setField("consRaw", e.target.value)}
            style={{ width: "100%", padding: "5px 8px", borderRadius: 4, border: `1px solid ${T.border}`, fontSize: T.fsXs, boxSizing: "border-box" }}
          />
        </div>
      </div>
      {error && (
        <div style={{ color: T.ragRed, fontSize: T.fsXs, marginBottom: 6 }}>{error}</div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          data-testid="candidate-form-save"
          onClick={onSave}
          disabled={saving}
          style={{
            padding:    "5px 16px",
            borderRadius: 6,
            border:     "none",
            background: T.blue,
            color:      T.white,
            fontWeight: T.fwSemibold,
            fontSize:   T.fsXs,
            cursor:     saving ? "default" : "pointer",
            opacity:    saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : (isEdit ? "Update" : "Add")}
        </button>
        <button
          type="button"
          data-testid="candidate-form-cancel"
          onClick={onCancel}
          style={{
            padding:    "5px 16px",
            borderRadius: 6,
            border:     `1px solid ${T.border}`,
            background: T.white,
            color:      T.dark,
            fontSize:   T.fsXs,
            cursor:     "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── CandidatesSection — prospecting candidates below the offices table ────────

export function CandidatesSection({ marketId, canEdit, onEditorState }) {
  const [candidates,   setCandidates]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [isAdding,     setIsAdding]     = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [deletingId,   setDeletingId]   = useState(null);
  const [openKebabId,  setOpenKebabId]  = useState(null);
  const [openStatusId, setOpenStatusId] = useState(null);
  const [formDraft,    setFormDraft]    = useState(CAND_EMPTY_FORM);
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState(null);
  const editorStateRef = useRef(onEditorState);
  editorStateRef.current = onEditorState;
  useEffect(() => {
    editorStateRef.current?.({ editing: Boolean(isAdding || editingId || deletingId), busy: saving });
  }, [isAdding, editingId, deletingId, saving]);
  useEffect(() => () => editorStateRef.current?.({ editing: false, busy: false }), []);
  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getCandidates(marketId);
      setCandidates(Array.isArray(rows) ? rows : []);
    } catch (e) {
      log.error("CandidatesSection: fetch failed", { marketId, err: String(e) });
    } finally {
      setLoading(false);
    }
  }, [marketId]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  function setField(f, v) {
    setFormDraft((prev) => ({ ...prev, [f]: v }));
  }

  function openAdd() {
    setFormDraft(CAND_EMPTY_FORM);
    setEditingId(null);
    setIsAdding(true);
    setOpenKebabId(null);
    setSaveError(null);
  }

  function openEdit(c) {
    setFormDraft({
      name:        c.name ?? "",
      address:     c.address ?? "",
      floor:       c.floor ?? "",
      visitedDate: c.visitedDate ?? "",
      status:      c.status ?? "under_review",
      leaseType:   c.leaseType ?? "",
      broker:      c.broker ?? "",
      prosRaw:     (c.pros ?? []).join(", "),
      consRaw:     (c.cons ?? []).join(", "),
    });
    setEditingId(c.id);
    setIsAdding(false);
    setOpenKebabId(null);
    setSaveError(null);
  }

  function cancelForm() {
    setIsAdding(false);
    setEditingId(null);
    setSaveError(null);
  }

  function buildBody() {
    const pros = formDraft.prosRaw.split(",").map((s) => s.trim()).filter(Boolean);
    const cons = formDraft.consRaw.split(",").map((s) => s.trim()).filter(Boolean);
    return {
      name:        formDraft.name.trim(),
      address:     formDraft.address.trim() || null,
      floor:       formDraft.floor.trim()   || null,
      visitedDate: formDraft.visitedDate    || null,
      status:      formDraft.status,
      leaseType:   formDraft.leaseType.trim() || null,
      broker:      formDraft.broker.trim()    || null,
      pros,
      cons,
    };
  }

  async function handleSave() {
    setSaveError(null);
    const body = buildBody();
    if (!body.name) { setSaveError("Name is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await updateCandidate(marketId, editingId, body);
      } else {
        await createCandidate(marketId, body);
      }
      await fetchCandidates();
      const savedName = body.name;
      const wasEdit   = !!editingId;
      cancelForm();
      showToast(wasEdit ? `${savedName} updated` : `${savedName} added as a candidate`);
    } catch (e) {
      setSaveError(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusChange(cand, newStatus) {
    setOpenStatusId(null);
    try {
      await updateCandidate(marketId, cand.id, { status: newStatus });
      setCandidates((prev) =>
        prev.map((c) => c.id === cand.id ? { ...c, status: newStatus } : c)
      );
    } catch (e) {
      log.error("CandidatesSection: status update failed", { err: String(e) });
    }
  }

  async function handleDeleteConfirm() {
    if (!deletingId) return;
    try {
      await deleteCandidate(marketId, deletingId);
      setCandidates((prev) => prev.filter((c) => c.id !== deletingId));
      showToast("Candidate removed");
    } catch (e) {
      log.error("CandidatesSection: delete failed", { err: String(e) });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div
      data-testid={`candidates-section-${marketId}`}
      style={{ padding: "14px 20px", borderTop: `1px solid ${T.border}` }}
    >
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div
          data-testid={`candidates-header-${marketId}`}
          style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}
        >
          Candidates
          <span style={{
            marginLeft:   6,
            fontSize:     T.fsXxs,
            fontWeight:   T.fwSemibold,
            color:        T.mid,
            background:   T.lightGray,
            padding:      "2px 6px",
            borderRadius: 8,
          }}>
            {candidates.length}
          </span>
        </div>
        {canEdit && (
          <button
            type="button"
            data-testid={`candidate-add-btn-${marketId}`}
            onClick={openAdd}
            style={{
              fontSize:   T.fsXs,
              fontWeight: T.fwSemibold,
              color:      T.blue,
              background: "transparent",
              border:     "none",
              cursor:     "pointer",
              padding:    "2px 8px",
            }}
          >
            + Add
          </button>
        )}
      </div>

      {loading && (
        <div style={{ color: T.light, fontSize: T.fsXs }}>Loading…</div>
      )}

      {!loading && candidates.length === 0 && !isAdding && (
        <div style={{ color: T.light, fontSize: T.fsXs, fontStyle: "italic" }}>
          No candidates yet.
        </div>
      )}

      {/* Candidate rows */}
      {!loading && candidates.map((c) => {
        const meta = CAND_STATUS_META[c.status] ?? CAND_STATUS_META.under_review;

        if (editingId === c.id) {
          return (
            <CandidateForm
              key={c.id}
              draft={formDraft}
              setField={setField}
              onSave={handleSave}
              onCancel={cancelForm}
              saving={saving}
              error={saveError}
              isEdit
            />
          );
        }

        return (
          <div
            key={c.id}
            data-testid={`candidate-row-${c.id}`}
            style={{
              display:      "flex",
              alignItems:   "center",
              gap:          8,
              padding:      "6px 0",
              borderBottom: `1px solid ${T.lightGray}`,
              fontSize:     T.fsXs,
              position:     "relative",
            }}
          >
            {/* Status pill */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <span
                data-testid={`candidate-status-pill-${c.id}`}
                onClick={canEdit ? () => setOpenStatusId(openStatusId === c.id ? null : c.id) : undefined}
                role={canEdit ? "button" : undefined}
                style={{
                  display:      "inline-flex",
                  alignItems:   "center",
                  padding:      "2px 10px",
                  borderRadius: 6,
                  fontSize:     T.fsXs,
                  fontWeight:   T.fwSemibold,
                  color:        meta.color,
                  background:   meta.bg,
                  cursor:       canEdit ? "pointer" : "default",
                  whiteSpace:   "nowrap",
                  userSelect:   "none",
                }}
              >
                {meta.label}
              </span>
              {canEdit && openStatusId === c.id && (
                <div
                  data-testid={`candidate-status-dropdown-${c.id}`}
                  style={{
                    position:     "absolute",
                    top:          "100%",
                    left:         0,
                    zIndex:       200,
                    background:   T.white,
                    border:       `1px solid ${T.border}`,
                    borderRadius: 6,
                    boxShadow:    "0 2px 8px rgba(0,0,0,0.12)",
                    minWidth:     130,
                    padding:      "4px 0",
                  }}
                >
                  {CAND_STATUS_ORDER.map((s) => {
                    const sm = CAND_STATUS_META[s];
                    return (
                      <div
                        key={s}
                        data-testid={`candidate-status-option-${c.id}-${s}`}
                        onClick={() => handleStatusChange(c, s)}
                        style={{
                          padding:    "6px 12px",
                          cursor:     "pointer",
                          fontSize:   T.fsXs,
                          fontWeight: T.fwSemibold,
                          color:      sm.color,
                          background: c.status === s ? sm.bg : "transparent",
                        }}
                      >
                        {sm.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Name */}
            <span style={{
              flex:         1,
              fontWeight:   T.fwSemibold,
              color:        T.dark,
              minWidth:     0,
              overflow:     "hidden",
              textOverflow: "ellipsis",
              whiteSpace:   "nowrap",
            }}>
              {c.name}
            </span>

            {/* Address · Floor */}
            <span style={{ color: T.mid, whiteSpace: "nowrap", flexShrink: 0 }}>
              {[c.address, c.floor].filter(Boolean).join(" · ") || "—"}
            </span>

            {/* Broker */}
            <span style={{ color: T.mid, whiteSpace: "nowrap", flexShrink: 0 }}>
              {c.broker || "—"}
            </span>

            {/* Visited date */}
            <span style={{ color: T.mid, whiteSpace: "nowrap", flexShrink: 0 }}>
              {c.visitedDate ? fmtDateShort(c.visitedDate) : "—"}
            </span>

            {/* ⋯ menu — editor+ only */}
            {canEdit && (
              <div style={{ position: "relative", flexShrink: 0 }}>
                <button
                  type="button"
                  data-testid={`candidate-kebab-${c.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenKebabId(openKebabId === c.id ? null : c.id);
                    setOpenStatusId(null);
                  }}
                  style={{
                    background: "transparent",
                    border:     "none",
                    cursor:     "pointer",
                    padding:    "2px 6px",
                    fontSize:   T.fsBase,
                    color:      T.mid,
                    lineHeight: 1,
                  }}
                >
                  ⋯
                </button>
                {openKebabId === c.id && (
                  <div style={{
                    position:     "absolute",
                    top:          "100%",
                    right:        0,
                    zIndex:       200,
                    background:   T.white,
                    border:       `1px solid ${T.border}`,
                    borderRadius: 6,
                    boxShadow:    "0 2px 8px rgba(0,0,0,0.12)",
                    minWidth:     100,
                    padding:      "4px 0",
                  }}>
                    <div
                      data-testid={`candidate-kebab-edit-${c.id}`}
                      onClick={() => openEdit(c)}
                      style={{ padding: "6px 14px", cursor: "pointer", fontSize: T.fsXs, color: T.dark }}
                    >
                      Edit
                    </div>
                    <div
                      data-testid={`candidate-kebab-delete-${c.id}`}
                      onClick={() => { setDeletingId(c.id); setOpenKebabId(null); }}
                      style={{ padding: "6px 14px", cursor: "pointer", fontSize: T.fsXs, color: T.ragRed }}
                    >
                      Delete
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Inline add form */}
      {isAdding && (
        <CandidateForm
          draft={formDraft}
          setField={setField}
          onSave={handleSave}
          onCancel={cancelForm}
          saving={saving}
          error={saveError}
        />
      )}

      {/* Delete confirm dialog */}
      {deletingId && (
        <div
          data-testid={`candidate-delete-confirm-${deletingId}`}
          style={{
            marginTop:    8,
            padding:      "10px 14px",
            background:   T.ragRedBg,
            border:       `1px solid ${T.ragRed}`,
            borderRadius: 6,
            fontSize:     T.fsXs,
            color:        T.dark,
          }}
        >
          Delete this candidate? This moves it to Recently deleted. You can restore it for 30 days from Admin → Recently deleted.
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              type="button"
              data-testid="candidate-delete-confirm-ok"
              onClick={handleDeleteConfirm}
              style={{
                padding:      "4px 14px",
                borderRadius: 6,
                border:       "none",
                background:   T.ragRed,
                color:        T.white,
                fontWeight:   T.fwSemibold,
                fontSize:     T.fsXs,
                cursor:       "pointer",
              }}
            >
              Delete
            </button>
            <button
              type="button"
              data-testid="candidate-delete-confirm-cancel"
              onClick={() => setDeletingId(null)}
              style={{
                padding:      "4px 14px",
                borderRadius: 6,
                border:       `1px solid ${T.border}`,
                background:   T.white,
                color:        T.dark,
                fontSize:     T.fsXs,
                cursor:       "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── OfficesTable — left panel in the accordion ───────────────────────────────

function OfficeRowThumbnail({ loc }) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [loc.imageUrl]);

  const placeholder = (
    <div
      data-testid={`office-thumb-placeholder-${loc.locationId}`}
      style={{
        width: 28, height: 28, borderRadius: 6, flexShrink: 0,
        background: T.photoPlaceholder,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <span style={{ fontSize: T.fsXxxs, color: T.mid, fontWeight: T.fwBold }}>
        {(loc.locationAlias || loc.name || loc.locationId || "??").slice(0, 2).toUpperCase()}
      </span>
    </div>
  );

  if (!loc.imageUrl || imageFailed) return placeholder;

  return (
    <img
      src={loc.imageUrl}
      alt=""
      loading="lazy"
      data-testid={`office-thumb-${loc.locationId}`}
      onError={() => setImageFailed(true)}
      style={{
        width: 28, height: 28, borderRadius: 6, objectFit: "cover", flexShrink: 0,
        display: "block", background: T.photoPlaceholder,
      }}
    />
  );
}

function MarketOfficesListOwner({ locations, onOpenLocation, canEdit = false, onMoveOffice, currentMarketId, allMarkets, onOpenOfficeDrawer, openOfficeId, onCloseOffice, onOfficeSaved, recordMode = false, onOfficeBusy, onNavigateRelated }) {
  const [moveMenuId, setMoveMenuId] = useState(null);
  // Browse Draft, but don't count its scaffolding in the effective footer.
  // Vacated retains its existing membership and count semantics.
  const active = locations.filter(isMarketMember);
  const effectiveOfficeCount = active.filter(isCountedMarketOffice).length;

  // Seats/headcount/util/headroom are a capacity concern: a 'vacated' office
  // holds no seats, so it's excluded from these sums even though it's still
  // listed (and counted) above.
  const operational = active.filter(isOperationallyActive);
  const assignmentOffices = active.filter(l => ["operational", "dormant"].includes(l.stage));
  const assignedCoverage = knownTotal(assignmentOffices, officeAssignedToday);
  const seatsCoverage = knownTotal(assignmentOffices, officeSeatsToday);
  const totalSeats = seatsCoverage.value;
  const totalHeadcount = assignedCoverage.value;
  const utilPct = totalHeadcount != null && totalSeats > 0 ? Math.min(100, Math.round(totalHeadcount / totalSeats * 100)) : null;
  const officesWithHeadroom = operational.filter((l) => l.headroom != null);
  const totalHeadroom = officesWithHeadroom.length > 0
    ? officesWithHeadroom.reduce((s, l) => s + l.headroom, 0)
    : null;

  return (
    <div style={{ padding: "16px 20px", flex: 1 }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsXs }}>
          <thead>
            <tr>
              {[
                "Office", "Office Stage", "Lease", "Plan", "People / Seats", "Expansion", "Space Fit", "Planned Seats", "Next Lease Event",
                ...(canEdit && onMoveOffice ? [""] : []),
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign:     h === "Office" ? "left" : "center",
                    fontSize:      T.fsXxs,
                    textTransform: "uppercase",
                    letterSpacing: ".07em",
                    color:         "var(--v2-mid)",
                    fontWeight:    T.fwBold,
                    background:    "var(--v2-tint)",
                    padding:       "10px 14px",
                    borderBottom:  "1px solid var(--v2-line)",
                    whiteSpace:    "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {active.map((loc) => {
              const isInBuild = loc.stage === "build";
              const locDisp = loc.disposition;
              return (
                <React.Fragment key={loc.locationId}>
                  <tr
                    data-testid={`office-row-${loc.locationId}`}
                    data-office-open={openOfficeId === loc.locationId ? "true" : undefined}
                    onClick={() => onOpenOfficeDrawer(loc)}
                    style={{
                      borderBottom: `1px solid ${T.lightGray}`,
                      cursor: "pointer",
                      background: openOfficeId === loc.locationId ? T.blueSoft : "transparent",
                    }}
                  >
                  {/* Office name + link — stopPropagation so row click doesn't also open drawer */}
                  <td style={{ padding: "8px 12px 8px 0", fontWeight: T.fwSemibold, color: "var(--v2-dark)", whiteSpace: "nowrap" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <OfficeRowThumbnail loc={loc} />
                      <span>
                        <InternalLink
                          href={buildLocationUrl(loc.locationId)}
                          data-testid={`office-link-${loc.locationId}`}
                          style={{ color: T.blue, textDecoration: "none", fontSize: T.fsBase }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenLocation && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                              e.preventDefault();
                              onOpenLocation(loc.locationId);
                            }
                          }}
                        >
                          {loc.locationAlias || loc.name || loc.locationId}
                        </InternalLink>
                      </span>
                    </div>
                  </td>

                  {/* Office Stage — reads STAGES[loc.stage] so every stage (Operational,
                      Prospecting, On-hold, etc.) renders with its canonical label and
                      color tokens, matching the Office Overview page pill exactly. */}
                  <td style={{ padding: "8px 12px 8px 0", whiteSpace: "nowrap", textAlign: "center" }}>
                    {(() => {
                      const stageMeta = STAGES[loc.stage];
                      if (!stageMeta) return <span style={{ color: T.light }}>—</span>;
                      return (
                        <PlanPill
                          testId={`office-stage-${loc.locationId}`}
                          label={stageMeta.label}
                          color={stageMeta.color}
                          bg={stageMeta.bg}
                        />
                      );
                    })()}
                  </td>

                  {/* Lease — reads LEASE_STATUS_META via planLeaseStatusBucket() so the
                      label and colors match the Lease detail page pill exactly. Renders
                      an em-dash when there is no active lease for this office. */}
                  <td style={{ padding: "8px 12px 8px 0", whiteSpace: "nowrap", textAlign: "center" }}>
                    {(() => {
                      const bucket = planLeaseStatusBucket(loc.leaseStatus, loc.stage);
                      const lsMeta = bucket ? LEASE_STATUS_META[bucket] : null;
                      if (!lsMeta) return <span style={{ color: T.light }}>—</span>;
                      return (
                        <PlanPill
                          testId={`lease-status-${loc.locationId}`}
                          label={lsMeta.label}
                          color={lsMeta.color}
                          bg={lsMeta.bg}
                        />
                      );
                    })()}
                  </td>

                  {/* Plan — build stage indicator for in-build offices; disposition
                      pill (or "Set plan…" link) for operational offices. Always
                      opens the office drawer as the click target. */}
                  <td style={{ padding: "8px 12px 8px 0", whiteSpace: "nowrap", textAlign: "center" }}>
                    {recordMode ? (
                      <button type="button" data-testid={`plan-cell-${loc.locationId}`}
                        onClick={(e) => { e.stopPropagation(); onOpenOfficeDrawer(loc); }}
                        aria-label={`${canEdit ? "Edit" : "View"} plan for ${loc.locationAlias || loc.locationId}`}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
                        {locDisp ? <DispositionBadge disposition={locDisp} /> : <span style={{ color: T.ragAmberText }}>Set plan</span>}
                        {loc.targetActionDate && <div style={{ color: T.mid, fontSize: T.fsXxs, marginTop: 4 }}>{fmtDateShort(loc.targetActionDate)}</div>}
                      </button>
                    ) : isInBuild ? (
                      <button
                        type="button"
                        data-testid={`plan-cell-${loc.locationId}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenOfficeDrawer(loc);
                        }}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}
                      >
                        <StageBadge stage={loc.stage} />
                      </button>
                    ) : locDisp ? (
                      <button
                        type="button"
                        data-testid={`plan-cell-${loc.locationId}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenOfficeDrawer(loc);
                        }}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}
                      >
                        <DispositionBadge disposition={locDisp} />
                      </button>
                    ) : (
                      <a
                        href="#"
                        data-testid={`plan-cell-${loc.locationId}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOpenOfficeDrawer(loc);
                        }}
                        style={{ fontSize: T.fsXs, color: T.blue, textDecoration: "none", fontWeight: T.fwSemibold }}
                      >
                        Set plan…
                      </a>
                    )}
                  </td>

                  {/* People / Seats */}
                  <td style={{ padding: "8px 12px 8px 0", whiteSpace: "nowrap", textAlign: "center" }}>
                    <PeopleSeatsValue loc={{ ...loc, headcount: officeAssignedToday(loc), installedCapacity: officeSeatsToday(loc) }} />
                  </td>

                  {/* Headroom (max_capacity − installed_capacity) */}
                  <td style={{ padding: "8px 12px 8px 0", whiteSpace: "nowrap", textAlign: "center" }} data-testid={`office-headroom-${loc.locationId}`}>
                    <HeadroomValue loc={loc} center={true} />
                  </td>

                  {/* Space Fit — canonical verdict via computeSpaceFit() (same as Offices list) */}
                  <td style={{ padding: "8px 12px 8px 0", textAlign: "center" }}>
                    <PlanSpaceFitPill loc={loc} />
                  </td>

                  {/* Planned Seats — running-tally stacked rows, soonest first. */}
                  <td
                    style={{ padding: "8px 12px 8px 0", whiteSpace: "nowrap", textAlign: "center" }}
                    data-testid={`office-planned-${loc.locationId}`}
                  >
                    <PlannedValue loc={loc} />
                  </td>

                  {/* Next Lease Event */}
                  <td style={{ padding: "8px 12px 8px 0", whiteSpace: "nowrap", textAlign: "center" }}>
                    <NextLeaseEventValue loc={loc} />
                  </td>

                  {/* Move action */}
                  {canEdit && onMoveOffice && (
                    <td style={{ padding: "8px 0 8px 4px", width: 36, whiteSpace: "nowrap", position: "relative" }}>
                      <button
                        type="button"
                        data-testid={`office-move-btn-${loc.locationId}`}
                        aria-label={recordMode ? `Actions for ${loc.locationAlias || loc.locationId}` : "Move to market"}
                        aria-haspopup={recordMode ? "menu" : undefined}
                        aria-expanded={recordMode ? moveMenuId === loc.locationId : undefined}
                        title="Move to market…"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (recordMode) { setMoveMenuId((id) => id === loc.locationId ? null : loc.locationId); return; }
                          onMoveOffice({ ...loc, marketId: currentMarketId });
                        }}
                        style={{
                          background: "transparent", border: "none",
                          borderRadius: 5, padding: "2px 6px",
                          fontSize: T.fsXs, color: T.mid,
                          cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        ⋯
                      </button>
                      {recordMode && moveMenuId === loc.locationId && (
                        <div role="menu" style={{ position: "absolute", right: 0, top: "100%", zIndex: 10, background: T.card, border: `1px solid ${T.border}`, borderRadius: 7, padding: 4 }}>
                          <button type="button" role="menuitem" data-testid={`office-move-menu-${loc.locationId}`}
                            onClick={(e) => { e.stopPropagation(); setMoveMenuId(null); onMoveOffice({ ...loc, marketId: currentMarketId }); }}
                            style={{ border: 0, background: "transparent", color: T.dark, padding: "6px 10px", whiteSpace: "nowrap", cursor: "pointer" }}>
                            Move to market
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                  </tr>
                  {openOfficeId === loc.locationId && (
                    <tr data-testid={`office-plan-row-${loc.locationId}`}>
                      <td colSpan={canEdit && onMoveOffice ? 10 : 9} style={{ padding: 0, background: T.blueSoft }}>
                        <OfficeScopeDrawer
                          loc={loc}
                          canEdit={canEdit}
                          onBusyChange={onOfficeBusy}
                          onNavigateRelated={onNavigateRelated}
                          onClose={onCloseOffice}
                          onSaved={onOfficeSaved}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {active.length === 0 && (
              <tr>
                <td colSpan={canEdit && onMoveOffice ? 10 : 9} style={{ padding: "12px 0", color: T.light, fontStyle: "italic" }}>
                  No offices in this market
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Capacity outlook one-liner */}
      {!recordMode && active.length > 0 && (
        <div style={{
          marginTop:  12,
          padding:    "8px 10px",
          background: T.planSummaryChipBg,
          border:     `1px solid ${T.planSummaryChipBorder}`,
          borderRadius: 6,
          fontSize:   T.fsXs,
          color:      T.mid,
        }}>
          {totalSeats != null ? (
            <>
              <strong style={{ color: T.dark }}>{totalSeats} seats</strong><PartialMarker coverage={seatsCoverage} measure="installed seats" /> across {effectiveOfficeCount} {effectiveOfficeCount === 1 ? "office" : "offices"}<PartialMarker coverage={assignedCoverage} measure="assignment" />
              {utilPct !== null && (
                <>, <strong style={{ color: utilPct >= 85 ? T.ragAmber : T.dark }}>{utilPct}% spoken for</strong></>
              )}
              {totalHeadroom !== null && (
                <>, <strong data-testid="offices-total-headroom" style={{ color: totalHeadroom < 0 ? T.ragRed : totalHeadroom > 0 ? T.greenDeep : T.dark }}>
                  {totalHeadroom > 0 ? `+${totalHeadroom}` : totalHeadroom < 0 ? `\u2212${Math.abs(totalHeadroom)}` : "0"} expansion
                </strong></>
              )}
              {totalHeadcount != null && totalSeats > 0 && totalHeadcount > totalSeats && (
                <> — <span style={{ color: T.ragRed, fontWeight: T.fwBold }}>over capacity</span></>
              )}
              {(() => {
                const withPlanned = active.filter((l) => l.stage !== "draft" && l.plannedSeats > 0);
                if (withPlanned.length === 0) return null;
                const totalPlanned = withPlanned.reduce((s, l) => s + l.plannedSeats, 0);
                const hint = withPlanned.length === 1
                  ? `${withPlanned[0].locationAlias || withPlanned[0].name || withPlanned[0].locationId}${fmtMonthYear(withPlanned[0].onlineDate) ? `, live ${fmtMonthYear(withPlanned[0].onlineDate)}` : ""}`
                  : `${withPlanned.length} offices`;
                return (
                  <> · <span data-testid="offices-planned-hint" style={{ color: T.mid }}>+{totalPlanned.toLocaleString()} planned ({hint})</span></>
                );
              })()}
            </>
          ) : (
            <>
              {effectiveOfficeCount} {effectiveOfficeCount === 1 ? "office" : "offices"} - {effectiveOfficeCount === 0 ? "Draft scaffolding is not operational capacity" : "no capacity data on file"}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PeopleSeatsCell / HeadroomCell / AnnualCostCell / NextDecisionCell ──────
// MARKET-PLAN-LEADERSHIP-VIEW-S1 — leadership-view table cells. Each degrades
// gracefully to "—" / "No capacity set" / "No headcount" rather than
// fabricating a number when the underlying market field is null.

// CAPACITY-DEFINITION-FIX: avgUtilPct prop removed. pct is now always the direct
// ratio totalHeadcount / totalSeats, where totalSeats already contains only usable
// stages (operational + dormant) from the server's GET /api/plan response.
// Red threshold changed to STRICTLY > 100 (was >= 100); 100% is no longer flagged.
export function MarketCapacityCell({ summary }) {
  const status = summary?.status ?? "loading";
  if (status === "loading") return <span data-testid="market-current-capacity" aria-busy="true" style={{ color: T.mid }}>Loading…</span>;
  if (status !== "ok") return <span data-testid="market-current-capacity" title={status === "error" ? summary.error : "This market is not in the Capacity model."} style={{ color: T.mid }}>
    {status === "error" ? "Read failed" : "Not in Capacity"}
  </span>;
  return <span data-testid="market-current-capacity" style={{ ...LIST_PRESENTATION.numeric, fontVariantNumeric: "tabular-nums" }}>
    {summary.people ?? "–"} / {summary.installed ?? "–"}
  </span>;
}

export function MarketAssignmentCell({ summary }) {
  if (!summary || summary.status === "loading") return <span data-testid="market-current-capacity" aria-busy="true">Loading…</span>;
  if (summary.status === "error") return <span data-testid="market-current-capacity" role="status" title={summary.error}>Read failed</span>;
  if (summary.status === "excluded") return <span data-testid="market-current-capacity" title="No nonarchived operational or dormant offices in the Offices default scope.">No eligible offices</span>;
  return <span data-testid="market-current-capacity" style={LIST_PRESENTATION.numeric}>
    {summary.assigned.value ?? "–"}<PartialMarker coverage={summary.assigned} measure="assignment" />
    {" / "}{summary.seats.value ?? "–"}<PartialMarker coverage={summary.seats} measure="installed seats" />
  </span>;
}

function PeopleSeatsCell({ totalHeadcount, totalSeats, isCompact = false }) {
  const pct =
    totalHeadcount != null && totalSeats != null && totalSeats > 0
      ? Math.round(totalHeadcount / totalSeats * 100)
      : null;

  if (pct === null) {
    if (totalSeats == null || totalSeats === 0) {
      return <span data-testid="seats-no-capacity" style={{ color: "var(--v2-light)", fontSize: T.fsXs }}>No capacity set</span>;
    }
    return <span data-testid="seats-no-headcount" style={{ color: "var(--v2-light)", fontSize: T.fsXs }}>No headcount</span>;
  }

  const pctColor = pct > 100 ? T.ragRed : pct >= 90 ? T.ragAmber : "var(--v2-green)";
  const countText = `${totalHeadcount ?? "—"}${totalSeats != null ? ` / ${totalSeats}` : ""}`;

  return (
    <div style={{ display: "inline-grid", gridTemplateColumns: "1fr auto 1fr", width: "100%", alignItems: "center" }}>
      <span data-testid="people-seats-count" style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: "var(--v2-mid)", whiteSpace: "nowrap", textAlign: "right" }}>
        {countText}
      </span>
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: "var(--v2-mid)", padding: "0 3px" }}>{"\u00b7"}</span>
      <span data-testid="seats-bar-pct" style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: pctColor, textAlign: "left" }}>
        {pct}%
      </span>
    </div>
  );
}

function HeadroomCell({ totalHeadroom }) {
  if (totalHeadroom == null) {
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <span data-testid="headroom-value" style={{ color: "var(--v2-light)", fontSize: T.fsXs, display: "inline-block", minWidth: "3.5em", textAlign: "right" }}>—</span>
      </div>
    );
  }
  const color = totalHeadroom > 0 ? T.greenDeep : totalHeadroom < 0 ? T.ragRed : "var(--v2-mid)";
  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <span data-testid="headroom-value" style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color, display: "inline-block", minWidth: "3.5em", textAlign: "right" }}>
        {totalHeadroom > 0 ? `+${totalHeadroom}` : totalHeadroom}
      </span>
    </div>
  );
}

export function AnnualCostCell({ totalAnnualCost, rentIncompleteOffices = [], isCompact = false }) {
  const hasIncomplete = rentIncompleteOffices.length > 0;
  const dagTooltip = hasIncomplete
    ? rentIncompleteOffices.map((o) =>
        `Excludes ${o.locationAlias ?? o.locationId} (${o.stage}) — no rent on file`
      ).join("; ")
    : "";

  // When no cost is available at all but there are rent gaps, render "— †" so
  // the dagger and sub-line still surface rather than being silently hidden.
  const valueLabel = totalAnnualCost != null
    ? `$${Math.round(totalAnnualCost).toLocaleString()}`
    : "—";
  const valueColor = totalAnnualCost != null ? "var(--v2-mid)" : "var(--v2-light)";

  return (
    <div data-testid="annual-cost-value" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "100%" }}>
      <div style={{ display: "inline-flex", alignItems: "baseline", gap: 2 }}>
        <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: valueColor, whiteSpace: "nowrap" }}>
          {valueLabel}
        </span>
        {hasIncomplete && (
          <span
            data-testid="annual-cost-dagger"
            title={dagTooltip}
            style={{ color: T.ragAmber, fontWeight: T.fwBold, cursor: "help", fontSize: T.fsXs, marginLeft: 2 }}
          >
            †
          </span>
        )}
      </div>
      {hasIncomplete && !isCompact && (
        <div
          data-testid="annual-cost-incomplete"
          style={{ color: T.ragAmber, fontSize: T.fsXs, fontWeight: T.fwSemibold, marginTop: 1 }}
        >
          incomplete · {rentIncompleteOffices.length} office{rentIncompleteOffices.length === 1 ? "" : "s"} missing rent
        </div>
      )}
    </div>
  );
}

// ~6 months / ~18 months in days, matching NextLeaseEvent's month-based
// thresholds (6/18 mo) so the two "next decision" surfaces (Plan table +
// Leases action view) agree on what counts as "soon" vs. "on the horizon".
const NEXT_DECISION_RED_DAYS   = 182;
const NEXT_DECISION_AMBER_DAYS = 548;

// Threshold below which the countdown turns red/bold (≤ 90 d → urgent),
// matching Leases' "decide-now" bucket so the two surfaces agree on urgency.
const NEXT_DECISION_DECIDE_NOW_DAYS = 90;

/** Short countdown like "45d" or "6 mo", mirroring the office-row countdown. */
function decisionCountdownText(days) {
  if (days <= 30) return `${days}d`;
  const mos = Math.round(days / 30.44);
  return mos <= 1 ? "1 mo" : `${mos} mo`;
}

export function NextDecisionCell({ market, isCompact = false }) {
  const decision = deriveMarketNextDecision(market);
  if (!decision) {
    return <span data-testid="next-decision-value" style={{ color: "var(--v2-light)", fontSize: T.fsXs }}>—</span>;
  }
  const isDecideNow = decision.days <= NEXT_DECISION_DECIDE_NOW_DAYS;
  const color = decision.days <= NEXT_DECISION_RED_DAYS
    ? T.red
    : decision.days <= NEXT_DECISION_AMBER_DAYS
      ? T.pendingAmber
      : undefined;
  // Compact: keep just date + countdown on one line (no type sub-label).
  const countdownColor = isCompact ? (color ?? "var(--v2-light)") : (isDecideNow ? T.red : color ?? "var(--v2-light)");
  if (isCompact) {
    return (
      <div
        data-testid="next-decision-value"
        style={{ display: "inline-grid", gridTemplateColumns: "1fr auto", width: "100%", alignItems: "baseline", gap: "0 4px" }}
      >
        <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: "var(--v2-mid)", whiteSpace: "nowrap", textAlign: "right" }}>
          {fmtDateShort(decision.date)}
        </span>
        <span
          data-testid="next-decision-countdown"
          style={{
            fontSize:   T.fsXxs,
            fontWeight: color ? T.fwSemibold : T.fwRegular,
            color:      countdownColor,
            whiteSpace: "nowrap",
            textAlign:  "left",
          }}
        >
          {decisionCountdownText(decision.days)}
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="next-decision-value"
      style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0, width: "100%", maxWidth: "100%" }}
    >
      <div style={{ display: "inline-grid", gridTemplateColumns: "1fr auto", width: "100%", alignItems: "baseline", gap: "0 4px" }}>
        <span style={{ fontSize: T.fsBase, fontWeight: T.fwRegular, color: "var(--v2-mid)", whiteSpace: "nowrap", textAlign: "right" }}>
          {fmtDateShort(decision.date)}
        </span>
        <span
          data-testid="next-decision-countdown"
          style={{
            fontSize:   T.fsXxs,
            fontWeight: isDecideNow ? T.fwBold : color ? T.fwSemibold : T.fwRegular,
            color:      countdownColor,
            whiteSpace: "nowrap",
            textAlign:  "left",
          }}
        >
          {decisionCountdownText(decision.days)}
        </span>
      </div>
      {/* Type/label sub-line */}
      <span
        style={{
          fontSize:    T.fsXxs,
          color:       color ?? "var(--v2-light)",
          fontWeight:  color ? T.fwSemibold : T.fwRegular,
          whiteSpace:  "nowrap",
          overflow:    "hidden",
          textOverflow: "ellipsis",
          maxWidth:    "100%",
          textAlign:   "center",
        }}
      >
        {decision.label}
      </span>
    </div>
  );
}

// ─── PlanMatrixRow — one row of the matrix + inline accordion ─────────────────

// ─── MarketHeader — slim action row above the offices table ──────────────────
// All identity/read-mostly detail (thumbnail, strategy pill, completeness
// meter, callouts, next-lease alert) now lives in the market drawer. This row
// is just the section label plus the two entry points into that drawer/menu:
// "Edit plan" button and the ⋯ More menu (Deactivate / Delete — admin-gated).

// ─── MarketDirectivesStrip ────────────────────────────────────────────────────
// Compact inline strip showing active strategic directives for a market inside
// the expanded accordion panel. Only in_review or approved directives are shown.
// Clicking a chip opens DirectiveDrawer for that directive.

const ACTIVE_DIRECTIVE_STATUSES = new Set(["in_review", "approved"]);

const DIRECTIVE_TYPE_DOT_COLOR = {
  hub_designation:      T.teal,
  exit:                 T.ragRed,
  new_market:           T.blue,
  relocate_upgrade:     T.purple,
  capability_migration: T.amberDark,
};

const DIRECTIVE_STATUS_CHIP_STYLE = {
  in_review: { color: T.mid,           bg: T.lightGray },
  approved:  { color: T.utilGreenText, bg: T.utilGreen  },
};

function MarketDirectivesStrip({ directives, onOpenDirective }) {
  const active = (directives ?? []).filter((d) => ACTIVE_DIRECTIVE_STATUSES.has(d.status));
  if (active.length === 0) return null;

  return (
    <div
      data-testid="market-directives-strip"
      style={{
        padding:      "10px 20px",
        borderBottom: `1px solid ${T.planExpandDivider}`,
        display:      "flex",
        flexWrap:     "wrap",
        alignItems:   "center",
        gap:          6,
      }}
    >
      <span style={{
        fontSize:      T.fsXxs,
        fontWeight:    T.fwBold,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color:         T.teal,
        marginRight:   4,
        flexShrink:    0,
      }}>
        ◆ Directives
      </span>
      {active.map((d) => {
        const dotColor    = DIRECTIVE_TYPE_DOT_COLOR[d.type] ?? T.mid;
        const statusStyle = DIRECTIVE_STATUS_CHIP_STYLE[d.status] ?? { color: T.mid, bg: T.lightGray };
        return (
          <button
            key={d.id}
            type="button"
            data-testid={`market-directive-chip-${d.id}`}
            onClick={() => onOpenDirective?.(d)}
            title={`${DIRECTIVE_TYPE_LABELS[d.type] ?? d.type} · ${DIRECTIVE_STATUS_LABELS[d.status] ?? d.status}`}
            style={{
              display:      "inline-flex",
              alignItems:   "center",
              gap:          5,
              background:   T.white,
              border:       `1px solid ${T.border}`,
              borderRadius: 99,
              padding:      "3px 10px 3px 6px",
              cursor:       "pointer",
              fontFamily:   "inherit",
            }}
          >
            <span style={{
              display:      "inline-block",
              width:        8,
              height:       8,
              borderRadius: "50%",
              background:   dotColor,
              flexShrink:   0,
            }} />
            <span style={{
              fontSize:     T.fsXs,
              fontWeight:   T.fwSemibold,
              color:        T.dark,
              maxWidth:     200,
              overflow:     "hidden",
              textOverflow: "ellipsis",
              whiteSpace:   "nowrap",
            }}>
              {d.title}
            </span>
            <span style={{
              display:      "inline-flex",
              alignItems:   "center",
              fontSize:     T.fsXs,
              fontWeight:   T.fwSemibold,
              borderRadius: 6,
              padding:      "2px 8px",
              background:   statusStyle.bg,
              color:        statusStyle.color,
              flexShrink:   0,
              whiteSpace:   "nowrap",
            }}>
              {DIRECTIVE_STATUS_LABELS[d.status] ?? d.status}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function MarketHeader({ market, canEdit, canManageLifecycle, onEditPlan, onSaved, onDeleteMarket, onStartWorkflow, onOpenLinkedRun, title = "Offices in this market", hideEditPlan = false }) {
  // PLACES-MARKETS-DRAWER: lifecycle (deactivate/reactivate/delete) is
  // admin-only on the server (server/routes/markets.js:632-671). Drawer
  // callers pass canManageLifecycle explicitly; legacy unrouted callers keep
  // their previous canEdit behaviour.
  const showLifecycle = canManageLifecycle ?? canEdit;
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [menuError,    setMenuError]    = useState(null);
  const isInactive = isMarketExited(market);

  return (
    <div style={{ padding: "14px 20px 0 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, display: "flex", alignItems: "center", gap: 10 }}>
          {title}
          <LinkedWorkflowRunsIndicator sourceType="market" sourceId={market.id} onOpenRun={onOpenLinkedRun} />
        </div>

        {/* Actions — Edit plan + ⋯ More */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {canEdit && !hideEditPlan && (
            <button
              type="button"
              data-testid={`edit-plan-btn-${market.id}`}
              onClick={onEditPlan}
              style={{
                background:   T.blue,
                color:        T.white,
                border:       "none",
                borderRadius: 7,
                padding:      "7px 16px",
                fontSize:     T.fsSm,
                fontWeight:   T.fwBold,
                cursor:       "pointer",
                fontFamily:   "inherit",
                whiteSpace:   "nowrap",
              }}
            >
              Edit plan
            </button>
          )}
          {canEdit && onStartWorkflow && (
            <button
              type="button"
              data-testid={`start-workflow-btn-${market.id}`}
              onClick={() => onStartWorkflow({
                type: "market",
                id: market.id,
                label: market.market || market.id,
              })}
              style={{
                background:   "transparent",
                color:        T.blue,
                border:       `1px solid ${T.border}`,
                borderRadius: 7,
                padding:      "7px 16px",
                fontSize:     T.fsSm,
                fontWeight:   T.fwBold,
                cursor:       "pointer",
                fontFamily:   "inherit",
                whiteSpace:   "nowrap",
              }}
            >
              Start action
            </button>
          )}
          {showLifecycle && (
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                type="button"
                data-testid="plan-more-btn"
                onClick={() => setMoreMenuOpen((v) => !v)}
                style={{
                  background:   "transparent",
                  border:       `1px solid ${T.border}`,
                  borderRadius: 6,
                  padding:      "6px 10px",
                  fontSize:     T.fsXs,
                  fontWeight:   T.fwSemibold,
                  color:        T.mid,
                  cursor:       "pointer",
                  fontFamily:   "inherit",
                  letterSpacing: 0.5,
                }}
              >
                ⋯
              </button>
              {moreMenuOpen && (
                <div
                  data-testid="plan-more-menu"
                  style={{
                    position:     "absolute",
                    top:          "calc(100% + 4px)",
                    right:        0,
                    background:   T.white,
                    border:       `1px solid ${T.border}`,
                    borderRadius: 8,
                    boxShadow:    "0 4px 16px rgba(16,36,58,.14)",
                    minWidth:     160,
                    zIndex:       50,
                    overflow:     "hidden",
                  }}
                >
                  {isInactive ? (
                    <button
                      type="button"
                      data-testid="plan-reactivate-market-btn"
                      disabled={reactivating}
                      onClick={async () => {
                        setMoreMenuOpen(false);
                        setReactivating(true);
                        try {
                          const updated = await setMarketActive(market.id, true);
                          onSaved(market.id, updated, {});
                        } catch (err) {
                          setMenuError(err?.message ?? "Failed to reactivate market");
                        } finally {
                          setReactivating(false);
                        }
                      }}
                      style={{
                        display:    "block",
                        width:      "100%",
                        background: "transparent",
                        border:     "none",
                        textAlign:  "left",
                        padding:    "9px 14px",
                        fontSize:   T.fsXs,
                        fontWeight: T.fwSemibold,
                        color:      T.dark,
                        cursor:     "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      Reactivate market
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="plan-deactivate-market-btn"
                      disabled={deactivating}
                      onClick={async () => {
                        setMoreMenuOpen(false);
                        setDeactivating(true);
                        try {
                          const updated = await setMarketActive(market.id, false);
                          onSaved(market.id, updated, {});
                        } catch (err) {
                          setMenuError(err?.message ?? "Failed to deactivate market");
                        } finally {
                          setDeactivating(false);
                        }
                      }}
                      style={{
                        display:    "block",
                        width:      "100%",
                        background: "transparent",
                        border:     "none",
                        textAlign:  "left",
                        padding:    "9px 14px",
                        fontSize:   T.fsXs,
                        fontWeight: T.fwSemibold,
                        color:      T.dark,
                        cursor:     "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      Deactivate market
                    </button>
                  )}
                  {(() => {
                    const hasLocations = (market.totalLocationCount ?? market.locations?.length ?? 0) > 0;
                    return (
                      <button
                        type="button"
                        data-testid={`delete-market-btn-${market.id}`}
                        disabled={hasLocations}
                        title={hasLocations ? "Reassign its locations first." : "Delete this market"}
                        onClick={() => { setMoreMenuOpen(false); onDeleteMarket(market); }}
                        style={{
                          display:    "block",
                          width:      "100%",
                          background: "transparent",
                          border:     "none",
                          borderTop:  `1px solid ${T.border}`,
                          textAlign:  "left",
                          padding:    "9px 14px",
                          fontSize:   T.fsXs,
                          fontWeight: T.fwSemibold,
                          color:      hasLocations ? T.light : T.ragRed,
                          cursor:     hasLocations ? "not-allowed" : "pointer",
                          fontFamily: "inherit",
                          opacity:    hasLocations ? 0.5 : 1,
                        }}
                      >
                        Delete market
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {menuError && (
        <div style={{ color: T.ragRed, fontSize: T.fsXs, background: T.ragRedBg, borderRadius: 6, padding: "6px 10px", marginTop: 10 }}>
          {menuError}
        </div>
      )}
    </div>
  );
}

export function PlanMatrixRow({ market, isExpanded, isDimmed, isSoleExpanded, onToggle, canEdit, onSaved, onOfficeSaved, onClose, onOpenLocation, density = "comfortable", allMarkets, onDeleteMarket, onMoveOffice, activeDrawer, onOpenDrawer, onCloseDrawer, onExpandMarket, orderedMarkets, scrollTargetId, onScrollTargetHandled, onStartWorkflow, onOpenLinkedRun, marketDirectives = [], onOpenDirective, recordNavigation = false, isSelected = false }) {
  const { canSee } = useSensitivityPolicy();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  const colTemplate = recordNavigation ? (canSeeRentCost ? MARKETS_COLLECTION_COL : MARKETS_COLLECTION_COL_NO_COST) : canSeeRentCost ? COL : COL_NO_COST;
  const panelIsDirtyRef = useRef(false);
  const rowRef = useRef(null);
  useCollectionCellTitles(rowRef, ':scope > [data-testid^="matrix-row-"] > div:not(:nth-child(5))', recordNavigation);
  const [justHighlighted, setJustHighlighted] = useState(false);

  // In Focus tile → scroll-to-expand-and-highlight: when this row becomes the
  // expand target (e.g. via an Attention tile click, possibly from far down
  // the list), smooth-scroll it into view just under the sticky table header
  // and briefly highlight it so the change is obvious even off-screen.
  useEffect(() => {
    if (!isExpanded || scrollTargetId !== market.id) return;
    const raf = requestAnimationFrame(() => {
      if (rowRef.current) {
        const rect = rowRef.current.getBoundingClientRect();
        const stickyClearance = 110; // sticky matrix header + page toolbar
        const targetY = window.scrollY + rect.top - stickyClearance;
        window.scrollTo({ top: Math.max(targetY, 0), behavior: "smooth" });
      }
    });
    setJustHighlighted(true);
    // Clear the scroll target only after the highlight window ends (not
    // synchronously here) — clearing it immediately would change
    // `scrollTargetId`, which is a dependency of this effect, causing React
    // to run this effect's cleanup right away and cancel the pending RAF
    // scroll and highlight timer before they ever fire.
    const highlightTimer = setTimeout(() => {
      setJustHighlighted(false);
      onScrollTargetHandled?.();
    }, 1400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(highlightTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, scrollTargetId, market.id]);
  // Nested office state lives one level up (in PlanMatrix), so opening an
  // office in another market closes the previous inline card.
  const drawerState = activeDrawer && activeDrawer.marketId === market.id ? activeDrawer : null;
  const [localPlanOverrides, setLocalPlanOverrides] = useState({}); // locationId -> patch, applied optimistically after office-plan save

  // MARKET-PLAN-EXPAND-STATE-FIX: only attach the click-outside-to-close
  // listener when this is the *only* expanded row. When expand-all puts
  // multiple rows in the expanded state simultaneously, every row would
  // receive the same mousedown event and call onClose() in concert,
  // collapsing rows the user never touched. The explicit chevron click is
  // the correct close affordance when multiple rows are open.
  useEffect(() => {
    if (!isExpanded || !isSoleExpanded) return;
    function handleMouseDown(e) {
      if (rowRef.current && rowRef.current.contains(e.target)) return;
      if (panelIsDirtyRef.current) {
        if (!window.confirm("You have unsaved changes. Discard them and close?")) return;
      }
      onClose();
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isExpanded, isSoleExpanded, onClose]);

  // Close any open nested card (owned by this row) whenever this row collapses.
  useEffect(() => {
    if (!isExpanded && drawerState) onCloseDrawer();
  }, [isExpanded]);

  function handleOfficeDrawerSaved(locationId, updated, patch) {
    setLocalPlanOverrides((prev) => ({ ...prev, [locationId]: { ...(prev[locationId] ?? {}), ...patch } }));
    // PLAN-OFFICE-DRAWER-PERSIST-FIX: office-level fields must go through a
    // dedicated office callback, never the market-scope `onSaved` — office
    // and market patches share field names (e.g. `planNote`), and forwarding
    // an office patch through the market path used to silently overwrite the
    // market's own plan note in memory.
    if (onOfficeSaved) onOfficeSaved(market.id, locationId, updated, patch);
  }

  const locs      = market.locations ?? [];
  const locsWithOverrides = locs.map((l) =>
    localPlanOverrides[l.locationId] ? { ...l, ...localPlanOverrides[l.locationId] } : l
  );
  // STAGE-VS-LEASE-STATUS-DECOUPLE: membership (composition count + plan-card
  // nav) counts every non-closed office, including "vacated" — see
  // computeMarketComposition for the shared rationale.
  const activeLocs = locs.filter((l) => l.stage !== "draft" && l.stage !== "closed");

  function handleMarketNav(direction) {
    if (!orderedMarkets) return;
    const idx = orderedMarkets.findIndex((m) => m.id === market.id);
    if (idx === -1) return;
    const targetIdx = idx + (direction === "next" ? 1 : -1);
    if (targetIdx < 0 || targetIdx >= orderedMarkets.length) return;
    const targetMarket = orderedMarkets[targetIdx];
    onExpandMarket?.(targetMarket.id);
    onOpenDrawer({ scope: "market", marketId: targetMarket.id, initialPlanNote: undefined });
  }

  function marketNavAvailability() {
    if (!orderedMarkets) return { hasPrev: false, hasNext: false };
    const idx = orderedMarkets.findIndex((m) => m.id === market.id);
    return { hasPrev: idx > 0, hasNext: idx !== -1 && idx < orderedMarkets.length - 1 };
  }

  const inBuildCount = activeLocs.filter(
    (l) => l.stage === "build"
  ).length;
  const hasHub = locs.some((l) => l.isHub);
  const isCompact = density === "compact";

  const metrics   = computeMarketMetrics(market);
  const railColor = marketRailColor(metrics);
  const marketPresence = classifyMarketPresence(market);

  // Composition string: "APAC · 3 offices · 1 in build"
  const compositionParts = [market.region];
  compositionParts.push(`${activeLocs.length} ${activeLocs.length === 1 ? "office" : "offices"}`);
  if (inBuildCount > 0) compositionParts.push(`${inBuildCount} in build`);
  const composition = compositionParts.join(" · ");

  return (
    <div
      ref={rowRef}
      className={isExpanded ? undefined : "sib"}
      data-highlighted={justHighlighted ? "true" : undefined}
      data-dimmed={isDimmed ? "true" : undefined}
      data-lifted={isSoleExpanded && isExpanded ? "true" : undefined}
      style={{
        opacity:    isDimmed ? 0.12 : 1,
        filter:     isDimmed ? "saturate(.20) blur(.9px)" : "none",
        transition: "opacity .2s, filter .2s, box-shadow 200ms ease-out",
        ...(isSoleExpanded && isExpanded ? {
          background:   T.white,
          borderRadius: 12,
          border:       `1px solid ${T.borderDark}`,
          boxShadow:    justHighlighted
            ? `0 0 0 3px rgba(16,36,58,.30), 0 16px 44px -10px rgba(15,23,42,.38)`
            : "0 16px 44px -10px rgba(15,23,42,.38)",
          zIndex:       10,
          margin:       "3px 0",
          position:     "relative",
        } : isExpanded ? {
          background:   T.white,
          borderRadius: 14,
          boxShadow:    justHighlighted
            ? `0 0 0 3px rgba(16,36,58,.30), ${T.shadowPlanCardExpanded}`
            : T.shadowPlanCardExpanded,
          margin:       "14px 0",
        } : {}),
      }}
    >
      {/* Main row */}
      <div
        role="row"
        data-testid={`matrix-row-${market.id}`}
        className={recordNavigation ? "collection-keyboard-row" : undefined}
        data-list-row=""
        data-selected={isSelected ? "true" : undefined}
        aria-selected={recordNavigation ? isSelected : undefined}
        tabIndex={recordNavigation ? 0 : undefined}
        aria-label={recordNavigation ? `Open ${market.name} market` : undefined}
        onKeyDown={(event) => {
          if (recordNavigation && event.target === event.currentTarget && ["Enter", " "].includes(event.key)) {
            event.preventDefault();
            onToggle(event);
          }
        }}
        onClick={(event) => {
          if (recordNavigation && event.target.closest("button, a, input, select, textarea")) return;
          if (recordNavigation) onToggle(event); else onToggle();
        }}
        style={{
          display:      "grid",
          gridTemplateColumns: colTemplate,
          cursor:       "pointer",
          borderBottom: isExpanded ? "none" : "1px solid var(--v2-hair)",
          background:   isSelected ? T.blueSoft : "transparent",
          boxShadow:    isSelected ? `inset 3px 0 0 ${T.blue}` : "none",
          transition:   "background 0.1s",
          ...(recordNavigation ? { height: collectionRowHeight(density), boxSizing: "border-box", minHeight: 0, alignItems: "center" }
            : density === "compact" ? { height: PLAN_ROW_H.compact, overflow: "hidden" } : {}),
        }}
        onMouseEnter={(e) => {
          if (!isExpanded && !isDimmed && !isSelected) e.currentTarget.style.background = T.hoverBg;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isSelected ? T.blueSoft : "transparent";
        }}
      >
        {recordNavigation ? <>
          <div data-collection-identity="" data-testid={`market-identity-${market.id}`} style={{ ...COLLECTION_ONE_LINE, padding: "0 16px" }}>
            <MarketCountry market={market} collection presenceTitle={`${isMarketExited(market) ? "Exited. " : ""}${marketPresence}: ${PRESENCE_META[marketPresence]?.tooltip || "Operating market."}`} />
          </div>
          <div data-testid={`market-hub-${market.id}`} style={{ textAlign: "center", color: hasHub ? T.hubChipText : T.light }} title={hasHub ? "Contains a Hub office" : "No Hub office"}>{hasHub ? "◆" : "–"}</div>
          <div data-testid={`market-region-${market.id}`} style={{ ...COLLECTION_ONE_LINE, padding: "0 8px", color: T.listMuted }} title={market.region || "Region not set"}>{market.region || "–"}</div>
        </> : (
        /* Retained legacy reader keeps its identity/lifecycle presentation. */
        <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 16px var(--plan-matrix-cell-padv, 11px) 16px", overflow: "hidden", display: "flex", alignItems: "center", gap: 10 }}>
          <div
            data-testid={`market-identity-${market.id}`}
            style={{
              minWidth: 0,
              flex:    1,
              ...(isCompact && marketPresence !== "Operating"
                ? { display: "flex", alignItems: "center", gap: 8 }
                : {}),
            }}
          >
            <div
              data-testid={`market-name-line-${market.id}`}
              style={{
                fontWeight: T.fwSemibold,
                fontSize:   T.fsBase,
                color:      "var(--v2-dark)",
                overflow:   "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display:    "flex",
                alignItems: "center",
                gap:        6,
                ...(isCompact && marketPresence !== "Operating"
                  ? { flex: "1 1 auto", minWidth: 0 }
                  : {}),
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{market.name}</span>
              {isMarketExited(market) && (
                <StatusBadge status="Exited" testId={`status-badge-exited-${market.id}`} />
              )}
              {hasHub && (
                <span
                  data-testid={`hub-chip-${market.id}`}
                  style={{
                    display:      "inline-flex",
                    alignItems:   "center",
                    gap:          2,
                    background:   T.hubChipBg,
                    border:       `1px solid ${T.hubChipBorder}`,
                    color:        T.hubChipText,
                    fontSize:     T.fsXxxs,
                    fontWeight:   T.fwBold,
                    padding:      "1px 4px",
                    borderRadius: 3,
                    flexShrink:   0,
                  }}
                >
                  ◆ Hub
                </span>
              )}
            </div>
            {(!isCompact || marketPresence !== "Operating") && (
              <div
                data-testid={`market-meta-line-${market.id}`}
                style={{
                  fontSize: T.fsXs,
                  color:    "var(--v2-light)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  ...(isCompact ? { flexShrink: 0 } : {}),
                }}
              >
                <MarketPresenceBadge presence={marketPresence} marketId={market.id} />
                {!isCompact && (
                  <>
                    {marketPresence !== "Operating" && <span aria-hidden="true"> · </span>}
                    {composition}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Strategy pill */}
        <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 8px var(--plan-matrix-cell-padv, 11px) 0", overflow: "hidden", display: "flex", flexDirection: "column", alignItems: recordNavigation ? "flex-start" : "center", justifyContent: "center" }}>
          <StrategyPill strategy={market.strategy} />
        </div>

        {/* Plan completeness dots */}
        <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 8px var(--plan-matrix-cell-padv, 11px) 8px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: recordNavigation ? "flex-start" : "center" }}>
          <PlanCompletenessCell
            strategy={market.strategy}
            strategyTargetDate={market.strategyTargetDate}
            owner={market.owner}
            planNote={market.planNote}
            locations={market.locations ?? []}
            isCompact={isCompact}
            inline={recordNavigation}
          />
        </div>

        {recordNavigation && <div style={{ ...COLLECTION_ONE_LINE, display: "flex", alignItems: "center", justifyContent: "center", paddingRight: 10 }}>
          <MarketsCapacityPill market={market} compact={isCompact} />
        </div>}
        {recordNavigation && <div data-testid={`market-office-count-${market.id}`} style={{ ...COLLECTION_ONE_LINE, ...LIST_PRESENTATION.numeric, padding: "0 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}
          title={`${composition}\n${marketPresence}: ${PRESENCE_META[marketPresence]?.tooltip || "Operating market."}`}>{activeLocs.length}</div>}

        {/* People/Seats·% — combined headcount, capacity, and assignment % */}
        <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 8px var(--plan-matrix-cell-padv, 11px) 8px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: recordNavigation ? "flex-end" : undefined }}>
          {recordNavigation || market.assignmentToday ? <MarketAssignmentCell summary={market.assignmentToday} /> : <PeopleSeatsCell
            totalHeadcount={market.totalHeadcount}
            totalSeats={market.totalSeats}
            isCompact={isCompact}
          />}
        </div>

        {/* Headroom — market.totalHeadroom rollup (see server/routes/plan.js) */}
        <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 8px var(--plan-matrix-cell-padv, 11px) 8px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: recordNavigation ? "flex-end" : undefined }}>
          <HeadroomCell totalHeadroom={market.totalHeadroom} />
        </div>

        {/* $/yr — market.totalAnnualCost */}
        {!recordNavigation && canSeeRentCost && <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 8px var(--plan-matrix-cell-padv, 11px) 8px", overflow: "hidden", display: "flex", alignItems: "center" }}>
          <AnnualCostCell
            totalAnnualCost={market.totalAnnualCost}
            rentIncompleteOffices={market.rentIncompleteOffices ?? []}
            isCompact={isCompact}
          />
        </div>}

        {/* Next decision — reuses the Leases next-action vocabulary
            (ACTION_DATE_TYPES) via deriveMarketNextDecision. */}
        {!recordNavigation && <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 16px var(--plan-matrix-cell-padv, 11px) 16px", overflow: "hidden", display: "flex", alignItems: "center" }}>
          <NextDecisionCell market={market} isCompact={isCompact} />
        </div>}

        {/* Note */}
        {!recordNavigation && <div style={{ padding: "var(--plan-matrix-cell-padv, 11px) 16px var(--plan-matrix-cell-padv, 11px) 0", overflow: "hidden", display: "flex", alignItems: "center" }}>
          {market.planNote ? (
            <span
              title={market.planNote}
              style={{
                fontSize:     T.fsXs,
                color:        T.mid,
                overflow:     "hidden",
                display:      "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                whiteSpace:   "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {market.planNote}
            </span>
          ) : (
            <span style={{ color: T.light, fontSize: T.fsXs, fontStyle: "italic" }}>No note set</span>
          )}
        </div>}

        {recordNavigation && canSeeRentCost && <div style={{ ...COLLECTION_ONE_LINE, padding: "0 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          <MarketsAnnualCost market={market} />
        </div>}
        {recordNavigation && <div style={{ ...COLLECTION_ONE_LINE, padding: "0 8px" }}>
          <MarketsNextDate selection={market.nextRecordDate} marketId={market.id} />
        </div>}
        {/* Expand chevron — rightmost column. Hit target matches the app's
            standard icon-button sizing (28px square, like the office-thumb /
            nav-caret controls) even though the row itself is already clickable. */}
        {!recordNavigation && <div style={{ padding: "0 16px 0 0", display: "flex", alignItems: "center", justifyContent: "flex-end", whiteSpace: "nowrap" }}>
          <PlanExpandCaret
            isExpanded={isExpanded}
            testId={`expand-chevron-${market.id}`}
          />
        </div>}
      </div>

      {/* Accordion panel (inline below the row, full width) */}
      {isExpanded && (
        <div
          data-testid={`accordion-${market.id}`}
          onClick={(e) => e.stopPropagation()}
          style={{ position: "relative", zIndex: 10, padding: 0, background: T.planExpandBg, borderRadius: "0 0 12px 12px" }}
        >
          <div style={{
            display:       "flex",
            flexDirection: "column",
            borderTop:     `1px solid ${T.planExpandDivider}`,
            minHeight:     200,
            borderRadius:  "0 0 12px 12px",
          }}>
            <MarketContextBand market={{ ...market, locations: locsWithOverrides }} />
            <MarketHeader
              market={market}
              canEdit={canEdit}
              onEditPlan={() => onOpenDrawer({ scope: "market", marketId: market.id, initialPlanNote: undefined })}
              onSaved={onSaved}
              onDeleteMarket={onDeleteMarket}
              onStartWorkflow={onStartWorkflow}
              onOpenLinkedRun={onOpenLinkedRun}
            />
            <MarketDirectivesStrip
              directives={marketDirectives}
              onOpenDirective={onOpenDirective}
            />
            <OfficesTable
              locations={locsWithOverrides}
              onOpenLocation={onOpenLocation}
              canEdit={canEdit}
              onMoveOffice={(loc) => {
                if (drawerState?.scope === "office" && drawerState.loc?.locationId === loc.locationId) {
                  onCloseDrawer();
                }
                onMoveOffice?.(loc);
              }}
              currentMarketId={market.id}
              allMarkets={allMarkets}
              onOpenOfficeDrawer={(loc) => onOpenDrawer({ scope: "office", marketId: market.id, loc })}
              openOfficeId={drawerState?.scope === "office" ? drawerState.loc?.locationId : null}
              onCloseOffice={onCloseDrawer}
              onOfficeSaved={handleOfficeDrawerSaved}
            />
            {(market.strategy === "expand" || market.strategy === "rightsize" ||
              locsWithOverrides.some((l) => l.disposition === "expand" || l.disposition === "rightsize")) && (
              <CandidatesSection
                marketId={market.id}
                canEdit={canEdit}
              />
            )}
          </div>
        </div>
      )}

      {drawerState?.scope === "market" && (
        <MarketPlanScopeDrawer
          market={market}
          canEdit={canEdit}
          onClose={onCloseDrawer}
          onSaved={onSaved}
          onPrev={() => handleMarketNav("prev")}
          onNext={() => handleMarketNav("next")}
          {...marketNavAvailability()}
          initialPlanNote={drawerState.initialPlanNote}
        />
      )}
    </div>
  );
}

// ─── CaretButton — xcaret standard expand/collapse control ───────────────────

function CaretButton({ marketId, isExpanded }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      data-testid={`expand-chevron-${marketId}`}
      aria-label={isExpanded ? "Collapse" : "Expand"}
      role="button"
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        justifyContent:  "center",
        width:           28,
        height:          28,
        borderRadius:    6,
        cursor:          "pointer",
        userSelect:      "none",
        fontSize:        "18px", /* chevron: 18px min — do not reduce */
        color:           hovered ? T.dark : T.mid,
        background:      "transparent",
        border:          "none",
        flexShrink:      0,
      }}
    >
      {isExpanded ? "▴" : "▾"}
    </div>
  );
}

// ─── SortableHeader — clickable column header with sort arrow (div-based) ────

function SortableHeader({ sortKey, label, tooltip = label, active, dir, onSort, userHasSetSort, textAlign = "left", standard = false }) {
  return (
    <CollectionSortHeader as="div" active={active} direction={dir} standard={standard}
      data-testid={`sort-th-${sortKey}`}
      className={textAlign === "right" ? "sort-header-numeric" : undefined}
      title={tooltip}
      data-sort-dir={active ? dir : undefined}
      onClick={() => onSort(sortKey)}
      style={{
        ...LIST_PRESENTATION.header,
        lineHeight:     "normal",
        color:          "var(--v2-mid)",
        fontWeight:     T.fwBold,
        padding:        "10px 14px",
        display:        "flex",
        alignItems:     "center",
        justifyContent: textAlign === "center" ? "center" : "flex-start",
        whiteSpace:     "nowrap",
        overflow:       "hidden",
        cursor:         "pointer",
        userSelect:     "none",
        background:     "var(--v2-tint)",
        ...(!standard ? {
          ...getColHeaderHighlight(sortKey, active ? sortKey : "__none__", userHasSetSort),
          ...((active && userHasSetSort) ? {} : { background: "var(--v2-tint)" }),
        } : {}),
      }}
    >
      <span className="sort-header-label">{label}</span>
      <CollectionSortCaret
        testId={`v2-sort-indicator-${sortKey}`}
      />
    </CollectionSortHeader>
  );
}

// ─── SortControl — reusable "Sort by" + "Then by" tool ───────────────────────
// Generic: takes its field list via `fields` prop so it can be reused on
// Offices/Leases later without hardcoding Market Plan-only field names here.
//
// Props:
//   fields    — [{ value, label }]  suggested/available sort fields
//   primary   — { key, dir }        controlled primary sort
//   secondary — { key, dir }        controlled secondary ("Then by") sort; key may be null
//   onChange  — ({ primary, secondary }) => void
//   testId    — optional data-testid prefix for the trigger button

// SortControl + SortIcon extracted to ../../v2/components/SortControl.jsx
// (LIST-SORT-ROLLOUT-S1) so Offices and Leases can reuse the same component.

// ─── PlanMatrix — the full matrix table ──────────────────────────────────────

// MARKET-PLAN-LEADERSHIP-VIEW-S1: Market · Strategy · Plan · People/Seats·% ·
// Headroom · $/yr · Next decision · Note · chevron. "Health" is gone — RAG
// coloring now lives inline on the numeric cells themselves (People/Seats·%,
// Headroom, Next decision) rather than a separate summary column.
const COL               = "200px 120px 90px 160px 100px 120px 150px 1fr 32px";
const COL_NO_COST       = "200px 120px 90px 160px 100px 150px 1fr 32px";
const MATRIX_TABLE_RADIUS = 12;
const PLAN_ROW_H = { comfortable: undefined, compact: 28 };

function PlanMatrix({ onOpenMarket, markets, sortPending = false, geometryPending = false, canEdit, onMarketSaved, onOfficeSaved, onOpenLocation, sortKey, sortDir, onSort, userHasSetSort, density = "comfortable", allMarkets, onDeleteMarket, onMoveOffice, expandedIds, onToggleExpanded, scrollTargetId, onScrollTargetHandled, onStartWorkflow, onOpenLinkedRun, allDirectives = [], onOpenDirective, selectedMarketId = null }) {
  const { canSee } = useSensitivityPolicy();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  const collection = Boolean(onOpenMarket);
  const geometryRef = useRef(null);
  useMarketsListGeometry(geometryRef, collection, markets, density, canSeeRentCost, geometryPending);
  const colTemplate = collection ? (canSeeRentCost ? MARKETS_COLLECTION_COL : MARKETS_COLLECTION_COL_NO_COST) : canSeeRentCost ? COL : COL_NO_COST;
  const sentinelRef = useRef(null);
  const [legacyIsStuck, setLegacyIsStuck] = useState(false);
  const headerScrollRef = useRef(null);
  const collectionIsStuck = useListHeaderStuck({ sentinelRef, headerScrollRef, enabled: collection });
  const isStuck = collection ? collectionIsStuck : legacyIsStuck;
  const bodyScrollRef = useRef(null);
  const [overflowElement, setOverflowElement] = useState(null);
  const horizontal = useListOverflow({ element: overflowElement, scrollRef: bodyScrollRef, grid: true, pinLast: !collection, invalidate: density });
  const minListWidth = collection ? marketsCollectionWidth(canSeeRentCost) : canSeeRentCost ? 1200 : 1080;

  // Single, matrix-level drawer state (item 6) — shared across all rows so
  // prev/next can hop between markets and there is never more than one
  // office/market drawer open at once.
  const [activeDrawer, setActiveDrawer] = useState(null);
  const closeDrawer = useCallback(() => setActiveDrawer(null), []);

  // Keep a ref in sync with expandedIds so expandMarket can read the live
  // value without listing the Set itself as a useCallback dependency.
  // A new Set instance is created on every setPlanExpandedIds call, so
  // putting expandedIds in the dep array gives expandMarket a new reference
  // every render, which propagates as onExpandMarket into every PlanMatrixRow
  // and triggers any effect that depends on it — causing an infinite loop.
  const expandedIdsRef = useRef(expandedIds);
  useEffect(() => { expandedIdsRef.current = expandedIds; }, [expandedIds]);

  const expandMarket = useCallback((marketId) => {
    if (!expandedIdsRef.current.has(marketId)) onToggleExpanded(marketId);
  }, [onToggleExpanded]);

  // ── Bottom-fade scroll tracking (page-level scroll) ───────────────────────
  const [atBottom, setAtBottom] = useState(true);

  useEffect(() => {
    function check() {
      const isScrollable = document.body.scrollHeight > window.innerHeight + 4;
      setAtBottom(!isScrollable || window.scrollY + window.innerHeight >= document.body.scrollHeight - 4);
    }
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  // Re-check after markets/expand change (body height shifts).
  useEffect(() => {
    const id = setTimeout(() => {
      const isScrollable = document.body.scrollHeight > window.innerHeight + 4;
      setAtBottom(!isScrollable || window.scrollY + window.innerHeight >= document.body.scrollHeight - 4);
    }, 0);
    return () => clearTimeout(id);
  }, [markets.length, expandedIds.size]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (collection || !el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => { setLegacyIsStuck(!entry.isIntersecting); },
      { root: null, threshold: [1] }
    );
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, [collection]);

  const handleCloseMarket = useCallback((id) => {
    onToggleExpanded(id);
  }, [onToggleExpanded]);

  const handleRowSaved = useCallback((marketId, updated, patch) => {
    onMarketSaved(marketId, updated, patch);
    showToast("Plan saved");
  }, [onMarketSaved]);

  const handleOfficeRowSaved = useCallback((marketId, locationId, updated, patch) => {
    onOfficeSaved(marketId, locationId, updated, patch);
    showToast("Plan saved");
  }, [onOfficeSaved]);

  const anyExpanded = expandedIds.size > 0;

  if (markets.length === 0) {
    return (
      <div style={{ padding: "40px 0", textAlign: "center", color: T.light }}>
        No markets match the current filters.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Dim-and-lift focus styles — CSS handles :hover restore and reduced-motion.
          Inline styles set the base opacity/filter; this sheet overrides them on hover
          and removes the transition for users who prefer no animation.
          !important is intentional: overrides inline style specificity. */}
      <style>{`
        .markets-collection [data-testid="plan-column-header"] [role="row"],
        .markets-collection [data-testid^="matrix-row-"] {
          grid-template-columns: var(--markets-columns, ${colTemplate}) !important;
          min-width: var(--markets-min-width, ${minListWidth}px) !important;
        }
        .markets-collection:not([data-markets-ready="true"]) [data-testid="plan-column-header"] [role="row"],
        .markets-collection:not([data-markets-ready="true"]) [data-testid^="matrix-row-"] {
          visibility: hidden;
        }
        .markets-collection[data-markets-ready="true"] [data-testid="markets-geometry-loading"] {
          display: none;
        }
        .markets-collection [data-testid="plan-column-header"] [role="row"] {
          height: ${COLLECTION_GEOMETRY.header}px; box-sizing: border-box;
        }
        .markets-collection [data-testid="plan-column-header"] [role="row"] > * {
          padding: 0 4px !important; min-height: 0; justify-content: center !important;
        }
        .markets-collection [data-testid="plan-column-header"] [role="row"] > :first-child,
        .markets-collection [data-testid="plan-column-header"] [role="row"] > :last-child {
          padding: 0 14px !important;
        }
        .markets-collection [data-testid^="market-region-"] {
          text-align: center;
        }
        .markets-collection .sort-header-label {
          white-space: nowrap;
        }
        .markets-collection [data-testid^="matrix-row-"] > * {
          min-width: 0; white-space: nowrap; box-sizing: border-box;
          padding-left: 4px !important; padding-right: 4px !important;
        }
        .markets-collection [data-testid^="matrix-row-"] > :first-child {
          padding-left: 14px !important;
        }
        .markets-collection [data-testid^="matrix-row-"] > :last-child {
          padding-right: 14px !important;
        }
        .markets-collection [data-testid^="matrix-row-"] > :nth-child(4),
        .markets-collection [data-testid^="matrix-row-"] > :nth-child(5),
        .markets-collection [data-testid^="matrix-row-"] > :nth-child(6) {
          justify-content: center !important;
        }
        .markets-collection[data-density="compact"] [data-testid^="strategy-pill-"] {
          height: 20px !important; font-size: ${T.fsXxs}px !important;
        }
        [data-dimmed="true"]:hover {
          opacity: .72 !important;
          filter: none !important;
        }
        @media (prefers-reduced-motion: reduce) {
          [data-dimmed="true"], [data-lifted="true"] {
            transition: none !important;
          }
        }
      `}</style>
      <div
        ref={geometryRef}
        data-testid="plan-matrix"
        className={collection ? "markets-collection" : undefined}
        data-density={density}
        style={{
          background:   T.white,
          border:       `1px solid ${T.border}`,
          borderRadius: MATRIX_TABLE_RADIUS,
          ...(collection ? LIST_PRESENTATION.container : {}),
          /* CSS variable controls cell padding — consumed by PlanMatrixRow cells.
             In compact mode the main row has a fixed height (PLAN_ROW_H.compact),
             so cell padding is set to 0 to avoid adding to intrinsic content height.
             In comfortable mode padding drives row height (no fixed height on row). */
          "--plan-matrix-cell-padv": collection || density === "compact" ? "0px" : "11px",
        }}
      >
        {/* Zero-height sentinel — exits viewport when header pins, triggering isStuck. */}
        <div ref={sentinelRef} aria-hidden="true" style={{ height: 0 }} />
        {/* Bottom-fade: sticky at viewport bottom while rows remain below visible area.
            Placed before the table so sticky positioning works in a page-scroll context.
            marginBottom: -72 prevents the 72px element from shifting the table down.
            pointerEvents: none keeps rows beneath fully clickable. */}
        {!atBottom && (
          <div
            aria-hidden="true"
            style={{
              position:      "sticky",
              top:           "calc(100vh - 72px)",
              height:        72,
              marginBottom:  -72,
              pointerEvents: "none",
              zIndex:        2,
            }}
          >
            <div
              style={{
                position:     "absolute",
                inset:        0,
                background:   "linear-gradient(to bottom, transparent 30%, rgba(255,255,255,0.97) 100%)",
                borderRadius: `0 0 ${MATRIX_TABLE_RADIUS}px ${MATRIX_TABLE_RADIUS}px`,
              }}
            />
          </div>
        )}
        <div ref={setOverflowElement} style={{ width: "100%", minWidth: 0 }}>
          {/* Sticky header row */}
          <div data-testid="plan-column-header" style={{ position: "sticky", top: "var(--places-sticky-top, 0px)", zIndex: 2 }}>
            <div ref={headerScrollRef} data-testid={collection ? "market-header-clip" : undefined}
              style={{ overflowX: "hidden", ...(collection ? listHeaderEdges(isStuck) : {}) }}>
            <div role="row" style={{
              display:             "grid",
              gridTemplateColumns: colTemplate,
              minWidth: minListWidth,
              borderBottom:        "1px solid var(--v2-line)",
              boxShadow:           isStuck ? "0 2px 6px rgba(16,36,58,.07)" : "none",
              transition:          "box-shadow 120ms ease",
              borderRadius:        isStuck ? 0 : `${MATRIX_TABLE_RADIUS}px ${MATRIX_TABLE_RADIUS}px 0 0`,
              ...(collection ? listHeaderEdges(isStuck) : {}),
            }}>
              <SortableHeader standard={collection} sortKey="market"       label="Market"           active={sortKey === "market"}       dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />
              {collection && [
                ["Hub", "center"], ["Region", "center"],
              ].map(([label, textAlign]) => <div key={label} role="columnheader" data-testid={`market-column-${label.toLowerCase()}`}
                style={{ display: "flex", alignItems: "center", justifyContent: textAlign === "right" ? "flex-end" : textAlign === "left" ? "flex-start" : "center",
                  padding: "0 14px", background: T.listTint, ...LIST_PRESENTATION.header,
                  whiteSpace: "nowrap" }}>{label}</div>)}
              <SortableHeader standard={collection} sortKey="strategy"     label="Strategy"         active={sortKey === "strategy"}     dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />
              {/* Plan completeness column — sorts by count of completed plan
                  steps (numeric "completeness" key, shared with the Sort tool
                  via PLAN_SORT_FIELDS/getSortValue). */}
              <SortableHeader standard={collection} sortKey="completeness" label="Plan"             active={sortKey === "completeness"} dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />
              {collection && <SortableHeader standard sortKey="capacity" label="Capacity" active={sortKey === "capacity"} dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />}
              {collection && <div role="columnheader" data-testid="market-column-offices"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "0 14px", background: T.listTint, ...LIST_PRESENTATION.header, whiteSpace: "nowrap" }}>Offices</div>}
              <SortableHeader standard={collection} sortKey="people" label={collection ? "Assigned / Seats" : "People / installed"} tooltip={collection ? "Assigned / Seats" : "People / Installed"} active={sortKey === "people"} dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />
              <SortableHeader standard={collection} sortKey="headroom"     label="Expansion"        active={sortKey === "headroom"}     dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />
              {canSeeRentCost && <SortableHeader standard={collection} sortKey="cost" label="Annual cost" active={sortKey === "cost"} dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />}
              {collection && <SortableHeader standard sortKey="nextDate" label="Next date" active={sortKey === "nextDate"} dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />}
              {!collection && <SortableHeader sortKey="nextDecision" label="Next decision"    active={sortKey === "nextDecision"} dir={sortDir} onSort={onSort} userHasSetSort={userHasSetSort} textAlign="center" />}
              {!collection && <div
                data-testid="sort-th-note"
                style={{
                  fontSize:       T.fsXxs,
                  textTransform:  "uppercase",
                  letterSpacing:  ".07em",
                  color:          "var(--v2-mid)",
                  fontWeight:     T.fwBold,
                  padding:        "10px 14px",
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  whiteSpace:     "nowrap",
                  overflow:       "hidden",
                  background:     "var(--v2-tint)",
                }}
              >
                Note
              </div>}
              {/* Chevron column — empty header */}
              {!collection && <div style={{ background: "var(--v2-tint)" }} />}
            </div>
            </div>
          </div>
          {horizontal.header && createPortal(<OverflowButton overflow={horizontal.overflow} right={horizontal.right} />, horizontal.header)}
          <div ref={bodyScrollRef} style={{ overflowX: "auto" }} onScroll={(event) => {
            if (headerScrollRef.current) headerScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }}>
          <div style={{ minWidth: minListWidth, ...(collection ? { position: "relative" } : {}) }}>
          {collection && !sortPending && <div data-testid="markets-geometry-loading" role="status" aria-busy="true"
            style={{ position: "absolute", top: 0, left: 14, color: T.mid }}>
            Preparing market columns…
          </div>}
          {collection && sortPending ? <div data-testid="markets-sort-loading" role="status" aria-busy="true"
            style={{ height: markets.length * collectionRowHeight(density), minHeight: collectionRowHeight(density), color: T.mid }}>
            Loading market sort data…
          </div> : markets.map((m) => (
            <PlanMatrixRow
              key={m.id}
                recordNavigation={Boolean(onOpenMarket)}
              isSelected={Boolean(onOpenMarket) && selectedMarketId === m.id}
              market={m}
              isExpanded={expandedIds.has(m.id)}
              isDimmed={expandedIds.size === 1 && !expandedIds.has(m.id)}
              isSoleExpanded={expandedIds.size === 1 && expandedIds.has(m.id)}
               onToggle={(event) => {
                 // PLACES-SLICE-2: the Places Markets list navigates to the
                 // market record instead of expanding an inline accordion.
                 if (onOpenMarket) { onOpenMarket(m.id, { event }); return; }
                 // A market toggle is a level-1 navigation action. Close any
                 // level-2 office card first so nested state never survives
                 // into another market context.
                 closeDrawer();
                 onToggleExpanded(m.id);
               }}
              canEdit={canEdit}
              onSaved={handleRowSaved}
              onOfficeSaved={handleOfficeRowSaved}
              onClose={() => handleCloseMarket(m.id)}
              onOpenLocation={onOpenLocation}
              density={density}
              allMarkets={allMarkets}
              onDeleteMarket={onDeleteMarket}
              onMoveOffice={onMoveOffice}
              activeDrawer={activeDrawer}
              onOpenDrawer={setActiveDrawer}
              onCloseDrawer={closeDrawer}
              onExpandMarket={expandMarket}
              orderedMarkets={markets}
              scrollTargetId={scrollTargetId}
              onScrollTargetHandled={onScrollTargetHandled}
              onStartWorkflow={onStartWorkflow}
              onOpenLinkedRun={onOpenLinkedRun}
              marketDirectives={allDirectives.filter((d) =>
                (d.markets ?? []).some((dm) => dm.marketId === m.id)
              )}
              onOpenDirective={onOpenDirective}
            />
          ))}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── UnassignedBin ────────────────────────────────────────────────────────────
// PLAN-UNASSIGNED-LOCATIONS: pinned bin at top of Matrix showing locations with
// no market assignment (market_id IS NULL). Hidden when count = 0.

function UnassignedBin({ locations, canEdit, allMarkets, onAssign }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!locations || locations.length === 0) return null;

  return (
    <div data-testid="unassigned-bin" style={{ marginBottom: 8 }}>
      {/* Header row */}
      <div
        data-testid="unassigned-bin-header"
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setIsExpanded((v) => !v); }}
        style={{
          display:     "flex",
          alignItems:  "center",
          gap:         8,
          padding:     "0 12px",
          height:      44,
          background:  T.white,
          border:      `1px solid ${T.border}`,
          borderRadius: isExpanded ? "8px 8px 0 0" : 8,
          cursor:      "pointer",
          userSelect:  "none",
        }}
      >
        <span
          data-testid="unassigned-expand-chevron"
          style={{
            fontSize:         T.fsXxxs,
            color:            T.light,
            display:          "inline-block",
            transform:        isExpanded ? "rotate(90deg)" : "none",
            transition:       "transform 120ms ease",
          }}
        >
          ▶
        </span>
        <span
          data-testid="unassigned-bin-badge"
          style={{
            background:   T.warnAmberBg,
            color:        T.blockedText,
            borderRadius: 12,
            padding:      "2px 9px",
            fontSize:     T.fsXxs,
            fontWeight:   T.fwBold,
          }}
        >
          ⚠ Unassigned · {locations.length}
        </span>
      </div>

      {/* Expanded location rows */}
      {isExpanded && (
        <div
          style={{
            border:       `1px solid ${T.border}`,
            borderTop:    "none",
            borderRadius: "0 0 8px 8px",
            background:   T.white,
          }}
        >
          {locations.map((loc, idx) => (
            <div
              key={loc.locationId}
              data-testid={`unassigned-row-${loc.locationId}`}
              style={{
                display:    "flex",
                alignItems: "center",
                padding:    "10px 16px",
                gap:        16,
                borderTop:  idx === 0 ? "none" : `1px solid ${T.border}`,
              }}
            >
              <span style={{ flex: "2 1 0", fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold }}>
                {loc.locationAlias || loc.locationId}
              </span>
              <span style={{ flex: "1 1 0", fontSize: T.fsXs, color: T.mid }}>
                {loc.region ?? "—"}
              </span>
              <span style={{ flex: "1 1 0", fontSize: T.fsXs, color: T.mid }}>
                {loc.stage ?? "—"}
              </span>
              {canEdit && (
                <button
                  type="button"
                  data-testid={`unassigned-assign-btn-${loc.locationId}`}
                  onClick={() => onAssign({ ...loc, marketId: null })}
                  style={{
                    background:   "none",
                    border:       `1px solid ${T.border}`,
                    borderRadius: 6,
                    padding:      "4px 10px",
                    fontSize:     T.fsXs,
                    color:        T.blue,
                    cursor:       "pointer",
                    whiteSpace:   "nowrap",
                    fontFamily:   "inherit",
                  }}
                >
                  Assign to market →
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Plan filter dimension configs ────────────────────────────────────────────

const REGIONS = ["AMER", "APAC", "EMEA"];

// Synthetic status value that folds the old "In build" smart chip into the
// Status filter group (see matchesStatusValue below). The underlying value
// stays "In build" (matched against in matchesStatusValue); only the
// displayed option label is title-cased to "In-Build".
const STATUS_IN_BUILD_VALUE = "In build";
const STATUS_IN_BUILD_LABEL = "In-Build";

const STATUS_FILTER_OPTIONS = [
  ...STATUS_OPTIONS,
  { value: STATUS_IN_BUILD_VALUE, label: STATUS_IN_BUILD_LABEL },
];

// Hub filter dimension values — plain booleans on the wire, dedicated
// string values in the filter panel so they compose with the other
// multi-select dimensions the same way.
const HUB_FILTER_HUB     = "hub";
const HUB_FILTER_NON_HUB = "non_hub";
const HUB_FILTER_OPTIONS = [
  { value: HUB_FILTER_HUB,     label: "Hub" },
  { value: HUB_FILTER_NON_HUB, label: "Non-Hub" },
];

const PLAN_FILTER_DIMENSIONS_BASE = [
  {
    id:      "strategy",
    label:   "Strategy",
    options: STRATEGY_OPTIONS,
  },
  {
    id:      "region",
    label:   "Region",
    options: REGIONS.map((r) => ({ value: r, label: r })),
  },
  {
    id:      "status",
    label:   "Status",
    options: STATUS_FILTER_OPTIONS,
  },
  {
    id:      "hub",
    label:   "Hub",
    options: HUB_FILTER_OPTIONS,
  },
];

function buildPlanFilterDimensions() {
  return PLAN_FILTER_DIMENSIONS_BASE;
}

const MARKET_UNCLASSIFIED_STATUS = "__none__";
export const MARKET_FILTER_DIMENSIONS = PLAN_FILTER_DIMENSIONS_BASE.map(dim => dim.id === "status"
  ? { ...dim, options: [...dim.options, { value: MARKET_UNCLASSIFIED_STATUS, label: "Unclassified" }] }
  : dim);
export const MARKET_FILTER_DEFAULTS = {
  status: MARKET_FILTER_DIMENSIONS.find(dim => dim.id === "status").options.filter(option => option.value !== "Exited").map(option => option.value),
};

export function marketCheckedSelections(prefs) {
  const raw = prefs.cfSelections ?? {};
  const legacy = prefs.filterVersion !== CHECKED_FILTER_VERSION;
  return normalizeCheckedSelections(MARKET_FILTER_DIMENSIONS, {
    ...raw,
    ...(legacy && !raw.status?.length ? { status: MARKET_FILTER_DEFAULTS.status } : {}),
  }, MARKET_FILTER_DEFAULTS, { legacy });
}

export function applyMarketCollectionFilters(rows, selections, countGroup = null) {
  const predicates = checkedPredicateSelections(MARKET_FILTER_DIMENSIONS, selections, false, countGroup);
  if (!predicates) return [];
  return rows.filter(m => {
    if (predicates.strategy?.length && !predicates.strategy.includes(m.strategy)) return false;
    if (predicates.region?.length && !predicates.region.includes(m.region)) return false;
    const metrics = computeMarketMetrics(m);
    if (predicates.status?.length && !predicates.status.some(v => v === MARKET_UNCLASSIFIED_STATUS
      ? !STATUS_OPTIONS.some(option => option.value === m.status)
      : matchesStatusValue(m, metrics, v))) return false;
    if (predicates.hub?.length && !predicates.hub.some(v => v === HUB_FILTER_HUB ? metrics.hasHub : !metrics.hasHub)) return false;
    return true;
  });
}

function legacyMarketSelections(prefs) {
  if (prefs.filterVersion !== CHECKED_FILTER_VERSION) return prefs.cfSelections ?? {};
  const selected = marketCheckedSelections(prefs);
  const legacy = checkedPredicateSelections(MARKET_FILTER_DIMENSIONS, selected) ?? selected;
  if (selectionSetsEqual(selected.status, MARKET_FILTER_DEFAULTS.status)) delete legacy.status;
  return legacy;
}

// ─── quarterFromDate — convert a JS Date to "Q1 2026" notation ──────────────
// Returns null for years > 2100 (sentinel guard) or invalid dates.
function quarterFromDate(d) {
  if (!d || isNaN(d.getTime())) return null;
  if (d.getFullYear() > 2100) return null;
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${q} ${d.getFullYear()}`;
}

// ─── computeMarketMetrics — derive KPI/chip/rail signals from a market object ──

function computeMarketMetrics(market) {
  const activeLocs = (market.locations ?? []).filter(
    (l) => l.stage !== "draft" && l.stage !== "closed" && l.stage !== "vacated"
  );

  const runways = activeLocs
    .map((l) => monthsUntil(l.nextDeadline?.dueDate))
    .filter((n) => n !== null);
  const nextLeaseEventMonths = runways.length > 0 ? Math.min(...runways) : null;

  const assignedPct = market.assignmentToday
    ? (marketAssignedToday(market) != null && marketInstalledToday(market) > 0
        ? Math.round(marketAssignedToday(market) / marketInstalledToday(market) * 100) : null)
    : market.avgUtilPct != null
    ? market.avgUtilPct
    : (market.totalHeadcount != null && market.totalSeats != null && market.totalSeats > 0
        ? Math.round(market.totalHeadcount / market.totalSeats * 100)
        : null);

  const hasInBuild = activeLocs.some(
    (l) => l.stage === "build"
  );

  // TODO: hasBadgeReader not yet in /api/plan response shape; always false until exposed
  const hasBadgeReader = false;

  const missingPlan = !market.strategy;

  const hasHub = (market.locations ?? []).some((l) => l.isHub);

  return { nextLeaseEventMonths, assignedPct, hasInBuild, hasBadgeReader, missingPlan, hasHub };
}

/** Returns the rail/signal color for a market based on its metrics. */
function marketRailColor(metrics) {
  const { nextLeaseEventMonths, assignedPct, hasInBuild } = metrics;
  if (nextLeaseEventMonths !== null && nextLeaseEventMonths < 0) return T.ragRed;
  if (assignedPct !== null && assignedPct >= 100) return T.ragRed;
  if (nextLeaseEventMonths !== null && nextLeaseEventMonths <= 12) return T.ragAmber;
  if (assignedPct !== null && assignedPct >= 94) return T.ragAmber;
  if (hasInBuild) return T.blue;
  return "transparent";
}

// ─── KpiStrip — 6-tile KPI grid above the toolbar (PLAN-WHATIF-REDESIGN-S1) ──
// Grid sizing must stay pixel-identical to the Offices KPI tiles
// (src/v2/locations/OfficesPage.jsx "v2-kpi-tiles"): 6 columns,
// minmax(140px, 260px) each, 12px gap.
//
// KPI-SCOPE-INDICATOR (G-22): tile set updated to match the scope indicator spec:
//   Responsive (left 4, react to filteredMarkets): Annual cost ·
//     Markets over capacity · Portfolio balance · Next wall
//   Portfolio (left, always allMarkets): Markets
//   Portfolio (right, always allMarkets): Assignment ratio
//
// `kpiHistory` is `{ available, month, kpis }` from GET /api/plan/kpi-history.
// `filteredMarkets` drives the 5 responsive tiles; `allMarkets` drives the portfolio ratio.
// `nextWallQuarter` is pre-computed by the parent from filteredMarkets.
function KpiStrip({ filteredMarkets, allMarkets, isFiltered, kpiHistory, nextWallQuarter, showPlansComplete = false, showAnnualCost = true, listSummary = null }) {
  const { canSee } = useSensitivityPolicy();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  const kpis      = useMemo(() => computePortfolioKpis(filteredMarkets), [filteredMarkets]);
  const portKpis  = useMemo(() => computePortfolioKpis(allMarkets),      [allMarkets]);
  const operatingMarketCount = useMemo(
    () => getOperatingMarketKpiCount(allMarkets),
    [allMarkets],
  );
  const establishingMarketCount = useMemo(
    () => getEstablishingMarketKpiCount(allMarkets),
    [allMarkets],
  );

  // Derive the rent/opex breakdown from the filtered markets that drive the
  // Annual Cost tile — same scope so the two numbers are always consistent.
  const annualCostBreakdown = useMemo(() => {
    const { rentTotal, opexTotal } = computeAnnualCostBreakdown(filteredMarkets);
    const rows = [];
    if (rentTotal != null && rentTotal > 0) {
      rows.push({ label: "Base rent", value: fmtKpiUsd(rentTotal) });
    }
    if (opexTotal != null && opexTotal > 0) {
      rows.push({ label: "Opex (workplace + security)", value: fmtKpiUsd(opexTotal) });
    }
    return rows.length > 0 ? rows : null;
  }, [filteredMarkets]);

  const overCapCount = listSummary ? listSummary.over : kpis.overCapacityCount;
  const capacitySourceUnavailable = listSummary && listSummary.capacityReadStatus !== "ok";
  const overCapColor  = overCapCount > 0 ? T.ragRed : T.dark;
  const balanceColor  = kpis.balance < 0 ? T.ragRed : T.greenDeep;
  const balanceLabel  = `${kpis.balance >= 0 ? "+" : ""}${Math.round(kpis.balance)}`;
  const assignPct     = portKpis.peakUtilPct != null ? `${Math.round(portKpis.peakUtilPct)}%` : "—";
  const assignColor   = portKpis.peakUtilPct > 100 ? T.ragRed : (portKpis.peakUtilPct >= 90 ? T.ragAmber : "var(--v2-green)");

  // Markets KPI copy reviewed ahead of navigation enablement. Keep descriptions
  // aligned to each KPI's source and scope; visibility gates remain unchanged.
  return (
    <KpiBand testId="kpi-strip">
      <KpiTile
        key="markets"
        testId="kpi-tile-markets"
        label="Markets"
        tooltip={<KpiPopover period="Today" source="Ripple markets + office stages">
          Active markets with at least one operating office; markets with only offices in build appear separately.
        </KpiPopover>}
        tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
        tooltipPlacement="viewport-clamped"
        descriptor="distinct markets"
        value={String(operatingMarketCount)}
        rawValue={operatingMarketCount}
        secondary={establishingMarketCount > 0 ? `+${establishingMarketCount} establishing` : undefined}
        tileIndex={0}
        color={T.blue}
        height={KPI_TILE_ROW_HEIGHT}
        scope="portfolio"
      />
      {showAnnualCost && canSeeRentCost && <KpiTile
        key="annual-cost"
        testId="kpi-tile-annual-cost"
        label="Annual cost"
        tooltip={<KpiPopover
          period="Today"
          source="Spacebase (USD, derived)"
          details={annualCostBreakdown}
        >
          Annualized rent and operating expenses for active markets in the current view.
          {kpis.annualCostPartial && <> Some markets have no rent or opex on file, so the total excludes them.</>}
        </KpiPopover>}
        tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
        tooltipPlacement="viewport-clamped"
        descriptor="all-in, fully-loaded"
        value={kpis.totalAnnualCost != null ? fmtKpiUsd(kpis.totalAnnualCost) : "—"}
        rawValue={kpis.totalAnnualCost ?? undefined}
        formatter={fmtKpiUsd}
        tileIndex={1}
        color="var(--v2-green)"
        height={KPI_TILE_ROW_HEIGHT}
        scope="responsive"
        isFiltered={isFiltered}
      />}
      <KpiTile
        key="over-capacity"
        testId="kpi-tile-over-capacity"
        label="Markets over capacity"
        tooltip={<KpiPopover period={listSummary ? listSummary.month : "Latest available"} source="Workday + Envoy/admin (derived)">
          {listSummary ? <>{CAPACITY_STATUS_DEFINITION}. Over on the capacity ladder this month, using the current-calendar-month baseline and the same markets as the list.
            {listSummary.unavailable.length > 0 && <> {listSummary.unavailable.length} markets without capacity data.</>}
            {listSummary.unavailable.length > 0 && <> Unavailable or excluded: {listSummary.unavailable.map(m => `${m.name} (${m.capacityLadder?.label || m.capacityLadder?.status || "unavailable"})`).join(", ")}. This is not a confirmed complete count.</>}
            {listSummary.policyNote && <> {listSummary.policyNote}</>}
          </> : <>Active markets in the current view where the latest assigned headcount exceeds usable installed seats.</>}
        </KpiPopover>}
        tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
        tooltipPlacement="viewport-clamped"
        descriptor={listSummary ? "Over on capacity ladder this month" : "assigned exceeds usable seats"}
        value={capacitySourceUnavailable ? "–" : listSummary && !listSummary.confirmedCapacity ? (overCapCount > 0 ? `${overCapCount}+` : "0 †") : String(overCapCount)}
        rawValue={!capacitySourceUnavailable && (!listSummary || listSummary.confirmedCapacity) ? overCapCount : undefined}
        tileIndex={2}
        color={overCapColor}
        height={KPI_TILE_ROW_HEIGHT}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        key="portfolio-balance"
        testId="kpi-tile-portfolio-balance"
        label="Portfolio balance"
        tooltip={<KpiPopover period="Latest available" source="Workday + Envoy/admin (derived)">
          Usable installed seats minus assigned headcount across active markets in the current view; a negative balance means headcount exceeds seats.
        </KpiPopover>}
        tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
        tooltipPlacement="viewport-clamped"
        descriptor="usable seats minus assigned HC"
        value={balanceLabel}
        tileIndex={3}
        color={balanceColor}
        height={KPI_TILE_ROW_HEIGHT}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        key="next-wall"
        testId="kpi-tile-next-wall"
        label="Next lease event"
        tooltip={<KpiPopover period="Today" source="Spacebase (derived)">
          The soonest upcoming lease decision deadline or expiry across offices in the current view.
          {listSummary && <> {listSummary.dateDisclosure} {listSummary.incompleteDates && "Incomplete or unverified date evidence remains; this is not a confirmed absence."}</>}
        </KpiPopover>}
        tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
        tooltipPlacement="viewport-clamped"
        descriptor={listSummary ? "nearest qualified lease date" : "nearest lease expiry or break"}
        value={listSummary ? (listSummary.quarter
          ? `${listSummary.quarter}${listSummary.next.provenance === "calculated" ? " calc" : listSummary.next.provenance === "ambiguous" ? " unverified" : ""}`
          : listSummary.incompleteDates ? "Unavailable" : "None") : nextWallQuarter}
        tileIndex={4}
        color={T.ragAmber}
        height={KPI_TILE_ROW_HEIGHT}
        scope="responsive"
        isFiltered={isFiltered}
      />
      <KpiTile
        key="assignment-ratio"
        testId="kpi-tile-assignment-ratio"
        label="Assignment ratio"
        tooltip={<KpiPopover period="Latest available" source="Workday + Envoy/admin (derived)">
          Assigned headcount as a share of usable installed seats across all active markets.
        </KpiPopover>}
        tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
        tooltipPlacement="viewport-clamped"
        descriptor="assigned HC vs usable seats"
        value={assignPct}
        rawValue={portKpis.peakUtilPct ?? undefined}
        formatter={(n) => `${Math.round(n)}%`}
        tileIndex={5}
        color={assignColor}
        height={KPI_TILE_ROW_HEIGHT}
        scope="portfolio"
      />
      {showPlansComplete && <KpiTile
        key="plans-complete"
        testId="kpi-tile-plans-complete"
        label="Plans complete"
        tooltip={<KpiPopover period="Today" source="Ripple market plans">
          Markets in the current view with all four plan fields set: Strategy, Target date, Owner and Note.
        </KpiPopover>}
        tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
        tooltipPlacement="viewport-clamped"
        descriptor="strategy, date, owner, note"
        value={`${countCompletePlans(filteredMarkets)} of ${filteredMarkets.length}`}
        tileIndex={6}
        color={T.dark}
        height={KPI_TILE_ROW_HEIGHT}
        scope="responsive"
        isFiltered={isFiltered}
      />}
    </KpiBand>
  );
}

// ─── Plan StatusBand messages ────────────────────────────────────────────────
// CONTEXT-BANDS-S1: replaces the standalone DecisionsAheadStrip (which used
// to render above the Plan table) with a message fed into the shared
// StatusBand, now positioned in the canonical KPI strip → toolbar →
// StatusBand (matrix view only) → content order. Also folds in a QoQ-trend-unavailable info
// message when the kpi-history snapshot hasn't loaded, so both plan-health
// signals share one row.
//
// MARKET-PLAN-LEADERSHIP-VIEW-S1: "N markets need a decision in next 6 months
// — $X/yr, Y seats at stake" with up to 3 top-$ chips. Omitted entirely when
// selectDecisionsAhead finds no near-term decisions (never a fabricated "0
// markets" message).

// Formats a days-countdown to a compact chip label, e.g. 42 → "42d", 95 → "3 mo".
// Used by usePlanStatusMessages items for the decision-band popover (CONTEXT-BANDS-S2).
function formatCountdown(days) {
  if (!Number.isFinite(days) || days < 0) return null;
  if (days < 90) return `${days}d`;
  return `${Math.round(days / 30)} mo`;
}

// `kpiHistoryLoaded` — gates the "kpi-trend-unavailable" message so it only
// appears once the kpiHistory fetch has settled. The band itself renders
// immediately from synchronous decisions-ahead data (see gate below); this
// prevents a two-state reflow where the info message shows on first paint then
// disappears when kpiHistory resolves. Height is stable in both states because
// warn (decisions-ahead) takes priority over info, and ok/info have identical
// single-line heights.
function usePlanStatusMessages(allMarkets, kpiHistory, kpiHistoryLoaded, { onMarketClick, onViewAllDecisions } = {}) {
  const { canSee } = useSensitivityPolicy();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  const summary = useMemo(() => selectDecisionsAhead(allMarkets), [allMarkets]);

  return useMemo(() => {
    const messages = [];

    if (summary) {
      const dollarsText = summary.totalDollars > 0
        ? `${fmtKpiUsd(summary.totalDollars)}/yr${summary.dollarsPartial ? " (partial)" : ""}`
        : (summary.dollarsPartial ? "cost unknown" : "$0/yr");
      const seatsText = `${summary.totalSeats.toLocaleString()} seat${summary.totalSeats === 1 ? "" : "s"}`;
      messages.push({
        id:       "decisions-ahead",
        type:     "decision",
        severity: "warn",
        text:     `${summary.count} market${summary.count === 1 ? "" : "s"} need${summary.count === 1 ? "s" : ""} a decision in next 6 months - ${canSeeRentCost ? `${dollarsText}, ` : ""}${seatsText} at stake`,
        chips:    summary.topMarkets.map((tm) => (
          canSeeRentCost && tm.annualCost != null ? `${tm.name} · $${Math.round(tm.annualCost).toLocaleString()}` : tm.name
        )),
        // CONTEXT-BANDS-S2: full candidate list for the popover, soonest-first.
        items: (summary.markets ?? []).map((m) => ({
          id:        m.id,
          name:      m.name,
          dollars:   canSeeRentCost ? m.annualCost : undefined,
          countdown: formatCountdown(m.days),
          onClick:   onMarketClick ? () => onMarketClick(m.id) : undefined,
        })),
        onViewAll: onViewAllDecisions
          ? () => onViewAllDecisions((summary.markets ?? []).map((m) => m.id))
          : undefined,
      });
    }

    // Only include once kpiHistory has settled — avoids the initial-state false
    // "unavailable" flash that would reflow if kpiHistory later becomes available.
    if (kpiHistoryLoaded && !kpiHistory?.available) {
      messages.push({
        id:       "kpi-trend-unavailable",
        severity: "info",
        text:     "Quarter-over-quarter trend unavailable - last month's snapshot hasn't been captured yet.",
      });
    }

    return messages;
  }, [summary, kpiHistory, kpiHistoryLoaded, onMarketClick, onViewAllDecisions, canSeeRentCost]);
}

// ─── Flag filter definitions ───────────────────────────────────────────────────
// PLAN-MARKET-POLISH-S2: the old standalone smart-chip row (All markets · Needs
// attention · Lease risk · Capacity pressure · No reader · In build · Exited)
// is gone. "In build" folds into the Status filter group (see
// STATUS_IN_BUILD_VALUE, declared earlier alongside PLAN_FILTER_DIMENSIONS);
// "Exited" was already a Status option. The remaining derived flags become a
// "Flags" group in the shared filter panel. "All markets" is simply the
// no-filter-selected default.


/** Returns true when a market's status matches a selected Status-group value,
 *  including the synthetic "In build" value that isn't a real status field. */
function matchesStatusValue(market, metrics, value) {
  if (value === STATUS_IN_BUILD_VALUE) {
    return market.status !== "Exited" && metrics.hasInBuild;
  }
  return market.status === value;
}

// ─── nextLeaseNote — next-lease-event / capacity-pressure callout text ───────
// Feeds the market drawer's "next lease event" note tile. Returns null when
// the market has no lease-risk or capacity signal worth surfacing.

function nextLeaseNote(metrics) {
  const railColor = marketRailColor(metrics);
  if (railColor !== T.ragRed && railColor !== T.ragAmber) return null;

  const isRed = railColor === T.ragRed;
  const { nextLeaseEventMonths, assignedPct } = metrics;

  let title, body;
  if (isRed && nextLeaseEventMonths !== null && nextLeaseEventMonths < 0) {
    const moAbs = Math.abs(nextLeaseEventMonths);
    title = "Lease event is overdue";
    body  = `A lease event was due ${moAbs} month${moAbs !== 1 ? "s" : ""} ago. Confirm current status and update the plan.`;
  } else if (nextLeaseEventMonths !== null && nextLeaseEventMonths <= 12) {
    title = "Lease event coming up";
    body  = `A lease event is due in ${nextLeaseEventMonths} month${nextLeaseEventMonths !== 1 ? "s" : ""}. Review and confirm the maintain or exit decision.`;
  } else {
    title = "Capacity at pressure";
    body  = assignedPct != null
      ? `This market is at ${assignedPct}% utilisation. Consider right-sizing or additional space.`
      : "Capacity is above threshold. Review headcount and lease options.";
  }

  return { color: railColor, bg: isRed ? T.ragRedBg : T.ragAmberBg, title, body };
}


// ─── MarketPlanPage — main exported page component ────────────────────────────

// PLAN-UNASSIGNED-LOCATIONS: parse the new { markets, unassignedLocations } envelope
// (backward-compat: if server returns a bare array treat it as markets only).
function parsePlanResponse(data) {
  if (Array.isArray(data)) return { markets: data, unassigned: [] };
  return {
    markets:    Array.isArray(data?.markets)             ? data.markets             : [],
    unassigned: Array.isArray(data?.unassignedLocations)  ? data.unassignedLocations : [],
  };
}

export function MarketPlanPage({ planData: planDataProp = [], currentRole = "viewer", onOpenLocation, onPlanRefreshed, deepLinkLocationId = null, deepLinkMarketId = null, onDeepLinkHandled, onStartWorkflow, onOpenLinkedRun, placesLens = null }) {
  const { canSee } = useSensitivityPolicy();
  const { flags, flagsReady } = useFeatureFlagContext();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  const canViewMarketsStrategy = flagsReady &&
    flags[FEATURE_MARKETS_STRATEGY_TABS] === true &&
    canAccessMarketsStrategy(currentRole);
  // ── Persisted UI preferences (survive tab close / browser restart) ──────────
  // clearPlanPrefs unused — Plan Clear resets only filters/search/sort.
  const [planPrefs, setPlanPrefs] = useUiPrefs(UI_PREFS_PLAN_V1, PLAN_PREFS_DEFAULT);

  // ── Active tab — driven by the URL path segment (MARKET-PLAN-TAB-IN-URL) ───
  // Route: /markets/:tab  (AppShell uses path="/markets/*")
  // useParams()["*"] gives the tail segment after "/markets/".
  // Unknown segments fall back to "plan" so /markets/bogus still works.
  const navigate = useNavigate();
  const { "*": tabSegment } = useParams();
  const location = useLocation();
  const routeTabSegment = ({ markets: "plan", pressure: "pressure", scenario: "scenario" })[placesLens]
    ?? tabSegment ?? location.pathname?.match(/^\/markets\/([^/]+)/)?.[1] ?? null;
  const requestedPlanView = TAB_SEGMENT_TO_VIEW[routeTabSegment] ?? "plan";
  const planView = canonicalizeMarketsTab(
    requestedPlanView,
    currentRole,
    canViewMarketsStrategy,
  );

  // OBJECT-TABS-PERSIST-LAST-SELECTION: on mount, when the URL segment is absent
  // or falls back to the default "plan" view, restore the last-remembered tab.
  // Only fires once; a real URL segment (e.g. /markets/pressure) always wins.
  const planTabMemoryApplied = useRef(false);
  useEffect(() => {
    const isRestrictedTab = ["strategy", "recommendations"].includes(requestedPlanView);
    if (isRestrictedTab) {
      planTabMemoryApplied.current = true;
      // Render Plan while the saved setting loads, but do not discard a valid
      // deep link before we know whether the tab is enabled.
      if (!flagsReady) return;
      if (canViewMarketsStrategy) return;
      navigate(buildMarketPlanUrl("plan") + location.search + (location.hash ?? ""), { replace: true });
      return;
    }
    if (planTabMemoryApplied.current) return;
    // Wait for the persisted feature flag before restoring a remembered tab.
    // The page itself remains on Plan until the public flag response is ready.
    if (!routeTabSegment && !flagsReady) return;
    planTabMemoryApplied.current = true;
    // If the user arrived via a meaningful URL segment, honour it (no redirect).
    if (routeTabSegment && TAB_SEGMENT_TO_VIEW[routeTabSegment]) return;
    // No segment (or unrecognised) — check memory.
    const remembered = canonicalizeMarketsTab(
      loadLastTab("plan"),
      currentRole,
      canViewMarketsStrategy,
    );
    if (remembered && buildMarketPlanUrl(remembered) !== buildMarketPlanUrl("plan")) {
      navigate(buildMarketPlanUrl(remembered) + location.search + (location.hash ?? ""), { replace: true });
    }
  }, [
    canViewMarketsStrategy,
    currentRole,
    flagsReady,
    location.search,
    location.hash,
    navigate,
    requestedPlanView,
    routeTabSegment,
  ]);

  // Tab switches replace the current history entry so the browser Back button
  // exits Market Plan entirely rather than cycling through previously-visited tabs.
  // Existing query params (?market=, ?search=, etc.) are preserved so that
  // display-control state (open drawer, active filters) survives a tab change.
  const setPlanView = useCallback((v) => {
    const next = canonicalizeMarketsTab(
      typeof v === "function" ? v(planView) : v,
      currentRole,
      canViewMarketsStrategy,
    );
    // OBJECT-TABS-PERSIST-LAST-SELECTION: persist before navigating.
    saveLastTab("plan", next);
    navigate(buildMarketPlanUrl(next) + location.search + (location.hash ?? ""), { replace: true });
  }, [canViewMarketsStrategy, currentRole, navigate, planView, location.search, location.hash]);

  // State for the KPI strip hoisted above the shell toolbar when market_pressure
  // view is active.  Populated by MarketPressurePage via onKpisChange callback.
  const [mpKpisData, setMpKpisData] = useState({ kpis: {}, loading: true, horizon: 18, basis: "usable", layer: "as-committed", marketsCount: 0, alertMessages: [], quarters: [] });

  // Isolated sort state for the Pressure view — ephemeral (reset on tab change
  // is fine because the period columns themselves reset with the horizon toggle).
  const [pressureSortKey, setPressureSortKey] = useState("market");
  const [pressureOptionsTarget, setPressureOptionsTarget] = useState(null);
  const [pressureKeyTarget, setPressureKeyTarget] = useState(null);
  const [pressureCapacityTarget, setPressureCapacityTarget] = useState(null);
  const pressureToolbarRef = useRef(null);
  const [collapsePressureCapacity, setCollapsePressureCapacity] = useState(false);
  useEffect(() => {
    const node = pressureToolbarRef.current;
    if (planView !== "market_pressure" || !node) return undefined;
    // Measure the actual content area, including the expanded navigation rail.
    const measure = () => {
      const width = node.getBoundingClientRect().width;
      if (width > 0) setCollapsePressureCapacity(width < 1120);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [planView]);
  const [pressureSortDir, setPressureSortDir] = useState("asc");
  const [pressureCounts, setPressureCounts] = useState({ count: 0, total: 0 });
  const secondaryCollection = ["markets", "pressure", "scenario"].includes(placesLens);

  // Derive Pressure sort fields from the live quarter list surfaced by
  // MarketPressurePage via onKpisChange.  Recomputes only when quarters change.
  const pressureSortFields = useMemo(() => {
    const quarterEntries = (mpKpisData.quarters ?? []).map((q) => ({
      value:   `q_${q.label}`,
      label:   q.label,
      numeric: true,
    }));
    return [{ value: "market", label: "Market name" }, ...quarterEntries];
  }, [mpKpisData.quarters]);

  // When the quarter columns change (e.g. horizon toggle), reset the sort key
  // to "market" if the current key no longer matches any available field.
  useEffect(() => {
    const validKeys = new Set(pressureSortFields.map((f) => f.value));
    if (!validKeys.has(pressureSortKey)) {
      setPressureSortKey("market");
      setPressureSortDir("asc");
    }
  }, [pressureSortFields, pressureSortKey]);

  const [planData,           setPlanData]           = useState(planDataProp);
  useRecordPhotoRefresh(async () => {
    try {
      const { markets: fresh } = parsePlanResponse(await getPlan());
      const covers = new Map(fresh.map(row => [row.id, row]));
      setPlanData(previous => previous.map(row => {
        const next = covers.get(row.id);
        if (!next) return row;
        const offices = new Map((next.locations || []).map(office => [office.locationId, office]));
        return { ...row, imageUrl: next.imageUrl, photoUrl: next.photoUrl,
          locations: (row.locations || []).map(office => offices.has(office.locationId) ? { ...office, imageUrl: offices.get(office.locationId).imageUrl } : office) };
      }));
    } catch (error) {
      log.warn("Market photo refresh failed", { message: error.message });
    }
  });
  const [unassignedLocations, setUnassignedLocations] = useState([]);
  // MARKET-PLAN-LEADERSHIP-VIEW-S1: prior-month KPI snapshot for the KPI
  // strip's QoQ deltas. `available: false` (the pre-fetch default) means
  // "no history yet" — KpiStrip must render no deltas in that case, never a
  // fabricated "no change".
  const [kpiHistory, setKpiHistory] = useState({ available: false, month: null, kpis: null });
  // Becomes true once the kpiHistory fetch settles (success or error) so the
  // StatusBand only renders in its final state — never reflows when the fetch
  // completes after plan data has already loaded.
  const [kpiHistoryLoaded, setKpiHistoryLoaded] = useState(false);
  const [loading,            setLoading]             = useState(planDataProp.length === 0);
  const [fetchError,    setFetchError]    = useState(null);
  // ── Filter state — persisted via planPrefs.cfSelections ────────────────────
  // Thin aliases kept so the existing JSX (ConsolidatedFilter, filteredPlan,
  // handleClearPlanFilters) doesn't need touching.
  const cfs = useMemo(() => legacyMarketSelections({ cfSelections: planPrefs.cfSelections, filterVersion: planPrefs.filterVersion }),
    [planPrefs.cfSelections, planPrefs.filterVersion]);
  const collectionSelections = useMemo(() => marketCheckedSelections({ cfSelections: planPrefs.cfSelections, filterVersion: planPrefs.filterVersion }),
    [planPrefs.cfSelections, planPrefs.filterVersion]);
  const strategyFilter = cfs.strategy ?? [];
  const regionFilter   = cfs.region   ?? [];
  const statusFilter   = cfs.status   ?? [];
  const hubFilter      = cfs.hub      ?? [];
  const setCfs = (patch) => setPlanPrefs((s) => ({
    ...s,
    cfSelections: { ...legacyMarketSelections(s), ...patch },
    filterVersion: undefined,
  }));
  const setStrategyFilter = (v) => setCfs({ strategy: typeof v === "function" ? v(strategyFilter) : v });
  const setRegionFilter   = (v) => setCfs({ region:   typeof v === "function" ? v(regionFilter)   : v });
  const setStatusFilter   = (v) => setCfs({ status:   typeof v === "function" ? v(statusFilter)   : v });
  const setHubFilter      = (v) => setCfs({ hub:      typeof v === "function" ? v(hubFilter)      : v });

  const planShowAll    = planPrefs.showAll ?? false;
  const setPlanShowAll = (v) => setPlanPrefs((s) => ({ ...s, showAll: typeof v === "function" ? v(s.showAll) : v }));

  // ── Search — persisted via planPrefs.search ──────────────────────────────────
  const search    = planPrefs.search ?? "";
  const setSearch = (v) => setPlanPrefs((s) => ({ ...s, search: typeof v === "function" ? v(s.search) : v }));

  // ── Strategy tab search — local state, not persisted ─────────────────────────
  const [strategySearch, setStrategySearch] = useState("");

  // CONTEXT-BANDS-S2: "View all" from the decision-band popover sets this to
  // the list of market IDs that need a decision, narrowing the visible list.
  // Ephemeral — not persisted (it's driven by the popover, not a user choice).
  const [decisionFilter, setDecisionFilter] = useState([]);

  // Which market rows are expanded. Sole source of truth for the Expand-all
  // switch; no effect resets this in response to view or filter changes.
  const [planExpandedIds, setPlanExpandedIds] = useState(new Set());

  // Deep-link: ?expandMarket=<id> expands that market row once on mount.
  const expandOnMountRef = useRef(false);
  useEffect(() => {
    if (expandOnMountRef.current) return;
    expandOnMountRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const expandId = params.get("expandMarket");
    if (expandId) {
      setPlanExpandedIds((prev) => new Set([...prev, expandId]));
      setScrollTargetId(expandId);
    }
  }, []);
  // MARKET-PLAN-EXPAND-ALL-USE-TARGET-STATE: derive switch ON/OFF from whether
  // any row is currently open. Per-row carets mutate planExpandedIds independently;
  // the switch reflects reality rather than tracking its own last action.
  const anyPlanExpanded = planExpandedIds.size > 0;
  // Set only when an "In Focus" tile expands a row, so the matrix knows to
  // scroll it into view + highlight it; cleared once the row handles it.
  const [scrollTargetId, setScrollTargetId] = useState(null);

  const planFilterDimensions = useMemo(() => buildPlanFilterDimensions(), []);

  // ── PLACES-SLICE-2: Markets lens List / Tiles / Map ─────────────────────
  // One shared population (sortedMatrixMarkets: search + filters + sort) feeds
  // all three renderings; the KPI band is unchanged across them. The chosen
  // view persists in planPrefs.marketsView and may be deep-linked via ?view=.
  const isMarketsLens = placesLens === "markets";
  const [drawerCapacity, reloadCapacitySnapshot] = useCapacitySnapshot(isMarketsLens);
  const [drawerPolicyBuffer, setDrawerPolicyBuffer] = useState(null);
  const [drawerPolicyPending, setDrawerPolicyPending] = useState(true);
  const drawerPolicySequence = useRef(0);
  const drawerPolicySettled = useRef(false);
  const [drawerCapacityNote, setDrawerCapacityNote] = useState(null);
  const urlMarketsView = new URLSearchParams(location.search).get("view");
  const marketsView = isMarketsLens
    ? (["list", "tiles", "map"].includes(urlMarketsView) ? urlMarketsView
      : (["list", "tiles", "map"].includes(planPrefs.marketsView) ? planPrefs.marketsView : "list"))
    : "list";
  const setMarketsView = (next) => {
    setPlanPrefs((s) => ({ ...s, marketsView: next }));
    const params = new URLSearchParams(location.search);
    if (next === "list") params.delete("view"); else params.set("view", next);
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}${location.hash ?? ""}`, { replace: true, state: location.state });
  };
  // Drawer membership also reads the canonical inventory (full membership; the plan payload can omit offices).
  const inventory = useOfficeInventory(isMarketsLens);

  // ── Density — CSS-only row-height toggle, independent of expand state ────────
  // "compact" shrinks row padding and hides the composition sub-line via the
  // existing isCompact rendering in PlanMatrixRow.  Stored in planPrefs so the
  // preference survives navigation, but it never touches planExpandedIds —
  // toggling Compact while rows are expanded must NOT collapse them.
  const planDensity    = planPrefs.density ?? "comfortable";
  const toggleDensity  = () => setPlanPrefs((s) => ({
    ...s,
    density: (s.density ?? "comfortable") === "compact" ? "comfortable" : "compact",
  }));

  // ── Sort state — persisted via planPrefs ─────────────────────────────────────
  // Thin aliases so the existing JSX doesn't need to change.
  // Validate stored key against known sort keys; fall back to default if stale/invalid.
  // When key is invalid, also reset direction to default so the two stay consistent.
  const allowedSortKeys = (isMarketsLens ? MARKETS_SORT_FIELDS : PLAN_SORT_FIELDS)
    .filter(field => canSeeRentCost || field.value !== "cost").map(field => field.value);
  const _storedSortKeyValid = allowedSortKeys.includes(planPrefs.sortKey);
  const planSortKey  = _storedSortKeyValid ? planPrefs.sortKey : DEFAULT_SORT_KEY;
  const planSortDir  = _storedSortKeyValid
    ? ((planPrefs.sortDir === "asc" || planPrefs.sortDir === "desc") ? planPrefs.sortDir : DEFAULT_SORT_DIR)
    : DEFAULT_SORT_DIR;
  const planSortKey2 = allowedSortKeys.includes(planPrefs.sortKey2) ? planPrefs.sortKey2 : null;
  const planSortDir2 = planPrefs.sortDir2  ?? "asc";
  const setPlanSortKey  = (v) => setPlanPrefs((s) => ({ ...s, sortKey:  typeof v === "function" ? v(s.sortKey)  : v }));
  const setPlanSortDir  = (v) => setPlanPrefs((s) => ({ ...s, sortDir:  typeof v === "function" ? v(s.sortDir)  : v }));
  const setPlanSortKey2 = (v) => setPlanPrefs((s) => ({ ...s, sortKey2: typeof v === "function" ? v(s.sortKey2) : v }));
  const setPlanSortDir2 = (v) => setPlanPrefs((s) => ({ ...s, sortDir2: typeof v === "function" ? v(s.sortDir2) : v }));
  const userHasSetSort  = planSortKey !== DEFAULT_SORT_KEY || planSortDir !== DEFAULT_SORT_DIR || !!planSortKey2;
  const setUserHasSetSort = () => {}; // no-op — derived from prefs

  const hasNonDefaultSort = planSortKey !== DEFAULT_SORT_KEY || planSortDir !== DEFAULT_SORT_DIR
    || !!planSortKey2;

  const hasActiveFilters = (isMarketsLens
    ? activeFilterGroups(MARKET_FILTER_DIMENSIONS, collectionSelections, MARKET_FILTER_DEFAULTS, true).length > 0
    : strategyFilter.length > 0 || regionFilter.length > 0 || statusFilter.length > 0 || hubFilter.length > 0)
    || decisionFilter.length > 0
    || planShowAll
    || (secondaryCollection
      ? (planView === "market_pressure"
        ? pressureSortKey !== "market" || pressureSortDir !== "asc"
        : hasNonDefaultSort)
      : search.trim() !== "" || hasNonDefaultSort);

  function handleClearPlanFilters() {
    setPlanPrefs((s) => ({
      ...s,
      cfSelections: {},
      filterVersion: undefined,
      search:       secondaryCollection ? s.search : "",
      showAll:      false,
      sortKey:      DEFAULT_SORT_KEY,
      sortDir:      DEFAULT_SORT_DIR,
      sortKey2:     null,
      sortDir2:     "asc",
    }));
    setDecisionFilter([]);
    if (secondaryCollection && planView === "market_pressure") {
      setPressureSortKey("market");
      setPressureSortDir("asc");
    }
  }

  const handleSort = useCallback((key) => {
    if (key === planSortKey) {
      setPlanPrefs((s) => ({ ...s, sortDir: s.sortDir === "asc" ? "desc" : "asc" }));
    } else {
      setPlanPrefs((s) => ({
        ...s,
        sortKey: key,
        // First click on a numeric column defaults to descending — biggest/soonest first.
        // Non-numeric columns (Market name, Strategy) default to ascending.
        sortDir: NUMERIC_SORT_KEYS.includes(key) ? "desc" : "asc",
      }));
    }
  }, [planSortKey, setPlanPrefs]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = currentRole === "editor" || currentRole === "admin";

  // Fetch data if not supplied by App shell
  useEffect(() => {
    if (planDataProp.length > 0) {
      setPlanData(planDataProp);
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchPlan() {
      try {
        const data = await getPlan();
        if (!cancelled) {
          const { markets, unassigned } = parsePlanResponse(data);
          setPlanData(markets);
          setUnassignedLocations(unassigned);
        }
      } catch (err) {
        log.error("MarketPlanPage: fetch failed", { err: String(err) });
        if (!cancelled) setFetchError(err.message ?? "Failed to load plan");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchPlan();
    return () => { cancelled = true; };
  }, []);

  // MARKET-PLAN-LEADERSHIP-VIEW-S1: fetch the QoQ comparison snapshot once.
  // Failure/absence is silent — the KPI strip simply omits deltas rather
  // than surfacing a fetch error for a non-critical enhancement.
  // Deferred with setTimeout(0) so the primary plan fetch and first paint
  // complete before this secondary fetch competes for DB connections.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await getPlanKpiHistory();
        if (!cancelled && data) setKpiHistory(data);
      } catch (err) {
        log.error("MarketPlanPage: kpi-history fetch failed", { err: String(err) });
      } finally {
        // Mark loaded regardless of outcome so the StatusBand renders in its
        // settled state (no second paint when the fetch completes).
        if (!cancelled) setKpiHistoryLoaded(true);
      }
    }, 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  // Sync when App shell re-pushes planData
  useEffect(() => {
    if (planDataProp.length > 0) {
      setPlanData(planDataProp);
      setLoading(false);
    }
  }, [planDataProp]);

  // SPACE-PIPELINE-PANE-REFINE-2: when navigated here via a pipeline row
  // deep-link, find the parent market for the given locationId, expand it,
  // and scroll to it.  Runs at most once per mount (deepLinkHandledRef guard)
  // so re-renders of planData don't re-trigger the scroll.
  const deepLinkHandledRef = useRef(false);
  const onDeepLinkHandledRef = useRef(onDeepLinkHandled);
  useEffect(() => { onDeepLinkHandledRef.current = onDeepLinkHandled; }, [onDeepLinkHandled]);
  useEffect(() => {
    if (!deepLinkLocationId || deepLinkHandledRef.current || planData.length === 0) return;
    const parentMarket = planData.find((m) =>
      (m.locations ?? []).some((l) => l.locationId === deepLinkLocationId)
    );
    if (!parentMarket) return;
    deepLinkHandledRef.current = true;
    setPlanExpandedIds((prev) => new Set([...prev, parentMarket.id]));
    setScrollTargetId(parentMarket.id);
    onDeepLinkHandledRef.current?.();
  }, [deepLinkLocationId, planData]);

  // WORKFLOWS-SLICE-2-LAUNCH-FROM-OBJECT: run-detail "Started from <market>"
  // deep link — expand + scroll straight to the market row by its own id.
  const marketDeepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (!deepLinkMarketId || marketDeepLinkHandledRef.current || planData.length === 0) return;
    const market = planData.find((m) => m.id === deepLinkMarketId);
    if (!market) return;
    marketDeepLinkHandledRef.current = true;
    setPlanExpandedIds((prev) => new Set([...prev, market.id]));
    setScrollTargetId(market.id);
    onDeepLinkHandledRef.current?.();
  }, [deepLinkMarketId, planData]);

  // MARKETS-S3: modal state
  const [showCreateMarket, setShowCreateMarket] = useState(false);
  const [deletingMarket,   setDeletingMarket]   = useState(null);
  const [movingLocation,   setMovingLocation]   = useState(null);


  // ── Strategic directives (PLAN-STRATEGY-BAND-S1) ─────────────────────────
  const [directives,     setDirectives]     = useState([]);
  const [drawerDirective, setDrawerDirective] = useState(null); // null=closed; {}=new; obj=edit

  // ── Capacity policy baseline (MARKET-PLAN-POLICY-BAND) ────────────────────
  const [policyBaseline,  setPolicyBaseline]  = useState(null);
  const [overrideCount,   setOverrideCount]   = useState(0);

  // Deferred with setTimeout(0) so directives and policy baseline fetches
  // yield to the primary plan fetch and first paint before starting.
  useEffect(() => {
    if (!canViewMarketsStrategy) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      getDirectives()
        .then((data) => { if (!cancelled) setDirectives(Array.isArray(data) ? data : []); })
        .catch((err) => { log.error("MarketPlanPage: directives fetch failed", { err: String(err) }); });
      getPolicyBaseline()
        .then((data) => {
          if (!cancelled) {
            // /api/plan/policy returns { baseline, overrideCount }; unwrap before storing.
            setPolicyBaseline(data?.baseline ?? null);
            setOverrideCount(data?.overrideCount ?? 0);
          }
        })
        .catch((err) => { log.error("MarketPlanPage: policy baseline fetch failed", { err: String(err) }); });
    }, 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [canViewMarketsStrategy]);

  // Refresh plan from server after any mutation.
  // Also calls onPlanRefreshed so App.jsx can keep its pre-fetched copy in sync,
  // which prevents stale data from appearing when the user navigates away and back.
  const refreshPlan = useCallback(async () => {
    try {
      const data = await getPlan();
      const { markets: next, unassigned } = parsePlanResponse(data);
      setPlanData(next);
      setUnassignedLocations(unassigned);
      onPlanRefreshed?.(next);
    } catch (err) {
      log.error("MarketPlanPage: refreshPlan failed", { err: String(err) });
    }
  }, [onPlanRefreshed]);

  // MARKETPLAN-CACHE-INVALIDATE: re-fetch plan when any location write fires.
  //
  // Every write in locationsClient.js (alias, capacity, stage, headcount,
  // disposition, photo, capacity schedule, headcount schedule, …) calls
  // invalidatePortfolioCaches() which now fans out to this subscriber.
  // That means an alias or capacity edit made from the location-detail drawer
  // while this page is visible will reflect in the plan matrix within ~1 second
  // (one getPlan() round-trip) — no hard reload needed.
  //
  // The inflight ref prevents a second fetch from queuing while one is already
  // in flight (e.g. two rapid writes back-to-back).
  const planRefetchInFlightRef = useRef(false);
  useEffect(() => {
    function handleInvalidation() {
      if (planRefetchInFlightRef.current) return;
      planRefetchInFlightRef.current = true;
      refreshPlan().finally(() => { planRefetchInFlightRef.current = false; });
    }
    subscribePlanInvalidation(handleInvalidation);
    return () => unsubscribePlanInvalidation(handleInvalidation);
  }, [refreshPlan]);

  const handleDeleteMarket = useCallback((market) => {
    setDeletingMarket(market);
  }, []);

  const handleConfirmDelete = useCallback(async (market) => {
    await deleteMarket(market.id, market);
    await refreshPlan();
  }, [refreshPlan]);

  const handleMoveOffice = useCallback((location) => {
    setMovingLocation(location);
  }, []);

  const handleConfirmMove = useCallback(async (locationId, targetMarketId, before) => {
    await reassignLocationMarket(locationId, targetMarketId, before);
    const targetMarket = planData.find((m) => m.id === targetMarketId);
    const alias      = before?.locationAlias || locationId;
    const marketName = targetMarket?.name ?? targetMarketId;
    const verb = before?.marketId == null ? "assigned to" : "moved to";
    showToast(`${alias} ${verb} ${marketName}`, { type: "success" });
    await refreshPlan();
  }, [refreshPlan, planData]);

  // Handle save: update local cache so the row refreshes without a full refetch.
  // PLAN-MARKET-EXITED-STATE-S1: deactivate/reactivate calls onSaved with an
  // empty `patch` (only `strategy`/`strategyTargetDate`/`owner`/`planNote`
  // live there) but a real `active` value on `_serverResponse` — merge that
  // in too, or a reactivated market never re-enables editing/reappears in
  // the default (Exited-excluded) list until a full refetch.
  const handleMarketSaved = useCallback(async (marketId, serverResponse, patch) => {
    setPlanData((prev) =>
      prev.map((m) =>
        m.id !== marketId
          ? m
          : {
              ...m,
              strategy:           patch.strategy           !== undefined ? patch.strategy           : m.strategy,
              strategyTargetDate: patch.strategyTargetDate !== undefined ? patch.strategyTargetDate : m.strategyTargetDate,
              owner:              patch.owner              !== undefined ? patch.owner              : m.owner,
              planNote:           patch.planNote           !== undefined ? patch.planNote           : m.planNote,
              active:             serverResponse?.active   !== undefined ? serverResponse.active     : m.active,
              status:             serverResponse?.status   !== undefined ? serverResponse.status     : m.status,
            }
      )
    );
    await refreshPlan();
  }, [refreshPlan]);

  // PLAN-OFFICE-DRAWER-PERSIST-FIX: office-scope save must patch the specific
  // location inside `planData`, never the market record — office and market
  // patches share field names (`planNote`), so routing an office save through
  // `handleMarketSaved` used to silently clobber the market's own plan note.
  // Updating `planData[market].locations[]` here also fixes the market's
  // office plan fields shown in the offices table and market rows, which
  // otherwise only reflected instant-refresh via the row-local overrides
  // used just for the offices table.
  const handleOfficeSaved = useCallback(async (marketId, locationId, serverResponse, patch) => {
    setPlanData((prev) =>
      prev.map((m) => {
        if (m.id !== marketId) return m;
        return {
          ...m,
          locations: (m.locations ?? []).map((l) => {
            if (l.locationId !== locationId) return l;
            return {
              ...l,
              disposition:      patch.disposition      !== undefined ? patch.disposition      : l.disposition,
              exitReason:       patch.exitReason        !== undefined ? patch.exitReason        : l.exitReason,
              targetActionDate: patch.targetActionDate !== undefined ? patch.targetActionDate : l.targetActionDate,
              planNote:         patch.planNote          !== undefined ? patch.planNote          : l.planNote,
              planHealth:       serverResponse?.planHealth ?? l.planHealth,
              planSetAt:        serverResponse?.planSetAt  ?? l.planSetAt,
            };
          }),
        };
      })
    );
    // Plan Coverage pillar depends on disposition + targetActionDate. Notify
    // PortfolioCommandTile / PortfolioCommandPage so they re-fetch the index.
    if (patch.disposition !== undefined || patch.targetActionDate !== undefined) {
      emitCommandIndexStale();
    }
    // Sync App.jsx's in-memory planDataProp cache so that navigating away and
    // back does not overwrite local state with the stale pre-save copy (the
    // useEffect([planDataProp]) at mount time would otherwise restore old values).
    await refreshPlan();
  }, [refreshPlan]);

  // Count Exited markets for the toggle label.
  const exitedCount = useMemo(
    () => planData.filter((m) => m.status === "Exited").length,
    [planData]
  );

  // Client-side filter. Dimension filters (strategy/region/status) and
  // the shared search box are all applied together — see buildPlanFilterDimensions
  // and matchesStatusValue for how "In build"/"Exited" (status) work.
  // When planShowAll is ON, all rows are returned with no filtering applied.
  const filteredMarkets = useMemo(() => {
    if (planShowAll) return planData;
    const q = search.trim();
    const candidates = isMarketsLens ? applyMarketCollectionFilters(planData, collectionSelections) : planData;
    return candidates.filter((m) => {
      if (q) {
        const nameMatch = matchesFoldedQuery(q, m.name ?? m.id);
        const locMatch = nameMatch || (m.locations ?? []).some(
          (loc) => matchesFoldedQuery(q, loc.locationAlias ?? loc.id)
        );
        if (!locMatch) return false;
      }
      if (decisionFilter.length > 0 && !decisionFilter.includes(m.id)) return false;
      if (isMarketsLens) return true;
      if (strategyFilter.length > 0 && !strategyFilter.includes(m.strategy)) return false;
      if (regionFilter.length   > 0 && !regionFilter.includes(m.region))     return false;
      const mx = computeMarketMetrics(m);
      if (statusFilter.length > 0) {
        if (!statusFilter.some((v) => matchesStatusValue(m, mx, v))) return false;
      } else if (m.status === "Exited") {
        // Default view hides Exited markets unless the user explicitly
        // checks "Exited" in the Status filter.
        return false;
      }
      if (hubFilter.length > 0) {
        const matchesAnyHub = hubFilter.some((v) =>
          v === HUB_FILTER_HUB ? mx.hasHub : !mx.hasHub
        );
        if (!matchesAnyHub) return false;
      }
      return true;
    });
  }, [planData, planShowAll, decisionFilter, strategyFilter, regionFilter, statusFilter, hubFilter, search, isMarketsLens, collectionSelections]);

  const countedMarketDimensions = useMemo(() => withFacetCounts(
    MARKET_FILTER_DIMENSIONS, collectionSelections,
    planShowAll ? planData : planData.filter(m => {
      const q = search.trim();
      return (!decisionFilter.length || decisionFilter.includes(m.id))
        && (!q || matchesFoldedQuery(q, m.name ?? m.id)
          || (m.locations ?? []).some(loc => matchesFoldedQuery(q, loc.locationAlias ?? loc.id)));
    }),
    (rows, selected, countGroup) => planShowAll ? rows : applyMarketCollectionFilters(rows, selected, countGroup),
  ), [planFilterDimensions, collectionSelections, planData, search, decisionFilter, planShowAll]);

  const currentLadders = useMemo(() => currentMarketLadders(drawerCapacity, new Date(), drawerPolicyBuffer ?? undefined),
    [drawerCapacity, drawerPolicyBuffer]);
  const collectionMarketsById = useMemo(() => new Map(isMarketsLens ? planData.map(m => [m.id, {
    ...m, capacityLadder: ladderForMarket(currentLadders, m.id),
    capacitySummary: marketCapacitySummary(drawerCapacity, m.id),
    assignmentToday: marketAssignment(inventory, m.id),
    nextRecordDate: selectMarketNextDate(m),
  }]) : []), [isMarketsLens, planData, currentLadders, drawerCapacity, inventory.locations, inventory.loading, inventory.error]);
  const collectionMarkets = useMemo(() => isMarketsLens
    ? filteredMarkets.map(m => collectionMarketsById.get(m.id)) : filteredMarkets,
  [isMarketsLens, filteredMarkets, collectionMarketsById]);
  const listSummary = useMemo(() => {
    if (!isMarketsLens) return null;
    const summary = summarizeMarketsList(collectionMarkets);
    const winner = collectionMarkets.find(m => m.nextRecordDate?.next === summary.next);
    return { ...summary, capacityReadStatus: currentLadders.status, month: currentLadders.month, policyNote: drawerCapacityNote,
      dateDisclosure: winner ? marketDateDisclosure(winner.nextRecordDate) : summary.incompleteDates ? "Lease dates unavailable or need source review." : "No upcoming lease dates." };
  }, [isMarketsLens, collectionMarkets, currentLadders.month, currentLadders.status, drawerCapacityNote]);

  // Mirrors NextDecisionCell: reuses the same Leases-style next-action derivation.
  function getSortNextDecision(m) {
    const decision = deriveMarketNextDecision(m);
    return decision ? decision.days : Infinity;
  }

  function getSortSeats(m) {
    if (isMarketsLens || m.assignmentToday) {
      const assigned = m.assignmentToday?.assigned?.value;
      const seats = m.assignmentToday?.seats?.value;
      return assigned != null && seats > 0 ? assigned / seats * 100 : Infinity;
    }
    if (m.avgUtilPct != null) return m.avgUtilPct;
    if (m.totalHeadcount != null && m.totalSeats != null && m.totalSeats > 0) {
      return Math.round(m.totalHeadcount / m.totalSeats * 100);
    }
    return Infinity;
  }

  function getSortStrategyIdx(m) {
    const idx = STRATEGY_ORDER.indexOf(m.strategy);
    return idx === -1 ? Infinity : idx;
  }

  // Shared value-extractor for every sortable field — used by both the
  // primary (column header / "Sort by") and secondary ("Then by") keys.
  function getSortValue(key, m) {
    switch (key) {
      case "capacity":
      case "nextDate":      return isMarketsLens ? marketsListSortValue(key, m) ?? Infinity : Infinity;
      case "market":        return (m.name ?? "").toLowerCase();
      case "strategy":      return getSortStrategyIdx(m);
      case "nextDecision":  return getSortNextDecision(m);
      case "people":        return isMarketsLens || m.assignmentToday ? m.assignmentToday?.assigned?.value ?? Infinity : m.totalHeadcount ?? Infinity;
      case "seatsCapacity": return isMarketsLens || m.assignmentToday ? m.assignmentToday?.seats?.value ?? Infinity : m.totalSeats ?? Infinity;
      case "seats":         return getSortSeats(m);
      case "headroom":      return m.totalHeadroom ?? Infinity;
      case "cost":          return canSeeRentCost ? (isMarketsLens ? marketsListSortValue("cost", m) ?? Infinity : m.totalAnnualCost ?? Infinity) : Infinity;
      case "completeness":  return computeMarketCompleteness(m).progressPct;
      default:              return null;
    }
  }

  // Compares two already-extracted values honoring the Infinity/no-data
  // sentinel convention (always sorts last, regardless of direction).
  function compareSortValues(va, vb, dir) {
    if (va === Infinity && vb === Infinity) return 0;
    if (va === Infinity) return 1;
    if (vb === Infinity) return -1;
    return dir * (va < vb ? -1 : va > vb ? 1 : 0);
  }

  // Matrix sort — applied on top of the filtered list; Board uses the unsorted filtered list.
  // Primary key/dir come from either a column header click or the Sort tool's
  // "Sort by"; the Sort tool's "Then by" key/dir (planSortKey2/planSortDir2)
  // is used only as a tiebreaker when the primary values are equal.
  const sortedMatrixMarkets = useMemo(() => {
    const dir  = planSortDir  === "asc" ? 1 : -1;
    const dir2 = planSortDir2 === "asc" ? 1 : -1;

    return [...collectionMarkets].sort((a, b) => {
      if (!planSortKey) return 0;
      const primary = compareSortValues(getSortValue(planSortKey, a), getSortValue(planSortKey, b), dir);
      const secondary = primary === 0 && planSortKey2 ? compareSortValues(getSortValue(planSortKey2, a), getSortValue(planSortKey2, b), dir2) : 0;
      return primary || secondary || (isMarketsLens
        ? String(a.name || a.market || "").localeCompare(String(b.name || b.market || ""), undefined, { sensitivity: "base" })
          || String(a.id).localeCompare(String(b.id))
        : 0);
    });
  }, [collectionMarkets, planSortKey, planSortDir, planSortKey2, planSortDir2, isMarketsLens, canSeeRentCost]);

  // MARKET-PLAN-EXPAND-ALL-OFF-SNAP-BACK: keep a ref in sync so
  // handleExpandCollapseAll can read the live sorted list without it appearing
  // in the callback's dep array (which would recreate the callback whenever
  // filters/sort change, compounding the stale-closure risk).
  const sortedMatrixMarketsRef = useRef(sortedMatrixMarkets);
  useEffect(() => { sortedMatrixMarketsRef.current = sortedMatrixMarkets; }, [sortedMatrixMarkets]);

  // MARKET-PLAN-PURGE-TESTS-AND-RECONCILE-COUNT-S1: canonical active-market
  // set shared by every view's portfolio KPI computation (Plan's KpiStrip,
  // What-If's WhatIfKpiStrip, Timeline's heatmap) — independent of the
  // toolbar's search/strategy/region/status/flags/hub filters.
  const canonicalActiveMarkets = useMemo(
    () => selectActiveMarketsForKpis(planData),
    [planData]
  );

  // KPI-SCOPE-INDICATOR (G-22): true when any toolbar search or filter is
  // active (not counting "show all", which broadens rather than narrows the
  // view). Used by KpiStrip to toggle the responsive tile blue highlight.
  const isPlanFiltered =
    search.trim() !== "" ||
    strategyFilter.length  > 0 ||
    regionFilter.length    > 0 ||
    statusFilter.length    > 0 ||
    hubFilter.length       > 0 ||
    decisionFilter.length  > 0;  // "View all <band>" action also narrows the list

  // KPI-SCOPE-INDICATOR (G-22): "Next wall" — nearest lease-event quarter
  // across the currently-filtered market list, consumed by KpiStrip.
  // The quarterFromDate helper is local
  // to this file so this is computed here rather than inside the child strips.
  const filteredNextWallQuarter = useMemo(() => {
    let minMonths = null;
    for (const m of filteredMarkets) {
      const { nextLeaseEventMonths } = computeMarketMetrics(m);
      if (nextLeaseEventMonths !== null && nextLeaseEventMonths > 0) {
        if (minMonths === null || nextLeaseEventMonths < minMonths) {
          minMonths = nextLeaseEventMonths;
        }
      }
    }
    if (minMonths === null) return "—";
    const targetDate = new Date();
    targetDate.setMonth(targetDate.getMonth() + Math.round(minMonths));
    return quarterFromDate(targetDate) ?? "—";
  }, [filteredMarkets]);

  // Scenario needs operational values, never the restricted Strategy metadata.
  const [scenarioPolicy, setScenarioPolicy] = useState(null);
  const [scenarioPolicyError, setScenarioPolicyError] = useState(null);
  useEffect(() => {
    if (planView !== "whatif") return undefined;
    let cancelled = false;
    getEffectiveSpacePolicy()
      .then((data) => { if (!cancelled) { setScenarioPolicy(data?.baseline ?? null); setScenarioPolicyError(null); } })
      .catch(() => { if (!cancelled) { setScenarioPolicy(null); setScenarioPolicyError(SPACE_POLICY_FALLBACK_NOTICE); } });
    return () => { cancelled = true; };
  }, [planView]);
  const scenarioPolicyBaseline = useMemo(
    () => normaliseForScenario(scenarioPolicy),
    [scenarioPolicy]
  );

  // PLAN-WHATIF-REDESIGN-S1: What-If lever state + derived rows/portfolio
  // aggregates, shared between the KPI strip and the compare/controls body.
  // Computed unconditionally (hooks can't be conditional) but cheap even in
  // Plan mode since sortedMatrixMarkets is already memoized. `rows` (the
  // compare table) still reflects the toolbar's current filters via
  // sortedMatrixMarkets; the KPI strip's portfolio is computed separately
  // from canonicalActiveMarkets (see useWhatIfScenario's kpiMarkets param).
  const whatIf = useWhatIfScenario(sortedMatrixMarkets, canonicalActiveMarkets, scenarioPolicyBaseline);

  // PLACES-SLICE-2: opening a market in the Places Markets lens navigates to
  // the market record, carrying the visible order (pager) and return context.
  // PLACES-MARKETS-DRAWER: the Markets lens owns `?peek={id}` (+ peekTab),
  // always replace-style, preserving unrelated params, hash and router state.
  const locationRef = useRef(location);
  locationRef.current = location;
  const writePeek = useCallback((marketId, tab = "overview") => {
    const loc = locationRef.current;
    const qs = withMarketPeek(loc.search, marketId, tab);
    navigate({ pathname: loc.pathname, search: qs ? `?${qs}` : "", hash: loc.hash ?? "" }, { replace: true, state: loc.state });
  }, [navigate]);
  const openMarketRecord = useCallback((marketId, { replace = false, event } = {}) => {
    if (isMarketsLens) {
      if (event?.metaKey || event?.ctrlKey) {
        window.open(`${location.pathname}?${withMarketPeek(location.search, marketId, "overview")}${location.hash ?? ""}`, "_blank", "noopener,noreferrer");
        return;
      }
      writePeek(marketId, "overview"); return;
    }
    navigate(buildMarketUrl(marketId), {
      replace,
      state: {
        orderedIds: sortedMatrixMarketsRef.current.map((m) => m.id),
        returnPath: marketListReturnPath(location.pathname, location.search, location.hash),
      },
    });
  }, [isMarketsLens, writePeek, navigate, location.pathname, location.search, location.hash]);
  const peekState = isMarketsLens ? parseMarketPeek(location.search) : { peek: null, tab: "overview" };
  const peekMarket = peekState.peek ? planData.find((m) => m.id === peekState.peek) ?? null : null;

  // Policy is presentation-only; list and drawer share the snapshot above.
  const loadDrawerPolicy = useCallback(() => {
    const request = ++drawerPolicySequence.current;
    if (!drawerPolicySettled.current) setDrawerPolicyPending(true);
    return Promise.resolve().then(() => getEffectiveSpacePolicy())
      .then((r) => {
        if (request !== drawerPolicySequence.current) return;
        const buffer = Number.isFinite(r?.baseline?.hcBufferPct) ? r.baseline.hcBufferPct : null;
        setDrawerPolicyBuffer(buffer);
        setDrawerCapacityNote(buffer == null ? `No target buffer configured in policy. Using default ${DEFAULT_TARGET_BUFFER_PCT}% target buffer.` : null);
      })
      .catch(() => {
        if (request !== drawerPolicySequence.current) return;
        setDrawerPolicyBuffer(null);
        setDrawerCapacityNote(SPACE_POLICY_FALLBACK_NOTICE);
      }).finally(() => {
        if (request === drawerPolicySequence.current) {
          drawerPolicySettled.current = true;
          setDrawerPolicyPending(false);
        }
      });
  }, []);
  const loadDrawerCapacity = useCallback(() => {
    reloadCapacitySnapshot();
    loadDrawerPolicy();
  }, [reloadCapacitySnapshot, loadDrawerPolicy]);
  useEffect(() => {
    if (isMarketsLens) loadDrawerPolicy();
  }, [isMarketsLens, loadDrawerPolicy]);

  // Legacy expand deep links (?expandMarket=, workflow run "started from",
  // pipeline office links) land on the record instead of an inline accordion.
  useEffect(() => {
    if (!isMarketsLens) return;
    const params = new URLSearchParams(location.search);
    const expandId = params.get("expandMarket");
    // Legacy one-shot ?expandMarket= becomes the canonical peek (replace).
    if (expandId) { writePeek(expandId, "overview"); return; }
    if (scrollTargetId && planExpandedIds.has(scrollTargetId)) {
      const id = scrollTargetId;
      setScrollTargetId(null);
      setPlanExpandedIds(new Set());
      openMarketRecord(id, { replace: true });
    }
  }, [isMarketsLens, location.search, scrollTargetId, planExpandedIds, openMarketRecord, writePeek]);

  // CONTEXT-BANDS-S2 — decision-band row callbacks for the StatusBand popover.
  // Stable via useCallback (setters are always stable so no re-memoization).
  const handleMarketClickFromBand = useCallback((marketId) => {
    if (isMarketsLens) { openMarketRecord(marketId); return; }
    setPlanExpandedIds((prev) => new Set([...prev, marketId]));
    setScrollTargetId(marketId);
  }, [isMarketsLens, openMarketRecord]);

  const handleViewAllDecisions = useCallback((marketIds) => {
    setDecisionFilter(marketIds);
    setPlanSortKey("nextDecision");
    setPlanSortDir("asc");
    setUserHasSetSort(true);
  }, []);

  // CONTEXT-BANDS-S1/S2 — decisions-ahead + kpi-trend messages.
  const planStatusMessages = usePlanStatusMessages(canonicalActiveMarkets, kpiHistory, kpiHistoryLoaded, {
    onMarketClick:      handleMarketClickFromBand,
    onViewAllDecisions: handleViewAllDecisions,
  });

  // ALERT-CHIP-AND-TWO-CLUSTER-TOOLBAR: flatten the decision-band message
  // shape (items/chips/onViewAll) down to the shared AlertChip's simpler
  // {id, severity, text, actionLabel, onAction} row shape. The full
  // per-market breakdown that used to live in the decision-band popover is
  // reachable via the "View all" action, which sorts/filters the table to
  // exactly those markets — no information is lost, just one click deeper.
  const planAlertMessages = useMemo(() => planStatusMessages.map((m) => ({
    id:          m.id,
    severity:    m.severity === "urgent" ? "urgent" : "warn",
    text:        m.text,
    actionLabel: m.onViewAll ? "View all" : undefined,
    onAction:    m.onViewAll,
  })), [planStatusMessages]);

  // MP-20: Rent-incomplete alert — markets with operational/dormant offices that
  // have null annualRentUsd. Build-stage offices are legitimately absent (no rent
  // yet) and are excluded server-side before the array reaches the client.
  const rentIncompleteAlertMessages = useMemo(() => {
    const affected = (planData ?? []).filter(
      (m) => Array.isArray(m.rentIncompleteOffices) && m.rentIncompleteOffices.length > 0
    );
    if (affected.length === 0) return [];

    const msgs = [];
    // Leading summary — clicking filters the table to the affected markets.
    msgs.push({
      id:          "rent-incomplete-summary",
      severity:    "warn",
      text:        `${affected.length} market${affected.length === 1 ? "" : "s"} · rent total incomplete`,
      actionLabel: "Filter",
      onAction:    () => setDecisionFilter(affected.map((m) => m.id)),
    });
    // One row per market with a deep-link to the first missing office's lease tab.
    for (const mkt of affected) {
      const offices = mkt.rentIncompleteOffices ?? [];
      msgs.push({
        id:          `rent-incomplete-${mkt.id}`,
        severity:    "warn",
        text:        `${mkt.name}: ${offices.map((o) => o.locationAlias ?? o.locationId).join(", ")}`,
        actionLabel: offices[0]?.leaseId ? "Add rent →" : undefined,
        onAction:    offices[0]?.leaseId
          ? () => navigate(buildLeaseUrl(offices[0].leaseId, "financials"))
          : undefined,
      });
    }
    return canSeeRentCost ? msgs : [];
  }, [planData, navigate, canSeeRentCost]);

  const combinedPlanAlertMessages = useMemo(() => [
    ...rentIncompleteAlertMessages,
    ...planAlertMessages,
  ], [rentIncompleteAlertMessages, planAlertMessages]);

  // MARKET-PLAN-EXPAND-ALL-USE-TARGET-STATE: drive from the caller's intended
  // target (true = expand all, false = collapse all), not internal toggle math.
  // Stable — reads sortedMatrixMarketsRef at call time, never re-created.
  const handleExpandCollapseAll = useCallback((targetOn) => {
    const markets = sortedMatrixMarketsRef.current;
    setPlanExpandedIds(targetOn ? new Set(markets.map((m) => m.id)) : new Set());
  }, []); // stable — reads ref at call time, never re-created

  const handleTogglePlanExpanded = useCallback((marketId) => {
    setPlanExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(marketId)) { next.delete(marketId); } else { next.add(marketId); }
      return next;
    });
  }, []);

  return (
    <div
      data-testid="plan-matrix-page"
      style={{ minHeight: 400, paddingBottom: "40px" }}
    >
      <PageHeaderStack>
      {/* KPI strip — shared 5-tile set (Markets / Seats needed / Peak
          utilization / Markets over capacity / Portfolio balance), rendered
          through the same KpiTileRow container on every view so the row's
          height/position never shifts when toggling Plan/What-If/Timeline
          (MARKET-PLAN-CROSSVIEW-CONSISTENCY-S1). Plan and Timeline both show
          today's numbers via KpiStrip; What-If splits each tile into
          Today/Scenario blocks with a delta (PLAN-WHATIF-REDESIGN-S1). */}
      {planView === "whatif" ? (
        <WhatIfKpiStrip globalBand portfolio={whatIf.portfolio} allMarkets={canonicalActiveMarkets} />
      ) : (
        <KpiStrip
          filteredMarkets={collectionMarkets}
          allMarkets={canonicalActiveMarkets}
          isFiltered={isPlanFiltered}
          nextWallQuarter={filteredNextWallQuarter}
          kpiHistory={kpiHistory}
          showPlansComplete={isMarketsLens}
          showAnnualCost={!isMarketsLens}
          listSummary={listSummary}
        />
      )}

      {/* Toolbar — Filter left, controls right (matches Locations standard).
          Search/Filter/Sort + the Plan|What-If toggle stay in this same
          fixed position in both modes; matrix-only controls (Expand all,
          density toggle, Add market) are hidden in What-If mode.
          Hidden on Strategy tab — that view has its own self-contained layout. */}
      {planView !== "strategy" && <div style={secondaryCollection ? { minWidth: 0 } : { display: "contents" }}><div
        data-testid="plan-toolbar"
        ref={pressureToolbarRef}
        style={isMarketsLens && marketsView === "list" ? COLLECTION_TOOLBAR_STYLES.toolbar : {
          display:         "flex",
          alignItems:      "center",
          justifyContent:  "space-between",
          gap:             8,
          height:          T.toolbarRowH,
          flexWrap:        "nowrap",
        }}
      >
        {/* LEFT: Search → Filter (Strategy + Region + Status + Flags) + clear button.
            Never a density control (VIEWCONTROLS-STANDARDIZE-S1). */}
        <div data-testid="plan-toolbar-left" style={{ display: "flex", alignItems: "center", gap: planView === "market_pressure" ? 4 : 8, minWidth: 0 }}>
          {/* Alert chip — leads the toolbar row; actionable only on Plan
              view, so hidden entirely (no reserved space) on What-If and
              Timeline. */}
          {planView === "plan" && (
            <AlertChip
              testId="plan-alert-chip"
              storageKey="ripple.plan.alertChip.plan.open"
              messages={combinedPlanAlertMessages}
              okMessage={{ text: "No markets need a decision this quarter." }}
            />
          )}
          {planView === "market_pressure" && (
            <AlertChip
              testId="mp-alert-chip"
              storageKey="ripple.mp.alertChip.open"
              messages={mpKpisData.alertMessages ?? []}
              okMessage={{ text: "No upcoming capacity walls" }}
            />
          )}

          {/* Search — shared SearchInput component (matches Offices/Leases).
              No style override — uses SearchInput's default fontSize (T.fsSm5)
              so the input height is pixel-identical to Offices/Leases search
              inputs (MARKETPLAN-SEARCH-ROW-MEASURE-AND-CONFORM-S1).
              Hidden on the recommendations view — RecommendationsPane owns its
              own self-contained query row (MARKET-PLAN-RECS-QUERY-ROW). */}
          {planView !== "recommendations" && (
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search markets…"
              testId="plan-search"
              style={planView === "market_pressure" ? { width: collapsePressureCapacity ? 110 : 160, minWidth: 80 } : undefined}
            />
          )}
          {planView !== "recommendations" && (
            <ConsolidatedFilter
              testId="plan-filter"
              viewOptions={planView === "market_pressure" && collapsePressureCapacity ? <div ref={setPressureCapacityTarget} data-testid="pressure-capacity-filter-slot" style={{ padding: "10px 16px", borderBottom: `1px solid ${T.hair}` }} /> : undefined}
              unifiedDefaults={isMarketsLens}
              checkedInclusion={isMarketsLens}
              baselineSelections={isMarketsLens ? MARKET_FILTER_DEFAULTS : {}}
              dimensions={isMarketsLens ? countedMarketDimensions : planFilterDimensions}
              selections={isMarketsLens ? collectionSelections : { strategy: strategyFilter, region: regionFilter, status: statusFilter, hub: hubFilter }}
              onChange={(sel) => {
                if (isMarketsLens) {
                  setPlanPrefs(s => ({ ...s, cfSelections: sel, filterVersion: CHECKED_FILTER_VERSION, showAll: false }));
                  return;
                }
                // Strategy/Region/Status/Hub are all multi-select: each
                // dimension accumulates every checked value (OR within the
                // category), and the categories combine with AND in
                // filteredMarkets. "Exited" only appears when explicitly
                // checked here — see the default-hide logic in filteredMarkets.
                // "In build" is a synthetic status value resolved via
                // matchesStatusValue.
                // Changing any dimension filter turns off Show all.
                setStrategyFilter(sel.strategy ?? []);
                setRegionFilter(sel.region ?? []);
                setStatusFilter(sel.status ?? []);
                setHubFilter(sel.hub ?? []);
                setPlanShowAll(false);
              }}
              showAll={planShowAll}
              onShowAllChange={(val) => setPlanShowAll(val)}
              showAllSubLabel="includes hidden rows"
            />
          )}
          {planView === "market_pressure" && (
            <SortControl
              testId="pressure-sort"
              compactLabel
              fields={pressureSortFields}
              primary={{ key: pressureSortKey, dir: pressureSortDir }}
              secondary={{ key: null, dir: "asc" }}
              defaultKey="market"
              onChange={({ primary }) => {
                setPressureSortKey(primary.key);
                setPressureSortDir(primary.dir);
              }}
            />
          )}
          {planView === "market_pressure" && <div ref={setPressureOptionsTarget} data-testid="pressure-options-slot" style={{ display: "flex", flexShrink: 0 }} />}
          {(planView === "plan" || planView === "whatif") && (
            <SortControl
              testId="plan-sort"
              fields={(isMarketsLens ? MARKETS_SORT_FIELDS : PLAN_SORT_FIELDS).filter(field => canSeeRentCost || field.value !== "cost")}
              primary={{ key: planSortKey, dir: planSortDir }}
              secondary={{ key: planSortKey2, dir: planSortDir2 }}
              defaultKey={DEFAULT_SORT_KEY}
              onChange={({ primary, secondary }) => {
                setUserHasSetSort(true);
                setPlanSortKey(primary.key);
                setPlanSortDir(primary.dir);
                setPlanSortKey2(secondary.key);
                setPlanSortDir2(secondary.dir);
              }}
            />
          )}
          {planView !== "recommendations" && hasActiveFilters && (
            <button
              type="button"
              data-testid="plan-clear-btn"
              onClick={handleClearPlanFilters}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.blueSoft; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              style={{
                background:   "transparent",
                border:       "1px solid transparent",
                borderRadius: 8,
                color:        "var(--v2-accent)",
                fontSize:     T.fsBase,
                fontWeight:   T.fwBold,
                cursor:       "pointer",
                padding:      "7px 11px",
              }}
            >
              Clear
            </button>
          )}
        </div>

        {/* RIGHT — shared ViewControls (VIEWCONTROLS-STANDARDIZE-S1):
            density (Expand/Collapse all, Plan view only) → divider →
            + Add market */}
        <div data-testid="plan-toolbar-trailing" style={isMarketsLens && marketsView === "list" ? COLLECTION_TOOLBAR_STYLES.trailing : { display: "contents" }}>
        <ViewControls
          object="plan"
          view={planView}
          densityNodes={{
            compactComfortable: () => (
              <CompactSwitch
                testId="matrix-compact-switch"
                checked={planDensity === "compact"}
                onChange={toggleDensity}
              />
            ),
            expandCollapse: isMarketsLens ? undefined : () => (
              <ExpandAllSwitch
                testId="matrix-expand-collapse-all"
                expanded={anyPlanExpanded}
                onToggle={() => handleExpandCollapseAll(!anyPlanExpanded)}
              />
            ),
          }}
          viewToggle={isMarketsLens ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><ToolbarViewToggle
              testId="markets-view-toggle"
              value={marketsView}
              onChange={setMarketsView}
              segments={[
                { key: "list",  label: "List",  testid: "markets-view-list"  },
                { key: "tiles", label: "Tiles", testid: "markets-view-tiles" },
                { key: "map",   label: "Map",   testid: "markets-view-map"   },
              ]}
            /><SeatFitViewKey bufferPct={drawerPolicyBuffer ?? undefined} /></div>
          ) : planView === "market_pressure" ? <div ref={setPressureKeyTarget} data-testid="pressure-key-slot" style={{ display: "flex", flexShrink: 0 }} /> : planView === "whatif" ? <ScenarioViewKey policyBaseline={scenarioPolicyBaseline} /> : undefined}
          addButton={
            /* Scenario is read-only and ends with Key; other actions retain their gates. */
            canEdit && planView !== "whatif" ? (
              <ToolbarAddButton
                testId="add-market-btn"
                onClick={() => setShowCreateMarket(true)}
              >
                + Add market
              </ToolbarAddButton>
            ) : null
          }
        />
        </div>
      </div>
      {secondaryCollection && <CollectionCount testId="plan-showing-count"
        count={planView === "market_pressure" ? pressureCounts.count : isMarketsLens ? filteredMarkets.filter(m => ladderForMarket(currentLadders, m.id).status !== "excluded" && !["excluded", "no_seats"].includes(ladderForMarket(currentLadders, m.id)?.cell?.status)).length : filteredMarkets.length}
        total={planView === "market_pressure" ? pressureCounts.total : isMarketsLens ? planData.filter(m => ladderForMarket(currentLadders, m.id).status !== "excluded" && !["excluded", "no_seats"].includes(ladderForMarket(currentLadders, m.id)?.cell?.status)).length : planData.length} />}
      </div>}

      {/* Strategy toolbar — SearchInput left, + Add directive right.
          Only visible on the Strategy tab; mirrors the Plan tab toolbar height. */}
      {planView === "strategy" && (
        <div
          data-testid="strategy-toolbar"
          style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            gap:            8,
            height:         T.toolbarRowH,
            flexWrap:       "nowrap",
          }}
        >
          <SearchInput
            value={strategySearch}
            onChange={setStrategySearch}
            placeholder="Search directives…"
            testId="strategy-search"
          />
          {canEdit && (
            <ToolbarAddButton
              testId="strategy-add-directive-btn"
              onClick={() => setDrawerDirective({})}
            >
              + Add directive
            </ToolbarAddButton>
          )}
        </div>
      )}

      {/* Retained legacy readers are outside the Places collection contract. */}
      {!secondaryCollection && planView !== "recommendations" && hasActiveFilters && (
        <div
          data-testid="plan-showing-count"
          style={{ fontSize: T.fsXs, color: T.collectionCount, padding: "6px 0 2px" }}
        >
          Showing <strong>{filteredMarkets.length}</strong> of <strong>{planData.length}</strong> markets
        </div>
      )}

      {/* Lean lens switcher — sits below KPIs + toolbar, above body content */}
      {!placesLens && <PlanTabBar
        tabs={PLAN_TABS.filter((t) =>
          canViewMarketsStrategy || !["strategy", "recommendations"].includes(t.key)
        ).map((t) => ({
          key:    t.key,
          label:  t.label,
          testid: `plan-subnav-tab-${t.key}`,
        }))}
        value={planView}
        onChange={setPlanView}
        ariaLabel="Plan view"
      />}

      {/* Body */}
      {planView === "strategy" ? (
        <StrategyTabPane
          directives={directives}
          canEdit={canViewMarketsStrategy}
          onEditDirective={setDrawerDirective}
          baseline={policyBaseline}
          overrideCount={overrideCount}
          isAdmin={canViewMarketsStrategy}
          density={planDensity}
          markets={planData}
          searchQuery={strategySearch}
        />
      ) : planView === "whatif" ? (
        <>
          {scenarioPolicyError && <div role="alert" style={{ fontSize: T.fsXs, color: T.ragRed }}>{scenarioPolicyError}</div>}
          <WhatIfBody
            canEditPolicy={currentRole === "admin"}
            rows={whatIf.rows}
            levers={whatIf.levers}
            onChange={whatIf.setLevers}
            onReset={whatIf.resetLevers}
            policyBaseline={scenarioPolicyBaseline}
          />
        </>
      ) : planView === "market_pressure" ? (
        <MarketPressurePage
          toolbarTargets={{ options: pressureOptionsTarget, key: pressureKeyTarget, capacity: pressureCapacityTarget, collapseCapacity: collapsePressureCapacity }}
          onOpenMarketPlan={(marketId) => {
            setPlanView("plan");
            setScrollTargetId(marketId);
          }}
          onTestScenario={() => {
            setPlanView("whatif");
          }}
          onKpisChange={setMpKpisData}
          onCollectionCountsChange={secondaryCollection ? setPressureCounts : undefined}
          searchQuery={search}
          pressureSortKey={pressureSortKey}
          pressureSortDir={pressureSortDir}
          suppressKpiStrip={true}
        />
      ) : planView === "recommendations" ? (
        /* PLAN-RECOMMENDATIONS-TAB: Rules-derived signals + curator queue. */
        <RecommendationsPane
          userRole={currentRole}
          onNavigate={(view, params) => {
            setPlanView(view);
            if (params?.marketId) setScrollTargetId(params.marketId);
          }}
        />
      ) : (
        <>
          {loading && (
            <div style={{ color: T.light, fontSize: T.fsSm, padding: "24px 0" }}>
              Loading plan…
            </div>
          )}

          {!loading && fetchError && (
            <div style={{ color: T.ragRed, fontSize: T.fsSm, padding: "24px 0" }}>
              {fetchError}
            </div>
          )}

          {!loading && !fetchError && (
            <>
              {/* PLAN-UNASSIGNED-LOCATIONS: pinned orphan bin; hidden at count=0 */}
              {isMarketsLens && marketsView === "tiles" ? (
                <MarketTilesView
                  markets={sortedMatrixMarkets}
                  locations={inventory.locations}
                  inventoryReady={!inventory.loading && !inventory.error}
                  onOpenMarket={openMarketRecord}
                  renderStrategy={(strategy) => <StrategyPill strategy={strategy} />}
                />
              ) : isMarketsLens && marketsView === "map" ? (
                <MarketsMapView
                  markets={sortedMatrixMarkets}
                  locations={inventory.locations}
                  loading={inventory.loading}
                  error={inventory.error}
                  onRetry={inventory.reload}
                  onOpenMarket={openMarketRecord}
                />
              ) : (<>
              {isMarketsLens && drawerCapacity.status === "error" && <div role="alert" data-testid="markets-capacity-error"
                style={{ color: T.ragAmber, padding: "8px 0" }}>
                Capacity read failed: {drawerCapacity.error}{" "}
                <button type="button" onClick={reloadCapacitySnapshot}>Retry capacity</button>
              </div>}
              <UnassignedBin
                locations={unassignedLocations}
                canEdit={canEdit}
                allMarkets={planData}
                onAssign={handleMoveOffice}
              />
              <PlanMatrix
                onOpenMarket={isMarketsLens ? openMarketRecord : undefined}
                geometryPending={isMarketsLens && (["idle", "loading"].includes(drawerCapacity.status) || drawerPolicyPending)}
                sortPending={isMarketsLens && marketsListSortPending(planSortKey, planSortKey2, drawerCapacity.status, drawerPolicyPending,
                  inventory.loading ? "loading" : inventory.error ? "error" : "ready")}
                selectedMarketId={peekState.peek}
                markets={sortedMatrixMarkets}
                canEdit={canEdit}
                onMarketSaved={handleMarketSaved}
                onOfficeSaved={handleOfficeSaved}
                onOpenLocation={onOpenLocation}
                sortKey={planSortKey}
                sortDir={planSortDir}
                onSort={handleSort}
                userHasSetSort={userHasSetSort}
                density={planDensity}
                allMarkets={planData}
                onDeleteMarket={handleDeleteMarket}
                onMoveOffice={handleMoveOffice}
                expandedIds={planExpandedIds}
                onToggleExpanded={handleTogglePlanExpanded}
                scrollTargetId={scrollTargetId}
                onScrollTargetHandled={() => setScrollTargetId(null)}
                onStartWorkflow={onStartWorkflow}
                onOpenLinkedRun={onOpenLinkedRun}
                allDirectives={directives}
                onOpenDirective={setDrawerDirective}
              />
              </>)}
            </>
          )}
        </>
      )}

      {/* PLACES-MARKETS-DRAWER: market record drawer (Markets lens only) */}
      {isMarketsLens && (
        <MarketRecordDrawer
          open={Boolean(peekState.peek)}
          marketId={peekState.peek}
          market={peekMarket}
          tab={peekState.tab}
          onTabChange={(t) => writePeek(peekState.peek, t)}
          onClose={() => writePeek(null)}
          orderedIds={sortedMatrixMarkets.map((m) => m.id)}
          onNavigate={(id) => writePeek(id, peekState.tab)}
          currentRole={currentRole}
          allMarkets={planData}
          capacity={drawerCapacity}
          inventory={inventory}
          bufferPct={drawerPolicyBuffer ?? undefined}
          capacityNote={drawerCapacityNote}
          onRetryCapacity={loadDrawerCapacity}
          onOpenLocation={onOpenLocation}
          onMarketSaved={async (...args) => {
            await handleMarketSaved(...args);
            loadDrawerCapacity();
          }}
          onOfficeSaved={handleOfficeSaved}
          onMoveOffice={handleMoveOffice}
          onDeleteMarket={handleDeleteMarket}
          onStartWorkflow={onStartWorkflow}
          onOpenLinkedRun={onOpenLinkedRun}
          nestedOpen={Boolean(movingLocation || deletingMarket || showCreateMarket)}
          onOpenInOffices={(m) => navigate(`/places/offices?q=${encodeURIComponent(m.name ?? m.id)}`, { state: { marketReturnPath: `${location.pathname}${location.search}${location.hash ?? ""}` } })}
          onOpenCapacity={(id) => navigate(capacityHrefForMarket(id))}
        />
      )}

      {/* MARKETS-S3 modals */}
      {movingLocation && (
        <MoveToMarketPicker
          location={movingLocation}
          allMarkets={planData}
          onClose={() => setMovingLocation(null)}
          onMoved={async (...args) => {
            await handleConfirmMove(...args);
            inventory.reload();
            loadDrawerCapacity();
          }}
          onShowCreate={() => setShowCreateMarket(true)}
        />
      )}
      {showCreateMarket && (
        <CreateMarketModal
          onClose={() => setShowCreateMarket(false)}
          onCreated={async () => {
            setShowCreateMarket(false);
            await refreshPlan();
            showToast("Market created", { type: "success" });
          }}
        />
      )}
      {deletingMarket && (
        <DeleteMarketConfirm
          market={deletingMarket}
          onClose={() => setDeletingMarket(null)}
          onConfirm={async (m) => { await handleConfirmDelete(m); if (isMarketsLens && parseMarketPeek(locationRef.current.search).peek === m.id) writePeek(null); }}
        />
      )}

      {/* PLAN-STRATEGY-BAND-S1: Directive create/edit drawer */}
      <DirectiveDrawer
        directive={drawerDirective}
        canEdit={canViewMarketsStrategy}
        markets={planData}
        onClose={() => setDrawerDirective(null)}
        onSaved={(result) => {
          setDrawerDirective(null);
          if (canViewMarketsStrategy) {
            getDirectives()
              .then((data) => setDirectives(Array.isArray(data) ? data : []))
              .catch((err) => { log.error("MarketPlanPage: directives refresh failed", { err: String(err) }); });
          }
        }}
      />
      </PageHeaderStack>
    </div>
  );
}

// ─── MarketRecordWorkspace (PLACES-SLICE-2) ───────────────────────────────────
// The governed market record reuses the exact edit/workflow surfaces that the
// retired inline accordion exposed: plan drawer, More menu (deactivate /
// delete), linked workflow runs, directives, office plan table (disposition
// drawer + move office) and candidates. No business math is duplicated here.
//   section="plan"    → context band, header actions, directives, candidates
//   section="offices" → office plan table (disposition / move actions)
export function MarketRecordWorkspace({
  market, allMarkets = [], currentRole = "viewer", section = "plan",
  onOpenLocation, onStartWorkflow, onOpenLinkedRun, onChanged, onDeleted,
  directives = [], onOpenDirective,
}) {
  const canEdit = currentRole === "editor" || currentRole === "admin";
  const [drawer, setDrawer] = useState(null);
  const [moving, setMoving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [overrides, setOverrides] = useState({});
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useEffect(() => { setDrawer(null); setOverrides({}); }, [market?.id]);

  if (!market) return null;
  const locs = (market.locations ?? []).map((l) =>
    overrides[l.locationId] ? { ...l, ...overrides[l.locationId] } : l);
  const handleSaved = async () => { await onChangedRef.current?.(); };
  const marketDirectives = directives.filter((d) =>
    (d.markets ?? []).some((dm) => dm.marketId === market.id));

  return (
    <div data-testid={`market-record-workspace-${section}`}
      style={{ background: T.planExpandBg, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
      {section === "plan" ? (
        <>
          <MarketContextBand market={{ ...market, locations: locs }} />
          <MarketHeader
            market={market}
            canEdit={canEdit}
            title="Market plan"
            onEditPlan={() => setDrawer({ scope: "market" })}
            onSaved={handleSaved}
            onDeleteMarket={(m) => setDeleting(m)}
            onStartWorkflow={onStartWorkflow}
            onOpenLinkedRun={onOpenLinkedRun}
          />
          <MarketDirectivesStrip directives={marketDirectives} onOpenDirective={onOpenDirective} />
          {(market.strategy === "expand" || market.strategy === "rightsize" ||
            locs.some((l) => l.disposition === "expand" || l.disposition === "rightsize")) && (
            <CandidatesSection marketId={market.id} canEdit={canEdit} />
          )}
          <div style={{ height: 14 }} />
        </>
      ) : (
        <div style={{ paddingTop: 6 }}>
          <OfficesTable
            locations={locs}
            onOpenLocation={onOpenLocation}
            canEdit={canEdit}
            onMoveOffice={(loc) => { setDrawer(null); setMoving(loc); }}
            currentMarketId={market.id}
            allMarkets={allMarkets}
            onOpenOfficeDrawer={(loc) => setDrawer({ scope: "office", loc })}
            openOfficeId={drawer?.scope === "office" ? drawer.loc?.locationId : null}
            onCloseOffice={() => setDrawer(null)}
            onOfficeSaved={(locationId, _updated, patch) => {
              setOverrides((prev) => ({ ...prev, [locationId]: { ...(prev[locationId] ?? {}), ...patch } }));
              if (patch?.disposition !== undefined || patch?.targetActionDate !== undefined) emitCommandIndexStale();
              handleSaved();
            }}
          />
        </div>
      )}

      {drawer?.scope === "market" && (
        <MarketPlanScopeDrawer
          market={market}
          canEdit={canEdit}
          onClose={() => setDrawer(null)}
          onSaved={handleSaved}
          hasPrev={false}
          hasNext={false}
        />
      )}
      {moving && (
        <MoveToMarketPicker
          location={moving}
          allMarkets={allMarkets}
          onClose={() => setMoving(null)}
          onMoved={async (locationId, targetMarketId, before) => {
            await reassignLocationMarket(locationId, targetMarketId, before);
            const target = allMarkets.find((m) => m.id === targetMarketId);
            showToast(`${before?.locationAlias || locationId} moved to ${target?.name ?? targetMarketId}`, { type: "success" });
            await handleSaved();
          }}
          onShowCreate={() => setShowCreate(true)}
        />
      )}
      {showCreate && (
        <CreateMarketModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await handleSaved(); showToast("Market created", { type: "success" }); }}
        />
      )}
      {deleting && (
        <DeleteMarketConfirm
          market={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={async (m) => { await deleteMarket(m.id, m); onDeleted?.(m); }}
        />
      )}
    </div>
  );
}
